<template>
  <div class="trucking-job-main">
    <div class="left-menu" ref="leftMenu">
      <Menus :menu="menu" @menuClicked="menuClicked" />
    </div>

    <div class="right-tabulator">
      <div class="filter-area">
        <div>{{ filterText }}</div>
        <ButtonFilter
          class="filter-button"
          buttonText="Change Filter"
          :filterWidth="300"
          filterPosition="right"
          :isOpen.sync="showFilterBox"
        >
          <div class="filter-box">
            <BoxContainer
              class="filter-check"
              title="Date"
              :isChecked.sync="dateFilterChecked"
              :isCheckBox="true"
              style="margin-bottom: 5px"
            >
              <div class="dates">
                <DateTimePicker
                  placeholder="Start"
                  title="Start"
                  :showTime="false"
                  :value.sync="refreshStart"
                  :isDisabled="!dateFilterChecked"
                />
                <DateTimePicker
                  placeholder="End"
                  title="End"
                  :showTime="false"
                  :value.sync="refreshEnd"
                  :isDisabled="!dateFilterChecked"
                />
              </div>
            </BoxContainer>
            <BoxContainer class="filter-check" title="Search" :isChecked.sync="searchFilterChecked" :isCheckBox="true">
              <Dropdown
                placeholder="Search By"
                :items="searchFilterItems"
                itemKey="Search Criteria"
                :isDisabled="!searchFilterChecked"
                :isRequired="searchFilterChecked"
                :selectedObject.sync="selectedSearchBy"
                :errorMessage.sync="searchByError"
                @selected="searchBySelected"
              />
              <Dropdown
                placeholder="Search Criteria"
                :items="searchCriteriaItems"
                itemKey="name"
                :isDisabled="!searchFilterChecked || criteriaDisabled"
                :isRequired="searchFilterChecked"
                :selectedObject.sync="selectedCriteria"
              />
              <Input
                placeholder="Search"
                :inputValue.sync="searchByValue"
                :isDisabled="!searchFilterChecked"
                :isRequired="searchFilterChecked"
                :errorMessage.sync="searchByValueError"
              />
            </BoxContainer>
            <button @click="refreshClicked" :disabled="!dateFilterChecked && !searchFilterChecked">Refresh</button>
          </div>
        </ButtonFilter>
        <div>Last Refreshed: {{ lastRefreshed }}</div>
      </div>
      <div class="tabulator-area">
        <TableBox
          :tableData="jobsList"
          title="Trucking Job"
          :selectedRow.sync="selectedJob"
          :loading="tableLoading"
          :dataType="{
            'Job DateTime': 'date',
            'Required Date': 'date',
            DateUpdate: 'date',
            'Total Consignments': 'number',
          }"
          :columnOrderViews.sync="tableBoxColumnOrderViews"
          :columnOrderID="tableBoxColumnOrderID"
        />
      </div>
    </div>

    <PopupEntry
      style="z-index: 201"
      v-if="showPopup"
      :show.sync="showPopup"
      :title="popupTitle"
      @save="saveClicked"
      @requiredClicked="requiredClicked"
      :createdBy="createdBy"
      :updatedBy="updatedBy"
      :errorMessages="eMessages"
      :successMessages="sMessages"
      :requiredError="rMessages"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
    >
      <div class="popup-details" id="popupDetails" style="height: 100%">
        <div v-if="currentTabIndex != 1" class="summary">
          <div style="margin-bottom: 5px">Header Info</div>
          <Input
            :isDisabled="true"
            placeholder="Shippers/Consignee"
            :inputValue="selectedShipperConsignee ? selectedShipperConsignee['Customer Name'] : ''"
            :title="selectedShipperConsignee ? selectedShipperConsignee['Customer Name'] : ''"
            :style="{ opacity: selectedShipperConsignee ? '1' : '0.5' }"
          />
          <Input
            :isDisabled="true"
            placeholder="Vessel Code"
            :inputValue="
              selectedShippingInfoData['selectedVesselCode'] ?
                selectedShippingInfoData['selectedVesselCode']['Vessel Name']
              : ''
            "
            :title="
              selectedShippingInfoData['selectedVesselCode'] ?
                selectedShippingInfoData['selectedVesselCode']['Vessel Name']
              : ''
            "
            :style="{ opacity: selectedShippingInfoData['selectedVesselCode'] ? '1' : '0.5' }"
          />
          <Input
            :isDisabled="true"
            placeholder="Lorry No"
            :inputValue="
              selectedLorryInfoData['selectedLorryNoList'] ?
                selectedLorryInfoData['selectedLorryNoList']['Vehicle Number']
              : ''
            "
            :title="
              selectedLorryInfoData['selectedLorryNoList'] ?
                selectedLorryInfoData['selectedLorryNoList']['Vehicle Number']
              : ''
            "
            :style="{ opacity: selectedLorryInfoData['selectedLorryNoList'] ? '1' : '0.5' }"
          />
          <Input
            :isDisabled="true"
            placeholder="Driver Code"
            :inputValue="
              selectedLorryInfoData['selectedDriverCodeList'] ?
                selectedLorryInfoData['selectedDriverCodeList']['Driver Name']
              : ''
            "
            :title="
              selectedLorryInfoData['selectedDriverCodeList'] ?
                selectedLorryInfoData['selectedDriverCodeList']['Driver Name']
              : ''
            "
            :style="{ opacity: selectedLorryInfoData['selectedDriverCodeList'] ? '1' : '0.5' }"
          />
          <Input
            :isDisabled="true"
            placeholder="Attendant 1"
            :inputValue="
              selectedLorryInfoData['selectedAttendantList'] ?
                selectedLorryInfoData['selectedAttendantList']['Attendant Name']
              : ''
            "
            :title="
              selectedLorryInfoData['selectedAttendantList'] ?
                selectedLorryInfoData['selectedAttendantList']['Attendant Name']
              : ''
            "
            :style="{ opacity: selectedLorryInfoData['selectedAttendantList'] ? '1' : '0.5' }"
          />
          <Input
            :isDisabled="true"
            placeholder="Attendant 2"
            :inputValue="
              selectedLorryInfoData['selectedAttendantList2'] ?
                selectedLorryInfoData['selectedAttendantList2']['Attendant Name']
              : ''
            "
            :title="
              selectedLorryInfoData['selectedAttendantList2'] ?
                selectedLorryInfoData['selectedAttendantList2']['Attendant Name']
              : ''
            "
            :style="{ opacity: selectedLorryInfoData['selectedAttendantList2'] ? '1' : '0.5' }"
          />

          <Input
            :isDisabled="true"
            placeholder="Bill To"
            :inputValue="
              selectedHeaderInfoData['selectedBillTo'] ? selectedHeaderInfoData['selectedBillTo']['Customer Name'] : ''
            "
            :title="
              selectedHeaderInfoData['selectedBillTo'] ? selectedHeaderInfoData['selectedBillTo']['Customer Name'] : ''
            "
            :style="{ opacity: selectedHeaderInfoData['selectedBillTo'] ? '1' : '0.5' }"
          />

          <Input
            :isDisabled="true"
            placeholder="Job #"
            :inputValue="selectedHeaderInfoData['jobNo'] ? selectedHeaderInfoData['jobNo'] : ''"
            :title="selectedHeaderInfoData['jobNo'] ? selectedHeaderInfoData['jobNo'] : ''"
            :style="{ opacity: selectedHeaderInfoData['jobNo'] ? '1' : '0.5' }"
          />

          <Input
            :isDisabled="true"
            placeholder="Job Time"
            :inputValue="
              selectedHeaderInfoData['jobTime'] ?
                selectedHeaderInfoData['jobTime'].formatString('dd/MM/yyyy HH:mm')
              : ''
            "
            :title="
              selectedHeaderInfoData['jobTime'] ?
                selectedHeaderInfoData['jobTime'].formatString('dd/MM/yyyy HH:mm')
              : ''
            "
            :style="{ opacity: selectedHeaderInfoData['jobTime'] ? '1' : '0.5' }"
          />

          <Input
            :isDisabled="true"
            placeholder="Required Date/Time"
            :inputValue="
              selectedHeaderInfoData['reqJobTime'] ?
                selectedHeaderInfoData['reqJobTime'].formatString('dd/MM/yyyy HH:mm')
              : ''
            "
            :title="
              selectedHeaderInfoData['reqJobTime'] ?
                selectedHeaderInfoData['reqJobTime'].formatString('dd/MM/yyyy HH:mm')
              : ''
            "
            :style="{ opacity: selectedHeaderInfoData['reqJobTime'] ? '1' : '0.5' }"
          />

          <Input
            :isDisabled="true"
            placeholder="Job State"
            :inputValue="selectedHeaderInfoData['jobState'] ? selectedHeaderInfoData['jobState'] : ''"
            :title="selectedHeaderInfoData['jobState'] ? selectedHeaderInfoData['jobState'] : ''"
            :style="{ opacity: selectedHeaderInfoData['jobState'] ? '1' : '0.5' }"
          />
        </div>
        <Tab
          :style="{ width: currentTabIndex != 1 ? 'calc(100% - 120px)' : '100%' }"
          :tabs="tabsItems"
          :tabIndex.sync="currentTabIndex"
        >
          <!-- Quick Entry tab -->
          <div slot="Quick Entry">
            <!-- Header Info Section -->
            <div class="tab-block">
              <div class="tab-title">Header Info</div>
              <div class="details-column">
                <Input
                  placeholder="Job #"
                  :inputValue.sync="selectedHeaderInfoData['jobNo']"
                  :isRequired="true"
                  :isDisabled="true"
                />
                <InputQuery
                  placeholder="Shippers/Consignee"
                  :items="headerInfoData['shipperConsignee']"
                  itemKey="Description"
                  descKey="Customer Name"
                  :errorMessage.sync="errorMessages['shipperConsignee']"
                  :selectedObject.sync="selectedShipperConsignee"
                  @selected="shipperConsigneeSelected"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  ref="shipperConsignee"
                  addLink="/Home/MasterFiles/ProfileMaster/ShipperConsignee?new=1"
                  :canRefresh="true"
                  @refreshClicked="shipperConsigneeInputQueryRefreshClicked"
                />
                <InputQuery
                  placeholder="Vessel Code"
                  :items="shippingInfoData['vesselCodeList']"
                  itemKey="Vessel ID"
                  descKey="Vessel Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedVesselCode']"
                  ref="vesselcode"
                  :isRequired="
                    selectedJobType ? selectedJobType['Job Type'] == 'I' || selectedJobType['Job Type'] == 'E' : false
                  "
                />
                <DateTimePicker
                  placeholder="Required Date/Time"
                  title="Required Date/Time"
                  :value.sync="selectedHeaderInfoData['reqJobTime']"
                  :isRequired="true"
                  :maxDate="smallDateTimeMaxDate"
                  :minDate="smallDateTimeMinValue"
                  @dateChanged="checkDateTime('Required Date/Time', $event)"
                  :isDisabled="viewMode"
                  :errorMessage.sync="reqdatefrom"
                  ref="reqdatefrom"
                />
                <Dropdown
                  placeholder="Billing Fmt"
                  :items="headerInfoData['billingfmt']"
                  itemKey="Description"
                  descKey="Billing Fmt"
                  :errorMessage.sync="errorMessages['billfmt']"
                  :selectedObject.sync="selectedHeaderInfoData['selectedBillingFmt']"
                  :isRequired="true"
                  :isDisabled="viewMode || bilfmtIsDisabled"
                  ref="billfmt"
                />
                <Input
                  style="width: 46.5%"
                  placeholder="Request By"
                  :inputValue.sync="selectedHeaderInfoData['selectedRequestBy']"
                  :isDisabled="viewMode"
                  validator="text,20,alphanumeric"
                />
              </div>

              <div class="details-column">
                <DateTimePicker
                  placeholder="Job Time"
                  title="Job Time"
                  :value.sync="selectedHeaderInfoData['jobTime']"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  :maxDate="smallDateTimeMaxDate"
                  :minDate="smallDateTimeMinValue"
                  :errorMessage.sync="jobTimeErr"
                  @dateChanged="checkDateTime('Job Time', $event)"
                  ref="job_time"
                />
                <Input
                  placeholder="D/O #"
                  :isDisabled="viewMode"
                  :inputValue.sync="selectedHeaderInfoData['DOno']"
                  :isRequired="true"
                  ref="delivery_order"
                  :errorMessage.sync="errorMessages['delivery_order']"
                  validator="alphanumeric,40"
                />
                <InputQuery
                  placeholder="Bill To"
                  :items="headerInfoData['billTo']"
                  itemKey="Description"
                  descKey="Customer Name"
                  ref="billcu_no"
                  :errorMessage.sync="errorMessages['billcu_no']"
                  :selectedObject.sync="selectedHeaderInfoData['selectedBillTo']"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  @selected="billToSelected"
                  addLink="/Home/MasterFiles/ProfileMaster/Customer?new=1"
                  :canRefresh="true"
                  @refreshClicked="billToInputQueryRefreshClicked"
                />
                <InputQuery
                  placeholder="Job Type"
                  :items="headerInfoData['jobType']"
                  itemKey="Job Type"
                  descKey="Description"
                  :errorMessage.sync="errorMessages['job_type']"
                  :selectedObject.sync="selectedJobType"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  ref="job_type"
                  @selected="jobTypeSelected"
                />
                <Input
                  placeholder="Req Time Desc"
                  :inputValue.sync="selectedHeaderInfoData['ReqTimeDesc']"
                  :isDisabled="viewMode"
                  validator="text,20"
                />
                <RemarksBox
                  :style="{ height: 'calc(100% - 140px)' }"
                  :maxLength="200"
                  :text.sync="selectedGeneralInfoData['remarks']"
                  :isDisabled="viewMode"
                />
              </div>

              <div class="details-column">
                <InputQuery
                  placeholder="Pick Up"
                  :items="headerInfoData['pickup']"
                  itemKey="Description"
                  descKey="Company"
                  ref="pickup"
                  :errorMessage.sync="errorMessages['pickup']"
                  :selectedObject.sync="selectedHeaderInfoData['selectedPickUp']"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  @selected="pickUpSelected"
                  addLink="/Home/MasterFiles/OperationMaster/Destination?new=1"
                  :canRefresh="true"
                  @refreshClicked="destinationInputQueryRefreshClicked"
                />
                <div style="display: flex">
                  <Input
                    style="margin-right: 5px"
                    placeholder="Tariff Area"
                    textAlign="right"
                    :isDisabled="true"
                    :inputValue.sync="selectedHeaderInfoData['tariffAreaPickup']"
                    ref="tariffArea"
                  />
                  <Input
                    placeholder="Zones"
                    textAlign="right"
                    :isDisabled="true"
                    :inputValue.sync="selectedHeaderInfoData['zonesPickup']"
                    ref="zones"
                  />
                </div>
                <textarea class="address-details" disabled v-model="selectedHeaderInfoData['pickUpAddress']"></textarea>
                <InputQuery
                  placeholder="Transit 1"
                  :items="headerInfoData['transit1']"
                  itemKey="Description"
                  descKey="Company"
                  ref="transit1"
                  :selectedObject.sync="selectedHeaderInfoData['selectedTransit1']"
                  :isDisabled="viewMode"
                  @selected="transit1Selected"
                  addLink="/Home/MasterFiles/OperationMaster/Destination?new=1"
                  :canRefresh="true"
                  @refreshClicked="destinationInputQueryRefreshClicked"
                />
                <div style="display: flex">
                  <Input
                    style="margin-right: 5px"
                    :items="headerInfoData['tariffArea']"
                    placeholder="Tariff Area"
                    textAlign="right"
                    :isDisabled="true"
                    :inputValue.sync="selectedHeaderInfoData['tariffAreaTransit1']"
                    ref="tariffArea"
                  />
                  <Input
                    :items="headerInfoData['zones']"
                    placeholder="Zones"
                    textAlign="right"
                    :isDisabled="true"
                    :inputValue.sync="selectedHeaderInfoData['zonesTransit1']"
                    ref="zones"
                  />
                </div>
                <InputQuery
                  placeholder="Transit 2"
                  :items="headerInfoData['transit2']"
                  itemKey="Description"
                  descKey="Company"
                  ref="transit2"
                  :selectedObject.sync="selectedHeaderInfoData['selectedTransit2']"
                  :isDisabled="viewMode"
                  @selected="transit2Selected"
                  addLink="/Home/MasterFiles/OperationMaster/Destination?new=1"
                  :canRefresh="true"
                  @refreshClicked="destinationInputQueryRefreshClicked"
                />
                <div style="display: flex">
                  <Input style="margin-right: 5px" placeholder="Tariff Area" textAlign="right" :isDisabled="true" />
                  <Input
                    placeholder="Zones"
                    textAlign="right"
                    :isDisabled="true"
                    :inputValue.sync="selectedHeaderInfoData['zonesTransit2']"
                  />
                </div>
                <InputQuery
                  placeholder="Drop Off"
                  :items="headerInfoData['dropoff']"
                  itemKey="Description"
                  descKey="Company"
                  ref="dropoff"
                  :errorMessage.sync="errorMessages['dropoff']"
                  :selectedObject.sync="selectedHeaderInfoData['selectedDropOff']"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  @selected="dropOffSelected"
                  addLink="/Home/MasterFiles/OperationMaster/Destination?new=1"
                  :canRefresh="true"
                  @refreshClicked="destinationInputQueryRefreshClicked"
                />
                <div style="display: flex">
                  <Input
                    style="margin-right: 5px"
                    placeholder="Tariff Area"
                    textAlign="right"
                    :isDisabled="true"
                    :inputValue.sync="selectedHeaderInfoData['tariffAreaDropoff']"
                    ref="tariffArea"
                  />
                  <Input
                    placeholder="Zones"
                    textAlign="right"
                    :isDisabled="true"
                    :inputValue.sync="selectedHeaderInfoData['zonesDropoff']"
                    ref="zones"
                  />
                </div>
                <textarea
                  class="address-details"
                  disabled
                  v-model="selectedHeaderInfoData['dropOffAddress']"
                ></textarea>
              </div>
            </div>

            <!-- Lorry/Driver Section -->
            <div class="tab-block">
              <div class="tab-title">Lorry/Driver Info</div>
              <div class="details-column">
                <InputQuery
                  placeholder="Lorry No"
                  :items="LorryInfoTabData['lorryNoList']"
                  itemKey="Vehicle Code"
                  descKey="Vehicle Number"
                  :selectedObject.sync="selectedLorryInfoData['selectedLorryNoList']"
                  ref="lorry"
                  :isRequired="false"
                  :isDisabled="viewMode"
                  @selected="lorryNoChanged"
                  @focusChanged="lorryNoFocusChanged"
                />
                <CheckBoxV2
                  class="filter-check"
                  title="Job Completed"
                  :isChecked.sync="selectedLorryInfoData['isJobCompleted']"
                  :isDisabled="jobCompletedDisabled"
                />
              </div>

              <!-- driver -->
              <div class="details-column">
                <InputQuery
                  placeholder="Driver Code"
                  :items="LorryInfoTabData['driverCodeList']"
                  itemKey="Driver Code"
                  descKey="Driver Name"
                  :errorMessage.sync="driverCodeErr"
                  :selectedObject.sync="selectedLorryInfoData['selectedDriverCodeList']"
                  :isRequired="driverCodeRequired"
                  :isDisabled="driverInfoDisabled"
                  ref="drivercode"
                />
                <div style="display: flex">
                  <Input
                    style="margin-right: 5px; width: 100px"
                    placeholder="EXP"
                    validator="positiveDecimal,14,4"
                    textAlign="left"
                    :isRequired="false"
                    :isDisabled="driverInfoDisabled"
                    :inputValue.sync="selectedLorryInfoData['driverEXP']"
                    ref="noDriverExp"
                    :errorMessage.sync="errorMessages['noDriverExp']"
                  />
                  <Input
                    style="margin-right: 5px; width: 100px"
                    placeholder="TOL"
                    validator="positiveDecimal,14,4"
                    textAlign="left"
                    :isRequired="false"
                    :isDisabled="driverInfoDisabled"
                    :inputValue.sync="selectedLorryInfoData['driverTOL']"
                    ref="noDriverTol"
                    :errorMessage.sync="errorMessages['noDriverTol']"
                  />
                </div>
              </div>
              <!-- attendant -->
              <div class="details-column">
                <InputQuery
                  placeholder="Attendant"
                  :items="LorryInfoTabData['attendantList']"
                  itemKey="Attendant Code"
                  descKey="Attendant Name"
                  :isDisabled="driverInfoDisabled"
                  :selectedObject.sync="selectedLorryInfoData['selectedAttendantList']"
                  ref="attendantList"
                />
                <div style="display: flex">
                  <Input
                    style="margin-right: 5px; width: 100px"
                    placeholder="EXP"
                    validator="positiveDecimal,14,4"
                    textAlign="left"
                    :isRequired="false"
                    :isDisabled="driverInfoDisabled"
                    :inputValue.sync="selectedLorryInfoData['attendantEXP']"
                    ref="noAttendantEXP"
                    :errorMessage.sync="errorMessages['noAttendantExp']"
                  />
                  <Input
                    style="margin-right: 5px; width: 100px"
                    placeholder="TOL"
                    validator="positiveDecimal,14,4"
                    textAlign="left"
                    :isRequired="false"
                    :isDisabled="driverInfoDisabled"
                    :inputValue.sync="selectedLorryInfoData['attendantTOL']"
                    ref="noAttendantTOL"
                    :errorMessage.sync="errorMessages['noAttendantTol']"
                  />
                </div>
              </div>
              <!-- attendant 2 -->
              <div class="details-column">
                <InputQuery
                  placeholder="Attendant 2"
                  :items="LorryInfoTabData['attendantList']"
                  itemKey="Attendant Code"
                  descKey="Attendant Name"
                  :isDisabled="driverInfoDisabled"
                  :selectedObject.sync="selectedLorryInfoData['selectedAttendantList2']"
                  ref="attendantList2"
                />
                <div style="display: flex">
                  <Input
                    style="margin-right: 5px; width: 100px"
                    placeholder="EXP"
                    validator="positiveDecimal,14,4"
                    textAlign="left"
                    :isRequired="false"
                    :isDisabled="driverInfoDisabled"
                    :inputValue.sync="selectedLorryInfoData['attendantEXP2']"
                    ref="noAttendantEXP2"
                    :errorMessage.sync="errorMessages['noAttendantExp2']"
                  />
                  <Input
                    style="margin-right: 5px; width: 100px"
                    placeholder="TOL"
                    validator="positiveDecimal,14,4"
                    textAlign="left"
                    :isRequired="false"
                    :isDisabled="driverInfoDisabled"
                    :inputValue.sync="selectedLorryInfoData['attendantTOL2']"
                    ref="noAttendantTOL2"
                    :errorMessage.sync="errorMessages['noAttendantTol2']"
                  />
                </div>
              </div>
            </div>

            <!-- Cargo Info Section -->
            <div class="tab-block">
              <div class="tab-title">Cargo Info</div>
              <div class="details-column2">
                <div class="table-container">
                  <div class="tabulator-title">
                    <div style="flex-grow: 1; display: flex; justify-content: flex-end; margin-right: 5px"
                      >Cargo Info</div
                    >
                    <!-- <button v-if="!viewMode" @click="showHideDetailsPopup('Cargo')">+</button>
                    <button v-if="!viewMode" @click="cargoInfoPopupDeleteDetails('Cargo')">-</button> -->
                    <button v-if="!viewMode" @click="showHideDetailsPopup('Cargo')">Add</button>
                    <button v-if="!viewMode" @click="showHideDetailsPopup('Cargo', true)">Edit</button>
                    <button v-if="!viewMode" @click="cargoInfoPopupDeleteDetails('Cargo')">Delete</button>
                  </div>
                  <TableBox
                    title="Cargo Info"
                    tableID="truckDetails"
                    :tableData="cargoInfoList"
                    :selectedRow.sync="selectedCargoInfoData['selectedCargoInfo']"
                    :hideColumns="['action', 'Seqno']"
                    :maxSelection="-1"
                    :additionalIconsIndex="[0]"
                  />

                  <JobPageCargoInfoPopup
                    ref="BillCodeRef"
                    v-if="cargoPopupDetails['cargoAddType']"
                    :title="cargoPopupDetails['cargoAddType'] == 'Cargo' ? 'Cargo Info' : 'Send To / Cargo Info'"
                    @cancel="cargoPopupCancel"
                    @addDetails="cargoInfoPopupAddDetails"
                    :jobDateTime="
                      selectedHeaderInfoData['jobTime'] ?
                        selectedHeaderInfoData['jobTime'].formatString('yyyy-MM-dd')
                      : ''
                    "
                    :productList="cargoInfoData['cargoProductList']"
                    :truckPackageList="cargoInfoData['cargoPackageList']"
                    :truckTypeList="cargoInfoData['cargoTruckTypeList']"
                    :truckBillTypeList="cargoInfoData['cargoBillTypeList']"
                    :truckBillCodeList="cargoInfoData['cargoBillCodeList']"
                    @sendToBillCode="billTypeSelected"
                    @sendToTruckType="truckTypeSelected"
                    :isEdit="cargoPopupDetails['isEdit']"
                    :editDetails="cargoPopupDetails['editDetails']"
                    :truckContSizeList="cargoInfoData['cargoContSizeList']"
                    :truckContTypeList="cargoInfoData['cargoContTypeList']"
                  />

                  <div style="display: flex; flex-direction: column; margin-top: 10px; align-items: self-end">
                    <div style="display: flex">
                      <p style="margin-left: 4px; margin-top: 2px; margin-right: 4px"> Total: </p>
                      <p style="margin-left: 4px; margin-top: 2px; margin-right: 4px"> Transport </p>
                      <Input
                        style="margin-right: 3px; width: 100px"
                        placeholder=" "
                        title="cargoToll"
                        textType="number"
                        textAlign="right"
                        :isRequired="false"
                        :isDisabled="true"
                        :inputValue.sync="selectedCargoInfoData['cargoToll']"
                      />
                      <p style="margin-left: 4px; margin-top: 2px; margin-right: 4px"> + </p>
                      <p style="margin-left: 4px; margin-top: 2px; margin-right: 4px"> Toll </p>
                      <Input
                        style="margin-right: 3px; width: 100px"
                        placeholder=" "
                        title="cargoTransport"
                        textType="number"
                        textAlign="right"
                        :isRequired="false"
                        :isDisabled="true"
                        :inputValue.sync="selectedCargoInfoData['cargoTransport']"
                      />
                      <p style="margin-left: 4px; margin-top: 2px; margin-right: 4px"> = </p>
                      <Input
                        style="margin-right: 3px; width: 100px"
                        placeholder=" "
                        title="cargoTotal"
                        textType="number"
                        textAlign="right"
                        :isRequired="false"
                        :isDisabled="true"
                        :inputValue.sync="selectedCargoInfoData['cargoTotal']"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Header Info tab -->
          <div slot="Header Info" style="height: 100%">
            <div class="slot-body-block" style="height: 100%">
              <div class="slot-content-column column-flex">
                <InputQuery
                  placeholder="Shipper/Consignee"
                  :items="headerInfoData['shipperConsignee']"
                  itemKey="Description"
                  descKey="Customer Name"
                  :errorMessage.sync="errorMessages['shipperConsignee']"
                  :selectedObject.sync="selectedShipperConsignee"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  ref="Shipper/Consignee"
                  @selected="shipperConsigneeSelected"
                  addLink="/Home/MasterFiles/ProfileMaster/ShipperConsignee?new=1"
                  :canRefresh="true"
                  @refreshClicked="shipperConsigneeInputQueryRefreshClicked"
                />
                <InputQuery
                  placeholder="Job Type"
                  :items="headerInfoData['jobType']"
                  itemKey="Job Type"
                  descKey="Description"
                  :errorMessage.sync="errorMessages['job_type']"
                  :selectedObject.sync="selectedJobType"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  ref="job_type"
                  @selected="jobTypeSelected"
                />
                <InputQuery
                  placeholder="Cargo"
                  :items="headerInfoData['cargo']"
                  itemKey="Cargo Type"
                  descKey="Description"
                  :selectedObject.sync="selectedHeaderInfoData['selectedCargo']"
                  :isDisabled="viewMode"
                  ref="cargo"
                />
                <Input
                  style="width: 46.5%"
                  placeholder="Request By"
                  :inputValue.sync="selectedHeaderInfoData['selectedRequestBy']"
                  :isDisabled="viewMode"
                />
                <DateTimePicker
                  placeholder="Required Date/Time From"
                  title="Required Date/Time From"
                  :value.sync="selectedHeaderInfoData['reqJobTime']"
                  :isDisabled="viewMode"
                />
                <DateTimePicker
                  placeholder="Required Date/Time To"
                  title="Required Date/Time To"
                  :value.sync="selectedHeaderInfoData['reqJobTimeTo']"
                  :isDisabled="viewMode"
                />
                <CheckBoxV2
                  class="filter-check"
                  title="Office Hours"
                  :isChecked.sync="selectedLorryInfoData['isOfficeHours']"
                  :isDisabled="viewMode"
                />
                <InputQuery
                  placeholder="Shipper"
                  :items="headerInfoData['shipper']"
                  itemKey="Customer Code"
                  descKey="Customer Name"
                  :selectedObject.sync="selectedHeaderInfoData['selectedShipper']"
                  :isDisabled="viewMode"
                  ref="Shipper"
                />
                <InputQuery
                  placeholder="Consignee"
                  :items="headerInfoData['consignee']"
                  itemKey="Customer Code"
                  descKey="Customer Name"
                  :selectedObject.sync="selectedHeaderInfoData['selectedConsignee']"
                  :isDisabled="viewMode"
                  ref="Consignee"
                />
              </div>

              <div class="slot-content-column">
                <Input
                  placeholder="Project #"
                  :inputValue.sync="selectedHeaderInfoData['Projno']"
                  :isDisabled="viewMode"
                  validator="text,25,alphanumeric"
                />
                <Input
                  placeholder="Cust Ref #"
                  :inputValue.sync="selectedHeaderInfoData['CustRefno']"
                  :isDisabled="viewMode"
                  validator="text,40,alphanumeric"
                />
                <Input
                  placeholder="D/O #"
                  :inputValue.sync="selectedHeaderInfoData['DOno']"
                  :isDisabled="viewMode"
                  :isRequired="true"
                  ref="delivery_order"
                  :errorMessage.sync="errorMessages['delivery_order']"
                  validator="text,40,alphanumeric"
                />
                <InputQuery
                  placeholder="Bill To"
                  :items="headerInfoData['billTo']"
                  itemKey="Description"
                  descKey="Customer Name"
                  ref="billcu_no"
                  :errorMessage.sync="errorMessages['billcu_no']"
                  :selectedObject.sync="selectedHeaderInfoData['selectedBillTo']"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  @selected="billToSelected"
                  addLink="/Home/MasterFiles/ProfileMaster/Customer?new=1"
                  :canRefresh="true"
                  @refreshClicked="billToInputQueryRefreshClicked"
                />
                <div style="display: flex">
                  <Dropdown
                    style="margin-right: 5px; width: 200px"
                    placeholder="Bill Fmt"
                    :items="headerInfoData['billingfmt']"
                    itemKey="Description"
                    :isDisabled="viewMode || bilfmtIsDisabled"
                    :isRequired="true"
                    :errorMessage.sync="errorMessages['billfmt']"
                    :selectedObject.sync="selectedHeaderInfoData['selectedBillingFmt']"
                    ref="billfmt"
                  />
                  <InputQuery
                    placeholder="Bill By"
                    :items="headerInfoData['billBy']"
                    itemKey="Description"
                    descKey="Description"
                    :selectedObject.sync="selectedHeaderInfoData['selectedBillBy']"
                    :isDisabled="viewMode"
                  />
                </div>
              </div>

              <div class="slot-content-column">
                <Input
                  placeholder="Job #"
                  :inputValue.sync="selectedHeaderInfoData['jobNo']"
                  :isRequired="true"
                  :isDisabled="true"
                />
                <DateTimePicker
                  placeholder="Job Time"
                  title="Job Time"
                  :value.sync="selectedHeaderInfoData['jobTime']"
                  :isRequired="true"
                  :isDisabled="viewMode"
                  :maxDate="smallDateTimeMaxDate"
                  :minDate="smallDateTimeMinValue"
                  :errorMessage.sync="jobTimeErr"
                  @dateChanged="checkDateTime('Job Time', $event)"
                />
                <Input
                  placeholder="Job State"
                  :inputValue.sync="selectedHeaderInfoData['jobState']"
                  :isDisabled="true"
                />
                <ItemListing
                  style="min-height: 150px; max-height: 300px"
                  title="Services"
                  :items="headerInfoData['services']"
                  :isDisabled="viewMode"
                />
                <InputQuery
                  placeholder="Operation Team"
                  :items="headerInfoData['operationTeam']"
                  itemKey="Team"
                  descKey="Description"
                  ref="operationTeam"
                  :selectedObject.sync="selectedHeaderInfoData['selectedOperationTeam']"
                  :isDisabled="viewMode"
                />
                <Input
                  style="width: 46.5%"
                  placeholder="Sales Team"
                  :inputValue.sync="selectedHeaderInfoData['salesTeam']"
                  :isDisabled="true"
                />
                <Input
                  style="width: 46.5%"
                  placeholder="Sales Person"
                  :inputValue.sync="selectedHeaderInfoData['salesPerson']"
                  :isDisabled="true"
                />
                <Input
                  style="width: 46.5%"
                  placeholder="Service Team"
                  :inputValue.sync="selectedHeaderInfoData['serviceTeam']"
                  :isDisabled="true"
                />
                <Input
                  style="width: 46.5%"
                  placeholder="Service Person"
                  :inputValue.sync="selectedHeaderInfoData['servicePerson']"
                  :isDisabled="true"
                />
              </div>
            </div>
          </div>

          <!-- Cargo Info Tab -->
          <div slot="Send To / Cargo Info">
            <div slot="Quick Entry">
              <div class="tab-block">
                <div class="tab-title">Send To</div>
                <div class="details-column4">
                  <InputQuery
                    placeholder="Pick Up"
                    :items="headerInfoData['pickup']"
                    itemKey="Description"
                    descKey="Company"
                    ref="pickup"
                    :errorMessage.sync="errorMessages['pickup']"
                    :selectedObject.sync="selectedHeaderInfoData['selectedPickUp']"
                    :isRequired="true"
                    :isDisabled="viewMode"
                    @selected="pickUpSelected"
                    addLink="/Home/MasterFiles/OperationMaster/Destination?new=1"
                    :canRefresh="true"
                    @refreshClicked="destinationInputQueryRefreshClicked"
                  />
                  <div style="display: flex">
                    <Input
                      style="margin-right: 5px"
                      placeholder="Tariff Area"
                      textAlign="right"
                      :isDisabled="true"
                      :inputValue.sync="selectedHeaderInfoData['tariffAreaPickup']"
                      ref="tariffArea"
                    />
                    <Input
                      placeholder="Zones"
                      textAlign="right"
                      :isDisabled="true"
                      :inputValue.sync="selectedHeaderInfoData['zonesPickup']"
                      ref="zones"
                    />
                  </div>
                  <textarea
                    class="address-details"
                    disabled
                    v-model="selectedHeaderInfoData['pickUpAddress']"
                  ></textarea>
                </div>
                <div class="details-column4">
                  <InputQuery
                    placeholder="Transit 1"
                    :items="headerInfoData['transit1']"
                    itemKey="Description"
                    descKey="Company"
                    ref="transit1"
                    :selectedObject.sync="selectedHeaderInfoData['selectedTransit1']"
                    :isDisabled="viewMode"
                    @selected="transit1Selected"
                    addLink="/Home/MasterFiles/OperationMaster/Destination?new=1"
                    :canRefresh="true"
                    @refreshClicked="destinationInputQueryRefreshClicked"
                  />
                  <div style="display: flex">
                    <Input
                      style="margin-right: 5px"
                      :items="headerInfoData['tariffArea']"
                      placeholder="Tariff Area"
                      textAlign="right"
                      :isDisabled="true"
                      :inputValue.sync="selectedHeaderInfoData['tariffAreaTransit1']"
                      ref="tariffArea"
                    />
                    <Input
                      :items="headerInfoData['zones']"
                      placeholder="Zones"
                      textAlign="right"
                      :isDisabled="true"
                      :inputValue.sync="selectedHeaderInfoData['zonesTransit1']"
                      ref="zones"
                    />
                  </div>
                  <textarea
                    class="address-details"
                    disabled
                    v-model="selectedHeaderInfoData['transit1Address']"
                  ></textarea>
                </div>
                <div class="details-column4">
                  <InputQuery
                    placeholder="Transit 2"
                    :items="headerInfoData['transit2']"
                    itemKey="Description"
                    descKey="Company"
                    ref="transit2"
                    :selectedObject.sync="selectedHeaderInfoData['selectedTransit2']"
                    :isDisabled="viewMode"
                    @selected="transit2Selected"
                    addLink="/Home/MasterFiles/OperationMaster/Destination?new=1"
                    :canRefresh="true"
                    @refreshClicked="destinationInputQueryRefreshClicked"
                  />
                  <div style="display: flex">
                    <Input
                      style="margin-right: 5px"
                      placeholder="Tariff Area"
                      textAlign="right"
                      :isDisabled="true"
                      :inputValue.sync="selectedHeaderInfoData['tariffAreaTransit2']"
                      ref="tariffArea"
                    />
                    <Input
                      placeholder="Zones"
                      textAlign="right"
                      :isDisabled="true"
                      :inputValue.sync="selectedHeaderInfoData['zonesTransit2']"
                      ref="zones"
                    />
                  </div>
                  <textarea
                    class="address-details"
                    disabled
                    v-model="selectedHeaderInfoData['transit2Address']"
                  ></textarea>
                </div>
                <div class="details-column4">
                  <InputQuery
                    placeholder="Drop Off"
                    :items="headerInfoData['dropoff']"
                    itemKey="Description"
                    descKey="Company"
                    ref="dropoff"
                    :errorMessage.sync="errorMessages['dropoff']"
                    :selectedObject.sync="selectedHeaderInfoData['selectedDropOff']"
                    :isRequired="true"
                    :isDisabled="viewMode"
                    @selected="dropOffSelected"
                    addLink="/Home/MasterFiles/OperationMaster/Destination?new=1"
                    :canRefresh="true"
                    @refreshClicked="destinationInputQueryRefreshClicked"
                  />
                  <div style="display: flex">
                    <Input
                      style="margin-right: 5px"
                      placeholder="Tariff Area"
                      textAlign="right"
                      :isDisabled="true"
                      :inputValue.sync="selectedHeaderInfoData['tariffAreaDropoff']"
                      ref="tariffArea"
                    />
                    <Input
                      placeholder="Zones"
                      textAlign="right"
                      :isDisabled="true"
                      :inputValue.sync="selectedHeaderInfoData['zonesDropoff']"
                      ref="zones"
                    />
                  </div>
                  <textarea
                    class="address-details"
                    disabled
                    v-model="selectedHeaderInfoData['dropOffAddress']"
                  ></textarea>
                </div>
              </div>

              <div class="tab-block">
                <div class="tab-title">Job Total</div>
                <div class="details-column">
                  <Input
                    :items="headerInfoData['cargoContainerNo']"
                    placeholder="Container #"
                    textAlign="right"
                    :isDisabled="true"
                    :inputValue.sync="selectedHeaderInfoData['selectedCargoContainerNo']"
                    ref="cargoContainerNo"
                  />
                  <Input
                    :items="headerInfoData['cargoQuantity']"
                    placeholder="Quantity"
                    textAlign="right"
                    :isDisabled="true"
                    :inputValue.sync="selectedHeaderInfoData['selectedCargoQuantity']"
                    ref="cargoQuantity"
                  />
                  <Input
                    :items="headerInfoData['cargoPallet']"
                    placeholder="Pallet"
                    textAlign="right"
                    :inputValue.sync="selectedHeaderInfoData['selectedCargoPallet']"
                    ref="cargoPallet"
                    validator="integer,8"
                    :isDisabled="viewMode"
                  />
                </div>
                <div class="details-column">
                  <Dropdown
                    placeholder="Package"
                    :items="cargoInfoData['cargoPackageList']"
                    itemKey="Package Code"
                    ref="cargoPackage"
                    :selectedObject.sync="selectedHeaderInfoData['selectedCargoPackage']"
                    :isDisabled="viewMode"
                  />
                  <div style="display: flex">
                    <Input
                      placeholder="Gross weight"
                      textAlign="right"
                      :isDisabled="true"
                      :inputValue.sync="selectedHeaderInfoData['selectedCargoGrossWeight']"
                      ref="cargoGrossWeight"
                    />
                    <p style="margin-top: 2px; margin-left: 5px"> KG </p>
                  </div>
                  <div style="display: flex">
                    <Input
                      placeholder="Gross Volume"
                      textAlign="right"
                      :isDisabled="true"
                      :inputValue.sync="selectedHeaderInfoData['selectedCargoGrossVolume']"
                      ref="cargoGrossWeight"
                    />
                    <p style="margin-top: 2px; margin-left: 5px"> M3 </p>
                  </div>
                </div>
                <div class="details-column">
                  <Input
                    placeholder="Ttl Volumetric Weight"
                    textAlign="right"
                    :isDisabled="true"
                    :inputValue.sync="selectedHeaderInfoData['selectedCargoTtlVolumetricWeight']"
                    ref="cargoTtlVolumetricWeight"
                  />
                  <Input
                    placeholder="Est. Trucks"
                    textAlign="right"
                    :inputValue.sync="selectedHeaderInfoData['selectedCargoEstTrucks']"
                    ref="estTrucks"
                    validator="integer,1"
                    :isDisabled="viewMode"
                  />
                  <Dropdown
                    placeholder="Truck Type"
                    :items="cargoInfoData['cargoTruckTypeList']"
                    itemKey="Truck Type"
                    ref="cargoTruckType"
                    :selectedObject.sync="selectedHeaderInfoData['selectedCargoTruckType']"
                    :isDisabled="viewMode"
                  />
                </div>
              </div>
              <div class="tab-block">
                <div class="tab-title">Cargo Info</div>
                <div class="details-column2">
                  <div class="table-container">
                    <div class="tabulator-title">
                      <div style="flex-grow: 1; display: flex; justify-content: flex-end; margin-right: 5px"
                        >Send To / Cargo Info</div
                      >
                      <button v-if="!viewMode" @click="showHideDetailsPopup('Send To')">Add</button>
                      <button v-if="!viewMode" @click="showHideDetailsPopup('Send To', true)">Edit</button>
                      <button v-if="!viewMode" @click="cargoInfoPopupDeleteDetails('Send To')">Delete</button>
                    </div>
                    <TableBox
                      title="Send To / Cargo Info"
                      tableID="truckDetails"
                      :tableData="cargoInfoList"
                      :selectedRow.sync="selectedCargoInfoData['selectedCargoInfo']"
                      :hideColumns="['action', 'Seqno']"
                      :maxSelection="-1"
                      :additionalIconsIndex="[0]"
                    />
                  </div>

                  <JobPageCargoInfoPopup
                    ref="BillCodeRef"
                    v-if="cargoPopupDetails['cargoAddType']"
                    :title="cargoPopupDetails['cargoAddType'] == 'Cargo' ? 'Cargo Info' : 'Send To / Cargo Info'"
                    @cancel="cargoPopupDetails['cargoAddType'] = ''"
                    @addDetails="cargoInfoPopupAddDetails"
                    :productList="cargoInfoData['cargoProductList']"
                    :truckPackageList="cargoInfoData['cargoPackageList']"
                    :truckTypeList="cargoInfoData['cargoTruckTypeList']"
                    :truckBillTypeList="cargoInfoData['cargoBillTypeList']"
                    :truckBillCodeList="cargoInfoData['cargoBillCodeList']"
                    :truckContNoList="cargoInfoData['cargoContNoList']"
                    :truckContSizeList="cargoInfoData['cargoContSizeList']"
                    :truckContTypeList="cargoInfoData['cargoContTypeList']"
                    :truckVolumeDivisorList="cargoInfoData['cargoVolumeDivisorList']"
                    :isEdit="cargoPopupDetails['isEdit']"
                    :editDetails="cargoPopupDetails['editDetails']"
                    @sendToBillCode="billTypeSelected"
                    @sendToTruckType="truckTypeSelected"
                    :jobDateTime="
                      selectedHeaderInfoData['jobTime'] ?
                        selectedHeaderInfoData['jobTime'].formatString('yyyy-MM-dd')
                      : ''
                    "
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Documents Tab -->
          <div slot="Documents" :style="{ height: viewMode ? '100%' : 'calc(100% - 28px)' }" class="documents">
            <div v-if="showDocumentsLoader" class="loader">
              <span class="loader-text">Loading Documents...</span>
            </div>
            <div class="top-button-section" id="docPopup" v-if="!viewMode">
              <button @click="addDoc">
                <img src="https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png" height="15" />
                Add Documents
              </button>
              <button @click="editDoc">
                <img src="https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png" height="15" />
                Edit Documents
              </button>
              <button @click="deleteDoc">
                <img src="https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png" height="15" />
                Delete Documents
              </button>
            </div>

            <DocStatusUpdate
              :showPopup.sync="documentInfoData['updatePopup']"
              :statusList.sync="documentInfoData['statusList']"
              :docTypeList.sync="documentInfoData['docTypeList']"
              :docStatusList.sync="documentInfoData['docStatusList']"
              defaultCategory="document"
              @updateClicked="documentUpdate"
              :title="`Update Documents for Job # ${selectedHeaderInfoData['jobNo']}`"
              :editDetailsList.sync="editDocumentsList"
            />

            <TableBox
              :tableData="selectedDocumentInfoData['docData']"
              groupBy="ID"
              :selectedRow.sync="selectedDocumentInfoData['selectedDoc']"
              :additionalButtons="viewDocumentButtons"
              @buttonClicked="viewDocumentClicked"
              title="Documents"
              :maxSelection="1"
              :hideColumns="[
                'Document GUID',
                'documentguid',
                'statusseqno',
                'ID',
                'fileAtt',
                'filetype',
                'Action',
                'Doc Image',
                'Row #',
              ]"
            />
          </div>

          <!-- Shipping Info tab -->
          <div slot="Shipping Info" style="height: 100%">
            <div class="slot-body-block">
              <!-- Job type == ("R" / "E-CB HDG" / "L-BKH" / "L-SA" / "I-CB" / "B" ) refer jobTypeSelected-->
              <div v-if="changeFieldCaption" class="slot-content-column column-flex">
                <InputQuery
                  placeholder="Fleet #"
                  :items="shippingInfoData['shipCallNoList']"
                  itemKey="SCN - Terminal"
                  descKey="Vessel Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedShipCall']"
                  :isRequired="loadPortMandatory"
                  @selected="shipCallNoSelected"
                  :allowFreeText="true"
                  :search.sync="shipCallNoSearch"
                  :isLoading="shipCallNoLoading"
                />
                <Input
                  placeholder="Voyage #"
                  :inputValue.sync="selectedShippingInfoData['voyNo']"
                  :isDisabled="viewMode"
                  ref="shipcallid"
                  :errorMessage.sync="errorMessages['shipcallid']"
                  validator="text,10,alphanumeric"
                />
                <InputQuery
                  placeholder="Vehicle Code"
                  :items="shippingInfoData['vesselCodeList']"
                  itemKey="Description"
                  descKey="Vessel Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedVesselCode']"
                  :isRequired="loadPortMandatory"
                  ref="vesselCode"
                  :errorMessage.sync="errorMessages['vesselCode']"
                />
                <InputQuery
                  placeholder="Shipping Agent"
                  :items="shippingInfoData['shippingAgentList']"
                  itemKey="Description"
                  descKey="Agent Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedAgentList']"
                />
                <Input
                  placeholder="Fleet Line"
                  :inputValue.sync="selectedShippingInfoData['selectedShippingLine']"
                  validator="text,50,alphanumeric"
                  :isDisabled="viewMode"
                />
                <InputQuery
                  placeholder="Agent"
                  :items="shippingInfoData['agentList']"
                  itemKey="Description"
                  descKey="Agent Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedAgent']"
                />
                <InputQuery
                  placeholder="Fleet Agent"
                  :items="shippingInfoData['forwardingAgent']"
                  itemKey="Description"
                  descKey="Agent Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedForwardingAgent']"
                />
              </div>

              <!-- Job Type ==  (I / E / C / CC / D) refer jobTypeSelected-->
              <div v-else class="slot-content-column column-flex">
                <InputQuery
                  placeholder="Ship Call #"
                  :items="shippingInfoData['shipCallNoList']"
                  itemKey="SCN - Terminal"
                  descKey="Vessel Name"
                  ref="shipcallno"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedShipCall']"
                  :isRequired="loadPortMandatory"
                  @selected="shipCallNoSelected"
                  :allowFreeText="true"
                  :search.sync="shipCallNoSearch"
                  :isLoading="shipCallNoLoading"
                />
                <Input
                  placeholder="Voyage #"
                  :inputValue.sync="selectedShippingInfoData['voyNo']"
                  :isDisabled="viewMode"
                  validator="text,10,alphanumeric"
                />
                <InputQuery
                  placeholder="Vessel Code"
                  :items="shippingInfoData['vesselCodeList']"
                  itemKey="Description"
                  descKey="Vessel Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedVesselCode']"
                  :isRequired="
                    selectedJobType ? selectedJobType['Job Type'] == 'I' || selectedJobType['Job Type'] == 'E' : false
                  "
                />
                <InputQuery
                  placeholder="Shipping Agent"
                  :items="shippingInfoData['shippingAgentList']"
                  itemKey="Description"
                  descKey="Agent Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedAgentList']"
                />
                <Input
                  placeholder="Shipping Line"
                  validator="text,50,alphanumeric"
                  :inputValue.sync="selectedShippingInfoData['selectedShippingLine']"
                  :isDisabled="viewMode"
                />
                <InputQuery
                  placeholder="Agent"
                  :items="shippingInfoData['agentList']"
                  itemKey="Description"
                  descKey="Agent Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedAgent']"
                />
                <InputQuery
                  placeholder="Forwarding Agent"
                  :items="shippingInfoData['forwardingAgent']"
                  itemKey="Description"
                  descKey="Agent Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedForwardingAgent']"
                />
              </div>

              <div class="slot-content-column">
                <InputQuery
                  placeholder="Transhipment Port"
                  :items="shippingInfoData['transhipmentPort']"
                  itemKey="Description"
                  descKey="Port Location"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedTranshipmentPort']"
                  addLink="/Home/MasterFiles/OperationMaster/Port?new=1"
                  :canRefresh="true"
                  @refreshClicked="portInputQueryRefreshClicked"
                />
                <InputQuery
                  v-if="dischargePortMode"
                  placeholder="Discharge Port"
                  :items="shippingInfoData['dischargeLoadPort']"
                  itemKey="Description"
                  descKey="Port Location"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedDischargeLoadPort']"
                  addLink="/Home/MasterFiles/OperationMaster/Port?new=1"
                  :canRefresh="true"
                  @refreshClicked="portInputQueryRefreshClicked"
                />
                <!-- <InputQuery v-else-if="loadPortMode" placeholder="Load Port" :items="shippingInfoData['dischargeLoadPort']" itemKey="Port Code" descKey="Port Location" :isDisabled="viewMode"
                    :selectedObject.sync="selectedShippingInfoData['selectedDischargeLoadPort']" /> -->
                <InputQuery
                  v-else
                  placeholder="Load Port"
                  :items="shippingInfoData['dischargeLoadPort']"
                  itemKey="Description"
                  descKey="Port Location"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedDischargeLoadPort']"
                  addLink="/Home/MasterFiles/OperationMaster/Port?new=1"
                  :canRefresh="true"
                  @refreshClicked="portInputQueryRefreshClicked"
                />
                <Input
                  placeholder="Final Destination"
                  validator="text,10,alphanumeric"
                  :inputValue.sync="selectedShippingInfoData['selectedFinalDestination']"
                  :isDisabled="viewMode"
                />
                <Input
                  placeholder="Booking Ref #"
                  :inputValue.sync="selectedShippingInfoData['selectedBookingRefNo']"
                  :isRequired="dischargePortMandatory"
                  ref="bkgrefno"
                  :isDisabled="viewMode"
                  validator="text,25"
                />
                <InputQuery
                  placeholder="Local Carrier"
                  :items="shippingInfoData['localCarrier']"
                  itemKey="Description"
                  descKey="Agent Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedLocalCarrier']"
                />
                <!-- <InputQuery placeholder="Local Carrier" :items="shippingInfoData['localCarrier']" itemKey="Local Carrier" descKey="Local Carrier" :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedLocalCarrier']" /> -->
                <InputQuery
                  placeholder="Foreign Forwarding Agent"
                  :items="shippingInfoData['foreignForwardingAgent']"
                  itemKey="Description"
                  descKey="Agent Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedForeignForwardingAgent']"
                />
                <InputQuery
                  placeholder="Foreign Carrier"
                  :items="shippingInfoData['foreignCarrier']"
                  itemKey="Description"
                  descKey="Agent Name"
                  :isDisabled="viewMode"
                  :selectedObject.sync="selectedShippingInfoData['selectedForeignCarrier']"
                />
              </div>
              <div class="slot-content-column">
                <DateTimePicker
                  placeholder="ETA Date/Time"
                  title="ETA Date/Time"
                  :value.sync="selectedShippingInfoData['ETADateTime']"
                  :isDisabled="viewMode"
                />
                <DateTimePicker
                  placeholder="ETD Date/Time"
                  title="ETD Date/Time"
                  :value.sync="selectedShippingInfoData['ETDDateTime']"
                  :isDisabled="viewMode"
                />
                <DateTimePicker
                  placeholder="Close Date/Time"
                  title="Close Date/Time"
                  :value.sync="selectedShippingInfoData['closeDateTime']"
                  :isDisabled="viewMode"
                />
                <DateTimePicker
                  placeholder="Free Storage Until"
                  title="Free Storage Until"
                  :value.sync="selectedShippingInfoData['freeStorageUntil']"
                  :isDisabled="viewMode"
                />
                <DateTimePicker
                  v-if="unstuffviewmode"
                  placeholder="Stuffing Date/Time"
                  title="Stuffing Date/Time"
                  :value.sync="selectedShippingInfoData['unstuffingDateTime']"
                  :isDisabled="viewMode"
                />
                <DateTimePicker
                  v-else
                  placeholder="Unstuffing Date/Time"
                  title="Unstuffing Date/Time"
                  :value.sync="selectedShippingInfoData['unstuffingDateTime']"
                  :isDisabled="viewMode"
                />
              </div>
            </div>
            <div class="shipping-textarea">
              <RemarksBox
                title="Goods Description"
                :text.sync="selectedShippingInfoData['goodsDescription']"
                style="height: 100%"
                :freeText="true"
                :isDisabled="viewMode"
              />
              <Tooltip
                position="left"
                :minWidth="300"
                tooltipText="The text will be reformatted upon save to accommodate printing of official forms and EDI declaration."
              />
              <RemarksBox
                title="Shipping Marks"
                :text.sync="selectedShippingInfoData['shippingMarks']"
                style="height: 100%"
                :freeText="true"
                :isDisabled="viewMode"
              />
              <Tooltip
                position="left"
                :minWidth="300"
                tooltipText="The text will be reformatted upon save to accommodate printing of official forms and EDI declaration."
              />
            </div>
          </div>
        </Tab>
      </div>
    </PopupEntry>

    <SimplePopup v-if="showDocUpload" :show.sync="showDocUpload" :title="popupTitle" @close="closeClicked">
      <JobFormComponent
        ref="jobformcomponent"
        @submit="submitVerifiedInfo"
        :headerInfoData="headerInfoData"
        :generalInfoData="generalInfoData"
        :shippingInfoData="shippingInfoData"
        :cargoInfoData="cargoInfoData"
        :documentInfoData="documentInfoData"
      />
    </SimplePopup>

    <PopupV2
      :show="showSavePopup"
      :type="savePopupType"
      :title="savePopupTitle"
      :buttons="savePopupButtons"
      @buttonClicked="savePopupButtonClicked"
    >
      <div v-if="saveAlertType == 'required' || saveAlertType == 'requiredCantSave'">
        <div v-if="saveAlertType == 'requiredCantSave'">
          In order to save a job, the field{{ saveErrorList.length > 1 ? 's' : '' }} below
          {{ saveErrorList.length > 1 ? 'are' : 'is' }} required!
        </div>
        <div class="save-content">
          <span v-for="(e, i) in saveErrorList" :key="i">{{ i + 1 }}. {{ e }}<br /></span>
        </div>
        <div v-if="saveAlertType == 'required'">Saving will make this an unconfirm job</div>
        <div v-if="saveAlertType == 'required'" style="margin-bottom: 5px"
          >Do you want to continue to save this job?</div
        >
      </div>
      <div
        v-if="saveAlertType == 'choiceMode' || saveAlertType == 'required'"
        :style="{ textAlign: 'left', minHeight: reasonOpen ? '200px' : '' }"
      >
        <RadioButtonSet
          v-if="saveAlertType == 'choiceMode'"
          title="Save Job as"
          :buttons="saveRadioButton"
          labelKey="label"
          :selected.sync="selectedSaveBy"
          :isRequired="true"
          :isDisabled="viewMode"
          :isHorizontal="true"
          style="margin-bottom: 5px"
        />
        <div v-if="!saveAsConfirm || saveAlertType == 'required'" style="padding-left: 10px">
          <Dropdown
            :isRequired="true"
            placeholder="Reason"
            :items="reasonsList"
            :selectedObject.sync="selectedUnconfirmReason"
            itemKey="Reason"
            @focusChanged="reasonFocusChanged"
          />
          <Input
            :isRequired="selectedUnconfirmReason ? selectedUnconfirmReason['Reason'].includes('OTHERS') : false"
            placeholder="Remarks"
            :inputValue.sync="unconfirmRemarks"
          />
          <div class="popup-error">{{ popupErrorText }}</div>
        </div>
      </div>
    </PopupV2>

    <PopupV2 :show="rotVerifyPopup" type="error" title="Error Creating New ROT" @buttonClicked="rotVerifyPopup = false">
      <div style="white-space: pre-wrap">{{ rotVerifyError }}</div>
    </PopupV2>

    <PopupV2
      :show="defaultPopup['show']"
      :type="defaultPopup['type']"
      :title="defaultPopup['title']"
      @buttonClicked="defaultPopupButtonClicked"
    >
      <div style="white-space: pre-wrap">{{ defaultPopup['content'] }}</div>
    </PopupV2>

    <!-- #region EDN -->
    <div v-if="showEDN" class="edn-popup">
      <div class="edn-content">
        <div class="top-section">
          <ButtonFilter filterPosition="right" buttonText="Change Filter" :filterWidth="300" :isOpen.sync="filterOpen">
            <div class="filter-section">
              <Dropdown placeholder="From" />
              <BoxContainer title="DNs">
                <CheckBoxV2
                  title="All DNs"
                  @checkBoxClicked="dnCheckboxClicked('allDNChecked')"
                  :isChecked.sync="allDNChecked"
                />
                <BoxContainer
                  title="ROT Range"
                  :isCheckBox="true"
                  :isChecked.sync="rangeDNChceked"
                  @checkBoxClicked="dnCheckboxClicked('rangeDNChceked')"
                >
                  <DateTimePicker
                    placeholder="From"
                    :value.sync="dnRangeFrom"
                    :isDisabled="!rangeDNChceked"
                    :isRequired="rangeDNChceked"
                    :errorMessage.sync="dnRangeFromError"
                  />
                  <DateTimePicker
                    placeholder="To"
                    :value.sync="dnRangeTo"
                    :isDisabled="!rangeDNChceked"
                    :isRequired="rangeDNChceked"
                    :errorMessage.sync="dnRangeToError"
                  />
                </BoxContainer>
              </BoxContainer>
              <button class="refresh-button" @click="refreshDNClicked">Refresh</button>
            </div>
          </ButtonFilter>
          <div>Last Refreshed: {{ eDNLastRefreshed }}</div>
        </div>
        <div class="table-section">
          <TableBox title="eDN" :tableData="ednListing" :selectedRow.sync="selectedeDN" />
        </div>
        <div class="bottom-section">
          <button :disabled="selectedeDN.length == 0" @click="eDNAccept">Accept</button>
          <button :disabled="selectedeDN.length == 0" @click="eDNReject">Reject</button>
          <button class="cancel-btn" @click="showEDN = false">Cancel</button>
        </div>
      </div>
    </div>
    <Verification
      style="z-index: 200"
      v-if="showVerification"
      :show.sync="showVerification"
      :sections="eDNSections"
      @confirmClicked="verificationConfirm"
      title="Verify Details"
      showAttachment
      :attachmentsList="eDNAttachments"
    >
      <div slot="Quick Entry"></div>
      <div slot="Header Info"></div>
      <div slot="Send To / Cargo Info"></div>
    </Verification>

    <PopupV2
      :show="showDNReject"
      type="warning"
      title="Reject eDN"
      :buttons="eDNRejectButtons"
      @buttonClicked="eDNRejectButtonsClicked"
    >
      <div class="edn-reject-content">
        <div
          >You're about to reject an eDN with Request No:
          {{ selectedeDN.length > 0 ? selectedeDN[0]['AutoRequestNo'] : '' }}</div
        ><br />
        <div>To reject an eDN, you'll need send a rejected eDN notice.</div><br />
        <div
          >Please enter the email addresses that the notice will be send to, the message of the email and the reason for
          the eDN rejection below.</div
        ><br />
        <EmailInput style="margin-bottom: 5px" title="To" :mails.sync="rejectToEmails" />
        <div class="collapsible-title-container" @click="showCC = !showCC">
          <img
            :style="{ transform: `scaleY(${showCC ? '-1' : '1'})` }"
            src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
          />
          <div>CC ({{ rejectCCEmails.length }})</div>
        </div>
        <EmailInput v-if="showCC" style="margin-bottom: 5px" title="CC" :mails.sync="rejectCCEmails" />
        <div class="collapsible-title-container" @click="showBCC = !showBCC">
          <img
            :style="{ transform: `scaleY(${showBCC ? '-1' : '1'})` }"
            src="https://dsmlib.logistics.systems/Icons/icons8-expand-arrow-24.png"
          />
          <div>BCC ({{ rejectBCCEmails.length }})</div>
        </div>
        <EmailInput v-if="showBCC" style="margin-bottom: 5px" title="BCC" :mails.sync="rejectBCCEmails" />
        <RemarksBox
          class="message-reason-container"
          title="Email Message"
          :freeText="true"
          :text.sync="eDNRejectMessage"
        />
        <RemarksBox
          class="message-reason-container"
          title="Reason for Rejection"
          :freeText="true"
          :text.sync="eDNRejectReason"
        />
      </div>
    </PopupV2>
    <!-- #endregion -->

    <PopupV2
      :show="showDeletePopup"
      title="Delete Job"
      @buttonClicked="deletePopupButtonClicked"
      :buttons="deletePopupButtons"
    >
      <div>Are you sure you want to delete Job # {{ selectedJob.length > 0 ? selectedJob[0]['Job No'] : '' }}?</div>
      <div v-if="selectedJob.length > 0 ? selectedJob[0]['Job Assigned'] > 0 : false"
        ><br />Consignment under this Job ('{{ selectedJob.length > 0 ? selectedJob[0]['Job No'] : '' }}') has been
        assigned. Continue to delete?</div
      >

      <Input
        placeholder="Remarks"
        :inputValue.sync="deleteRemarks"
        :errorMessage.sync="deleteRemarksError"
        :isRequired="true"
      />
    </PopupV2>

    <PopupV2
      :show="showConsignmentDeletePopup"
      title="Delete Consigment"
      @buttonClicked="consignmentPopupDeleteDetails"
      :buttons="deletePopupButtons"
    >
      <div>Please state reason to delete {{ selectedJob.length > 0 ? selectedJob[0]['Consignment'] : '' }}?</div>
      <Input
        placeholder="Delete Remarks"
        :inputValue.sync="selectedCargoInfoData['cndeleteremark']"
        :errorMessage.sync="deleteRemarksError"
        :isRequired="true"
      />
    </PopupV2>

    <!-- #region Print Popup -->
    <PopupV2
      :show="printPopup"
      title="Print Delivery Order / Gate Pass"
      @buttonClicked="printPopupButtonClicked"
      :buttons="printPopupButtons"
    >
      <div>Please choose the report type to print</div>
      <CheckBoxV2 v-if="DNOrderView" title="Delivery Order" :isChecked.sync="printDOChecked" />
      <CheckBoxV2 title="Gate Pass" :isChecked.sync="printGatePassChecked" />
    </PopupV2>

    <div class="designer-v2-popup" v-if="showReportPopup && (printDOChecked || printGatePassChecked)">
      <div class="designer-container" v-if="printDOChecked">
        <div class="title">Delivery Order</div>
        <DesignerV2
          :viewMode="true"
          :reportFile="doReportFile"
          reportFolder="ROTForm"
          :reportData="reportData"
          :show.sync="printDOChecked"
        />
      </div>
      <div class="designer-container" v-if="printGatePassChecked">
        <div class="title">Gate Pass</div>
        <DesignerV2
          :viewMode="true"
          :reportFile="gatePassReportFile"
          reportFolder="ROTForm"
          :reportData="reportData"
          :show.sync="printGatePassChecked"
        />
      </div>
    </div>
    <!-- #endregion -->

    <!--Attachment Window-->
    <div class="attchWin-main" v-if="openPopup">
      <button class="attchWin-close" @click="closePopup()">X</button>
      <div class="attchWin-display" v-if="attachmentList.length > 0">
        <div class="left">
          <div v-if="attachmentList.length > 1" class="leftButton">
            <button class="navButton-left" @click="navAtt('prev')">&lt;</button>
          </div>

          <!-- image container and buttons -->
          <div class="attchWin-attachment">
            <iframe
              v-if="
                attToView['AttImageType'] === '.ogg' ||
                attToView['AttImageType'] === '.pdf' ||
                attToView['AttImageType'] === 'application/pdf'
              "
              :src="attToView['AttImage']"
            />
            <img
              v-else-if="
                attToView['AttImageType'] === '.jpg' ||
                attToView['AttImageType'] === 'image/Jpeg' ||
                attToView['AttImageType'] === '.jpeg' ||
                attToView['AttImageType'] === 'image/jpeg' ||
                attToView['AttImageType'] === '.png' ||
                attToView['AttImageType'] === '.gif' ||
                attToView['AttImageType'] === 'n/a'
              "
              :src="attToView['AttImage']"
            />
            <img
              style="width: 30%"
              v-else-if="attToView['AttImageType'] === '.docx'"
              src="https://i.ibb.co/PtPtyLZ /word.png"
              @click="downloadpop()"
            />
            <img
              style="width: 30%"
              v-else-if="attToView['AttImageType'] === '.pptx'"
              src="https://i.ibb.co/n3904Sm/ppt.png"
              @click="downloadpop()"
            />
            <img
              style="width: 30%"
              v-else-if="attToView['AttImageType'] === '.msg' || attToView['AttImageType'] === '.eml'"
              src="https://i.ibb.co/sjQCXt2/mail.png"
              @click="downloadpop()"
            />
            <img
              style="width: 30%"
              v-else-if="attToView['AttImageType'] === '.xlsx'"
              src="https://i.ibb.co/JFp0hrh/excel.png"
              @click="downloadpop()"
            />
            <img style="width: 30%" v-else :src="attToView['AttImage']" />
          </div>

          <div v-if="attachmentList.length > 1" class="rightButton">
            <button class="navButton-right" @click="navAtt('next')">></button>
          </div>
        </div>

        <div class="right">
          <div class="count">
            <b>{{ attToView['#'] }}</b> / {{ totalAtt }} Attachments
          </div>
          <div class="attchWin-info">
            <table id="infoHeader">
              <tr>
                <th>Job No</th>
                <td>:{{ attToView['Job No'] }}</td>
              </tr>

              <tr>
                <th>Doc Code</th>
                <td>:{{ selectedViewDocumentList['Doc Code'] }}</td>
              </tr>

              <tr>
                <th>Attachment</th>
                <td>:{{ selectedViewDocumentList['Attachment'] }}</td>
              </tr>
            </table>

            <table id="tableContent">
              <tr>
                <th>ID</th>
                <td>{{ selectedViewDocumentList['ID'] }}</td>
              </tr>

              <tr>
                <th>Remarks</th>
                <td>{{ selectedViewDocumentList['Remarks'] }}</td>
              </tr>

              <tr>
                <th>Status</th>
                <td>{{ selectedViewDocumentList['Status'] }}</td>
              </tr>

              <tr>
                <th>Start Date</th>
                <td>{{ selectedViewDocumentList['Start Date'] }}</td>
              </tr>

              <tr>
                <th>Received Date</th>
                <td>{{ selectedViewDocumentList['Received Date'] }}</td>
              </tr>

              <tr>
                <th>Due Date</th>
                <td>{{ selectedViewDocumentList['Due Date'] }}</td>
              </tr>

              <tr>
                <th>Assign To</th>
                <td>{{ selectedViewDocumentList['Assign To'] }}</td>
              </tr>

              <tr>
                <th>Sets</th>
                <td>{{ selectedViewDocumentList['Sets'] }}</td>
              </tr>

              <tr>
                <th>Page</th>
                <td>{{ selectedViewDocumentList['Page'] }}</td>
              </tr>
            </table>
          </div>
        </div>
      </div>

      <div v-else class="attchWin-display">
        <div style="margin: 0 auto">No Attachments Found</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {},
  data: function () {
    return {
      loginDetails: '',
      bilfmtIsDisabled: false,
      confirmJobView: false,
      DNOrderView: true,
      changeFieldCaption: false,
      dischargePortMandatory: false,
      loadPortMandatory: false,
      unstuffviewmode: false,
      dischargePortMode: false,
      loadPortMode: false,
      shippingviewmode: false,
      pageType: '',
      tableLoading: false,
      jobCompletedDisabled: false,
      driverInfoDisabled: true,
      driverCodeRequired: false,

      prePopulate: false,
      preJob: '',
      preDate: '',

      createdBy: '',
      updatedBy: '',

      saveRadioButton: [
        { label: 'Confirm Job', keyValue: '1' },
        { label: 'Unconfirm Job', keyValue: '2' },
      ],

      selectedSaveBy: {
        keyValue: '1',
        label: 'Confirm Job',
      },

      menu: [
        {
          title: 'New Trucking Job',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png',
          enabled: false,
          key: 'MnuNew',
          key2: 'mnuAddJob',
        },
        {
          title: 'Edit Trucking Job',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png',
          enabled: false,
          key: 'MnuEdit',
          key2: 'mnuEditJob',
        },
        {
          title: 'Delete Trucking Job',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-reduce-50.png',
          enabled: false,
          key: 'MnuDelete',
          key2: 'mnuDeleteJob',
        },
        { title: 'View Trucking Job', icon: 'https://dsmlib.logistics.systems/Icons/icons8-view-64.png', enabled: false },
        { title: 'Print Report ...', icon: 'https://dsmlib.logistics.systems/Icons/Print.png', enabled: true },
        { title: 'Duplicate Job', icon: 'https://dsmlib.logistics.systems/Icons/DuplicateJob.png', enabled: false },
        { title: 'eDN', icon: 'https://dsmlib.logistics.systems/Icons/eDN.png', enabled: false },
        {
          title: 'Job Charges',
          icon: 'https://dsmlib.logistics.systems/Icons/money.png',
          enabled: false,
          key: 'cmdJobCharges',
        },
      ],
      currentTabIndex: 1,

      //#region Default Items
      defaultTabIndex: -1,
      defaultVolDivisor: '',
      defaultJobType: '',
      defaultBillBy: '',
      //#endregion

      //#region Job Charges
      showJobChargesPopup: false,
      jobChargesButtons: [
        // { text: 'View Job Charges', color: 'green', textColor: 'white' },
        { text: 'Cancel', color: 'lightgray', textColor: 'black' },
      ],
      //#endregion

      tabsItems: [
        { title: 'Quick Entry', disabled: false },
        // { title: 'Quick Entry', disabled: this.SASControlList.includes('cmdQuickOrderView') ? true : false },
        { title: 'Header Info', disabled: false },
        // { title: 'General Info', disabled: true },
        // { title: 'Lorry/Driver Info', disabled: false },
        { title: 'Send To / Cargo Info', disabled: false },
        { title: 'Documents', disabled: false },
        { title: 'Shipping Info', disabled: true },
        // { title: 'Delivery Note', disabled: true },
        // { title: 'ROT', disabled: true },
        // { title: 'TAS & KPI', disabled: true },
        // { title: 'SMK', disabled: true }
      ],
      statusMapping: [
        { key: 'Copy Received', mapping: 'statuscopy' },
        { key: 'Original Received', mapping: 'statusoriginal' },
        { key: 'Couriered', mapping: 'statuscouriered' },
        { key: 'Prepared', mapping: 'statusprepared' },
        { key: 'Submitted', mapping: 'statussubmitted' },
      ],

      showFilterBox: false,
      dateFilterChecked: true,
      //jobCompletedChecked: true,
      searchFilterChecked: false,
      filterText: '',
      searchFilterItems: [],
      searchCriteriaItems: [{ name: 'EQUALS' }, { name: 'STARTS WITH' }, { name: 'CONTAINS' }, { name: 'ENDS WITH' }],
      selectedCriteria: null,
      selectedSearchBy: null,
      criteriaDisabled: false,
      searchByValue: '',
      searchByError: '',
      searchByValueError: '',

      refreshStart: null,
      refreshEnd: null,
      lastRefreshed: '',

      jobsList: [],
      selectedJob: [],
      tableBoxColumnOrderViews: [],
      tableBoxColumnOrderID: null,

      popupTitle: '',
      viewMode: false,
      showPopup: false,
      showDocUpload: false,

      eMessages: [],
      sMessages: [],
      rMessages: [],
      errorMessages: {
        pickup: '',
        dropoff: '',
        billcu_no: '',
        job_type: '',
        shipperConsignee: '',
        //reqdatefrom: '', moved out of object because it will cause error message stay even if clicked
        billfmt: '',
        cu_number: '',
        delivery_order: '',
        noDriverExp: '',
        noDriverTol: '',
        noAttendantExp: '',
        noAttendantExp2: '',
        noAttendantTol2: '',
        noAttendantTol: '',
        shipcallno: '',
        vesselcode: '',
        bkgrefno: '',
      },
      jobTimeErr: '',
      driverCodeErr: '',
      reqdatefrom: '',

      showSavePopup: false,
      savePopupType: 'error',
      savePopupLayout: null,
      savePopupTitle: null,
      savePopupFooter: null,
      savePopupButtons: null,

      rotVerifyPopup: false,
      rotVerifyError: '',

      defaultPopup: {
        show: false,
        type: '',
        title: '',
        content: '',
      },

      deleteLayout: null,
      deleteAllBtn: null,
      deleteSelectedBtn: null,
      deleteMessage: '',
      deleteItems: [],

      editDeleteDoc: [],
      editAddDoc: [],

      showDocumentsLoader: false,
      editDocumentsList: [],
      documentsToDelete: [],

      milestoneHeight: 0,
      milestonesItem: null,
      showMilestones: true,

      showTooltip: false,
      tooltipText: '',
      tooltipPos: 'top',

      saveAlertType: '', // required | option
      saveErrorList: [],

      popupError: false,
      popupErrorText: '',
      reasonsList: [],

      selectedUnconfirmReason: null,
      unconfirmRemarks: '',

      editDetails: null,
      detailsEditTitle: '',

      //for showing error message?
      popupShow: false,
      requiredError: [],
      successMessages: [],

      addressDetails: {
        postcodeList: [],
        stateList: [],
        countryList: [],
      },
      dnListing: [],
      rotListing: [],

      // Status task tab
      statusTaskData: {
        statusTaskListing: [],
        statusDocumentListing: [],

        statusJobNo: '',
      },

      smkListing: [],

      //#region Print Popup
      printPopup: false,
      printPopupButtons: [{ text: 'Print' }, { text: 'Cancel', color: 'lightgray', textColor: 'black' }],
      printDOChecked: true,
      printGatePassChecked: false,

      showDOReport: false,
      showReportPopup: false,
      showGatePassReport: false,
      reportData: null,

      doReportFile: [`DO_DSM.json`],
      gatePassReportFile: [`GatePass_DSM.json`],
      //#endregion

      reportNoContainer: false,
      reportIsCancelled: false,
      reportCargoTypeF: false,

      userParam: '',

      billTypeSend: null,

      //#region Header info data and details
      selectedShipperConsignee: null,
      headerInfoData: {
        shipper: [],
        shipperConsignee: [],
        consignee: [],
        vesselIDList: [],
        billingfmt: [],
        billBy: [],
        billTo: [],
        jobType: [],

        pickup: [],

        transit1: [],
        tariffArea: [],
        zones: [],

        transit2: [],
        dropoff: [],
        services: [],
        operationTeam: [],

        cargo: [],
        agent: [],
        fwdgAgent: [],
        localCarrier: [],
        foreignFwdg: [],
        foreignCarrier: [],

        cargoPackage: [],
        cargoTruckType: [],

        //#region cargo tab -Job Total
        cargoContainerNo: [],
        cargoQuantity: [],
        cargoPallet: [],
        //#endregion
      },
      selectedJobType: null,
      selectedHeaderInfoData: {
        selectedCargoEstTrucks: '',
        selectedShipper: null,
        // selectedVesselCode: null,
        selectedBillingFmt: null,
        selectedBillBy: null,
        selectedRequestBy: null,

        DOno: null,
        selectedBillto: null,
        ReqTimeDesc: null,

        selectedPickUp: null,
        tariffAreaPickup: null,
        zonesPickup: null,
        pickUpAddress: null,
        transit1Address: null,
        transit2Address: null,

        selectedTransit1: null,
        tariffAreaTransit1: null,
        zonesTransit1: null,

        selectedTransit2: null,
        tariffAreaTransit2: null,
        zonesTransit2: null,

        selectedDropOff: null,
        tariffAreaDropoff: null,
        zonesDropoff: null,
        dropOffAddress: null,

        jobNo: null,
        jobTime: null,
        jobStatus: null,

        selectedCargo: null,
        reqJobTime: null,
        reqJobTimeTo: null,
        isOfficeHours: false,
        selectedAgent: null,

        Projno: null,
        custRefNo: null,
        billToDetails: null,

        selectedFwdgAgent: null,
        selectedLocalCarrier: null,
        selectedForeignFwdg: null,
        selectedForeignCarrier: null,

        selectedOperationTeam: null,
        salesTeam: null,
        salesPerson: null,
        serviceTeam: null,
        servicePerson: null,

        selectedConsignee: null,

        jobState: null,
        // tableDataType: null,
        //#region cargo tab - Job total
        selectedCargoContainerNo: null,
        selectedCargoQuantity: null,
        selectedCargoPallet: null,
        selectedCargoPackage: null,
        selectedCargoTruckType: null,

        //#endregion
      },

      //#endregion

      //#region General info data and details
      generalInfoData: {
        freightTypeList: [],
        countryList: [],
        currencyList: [],
        incoTermsList: [],
        insuranceList: [],
        branchList: [],
        servicePICList: [],
        serviceTeamList: [],
        salesPICList: [],
        salesTeamList: [],
      },
      selectedGeneralInfoData: {
        vendor: null,
        vesselBkgBy: null,
        oriBL: null,
        nonNegoBL: null,
        selectedFreightType: null,
        selectedCountry: null,
        selectedCurrency: null,
        selectedIncoTerms: null,
        selectedInsurance: null,
        houseBL: null,
        selectedBranch: null,

        selectedServicePIC: null,
        selectedServiceTeam: null,
        selectedSalesPIC: null,
        selectedSalesTeam: null,
        remarks: null,
      },
      //#endregion

      //#region Shipping info data and details
      shippingInfoData: {
        shipCallNoList: [],
        vesselIDList: [],
        shippingAgentList: [],
        shippingLineList: [],
        frtForwarderList: [],
        specialTreatment: [],
        station: [],
        portOperator: [],

        portOfLoading: [],
        portOfDischarge: [],
        finalDestination: [],

        fumigatorList: [],
        fumigatorLocationList: [],
      },
      selectedShippingInfoData: {
        selectedShipCall: null,
        shipCallNoValue: '',
        voyNo: null,
        selectedVesselCode: null,
        selectedAgentList: null,
        selectedShippingLine: null,
        selectedFrtForwarder: null,
        selectedSpecialTreatment: null,
        selectedStation: null,
        selectedPortOperator: null,
        warehouse: null,

        selectedPortLoading: null,
        selectedPortDischarge: null,
        selectedFinalDestination: null,
        demurageTime: null,
        detention: null,
        isTranshipment: null,
        selectedMVesselID: null,
        mVoyNo: null,
        selectedPortDischarge2: null,
        ETDTime: null,

        closingTime: null,
        polETD: null,
        polETA: null,
        fDestETA: null,
        isFumigation: null,
        selectedFumigator: null,
        selectedLocation: null,
        fumigateTime: null,
        ventilationTime: null,
        phytoMarking: null,

        goodsDescription: '',
        shippingMarks: '',
        unstuffingDateTime: null,
        selectedBookingRefNo: '',
      },
      //#endregion

      //#region Driver data and details
      LorryInfoTabData: {
        lorryNoList: [],
        driverCodeList: [],
        driverNameList: [],
        driverICList: [],
      },
      selectedLorryInfoData: {
        selectedLorryNoList: null,
        selectedDriverCodeList: null,
        // selectedDriverNameList: null,
        // selectedDriverICList: null,
        isCompleted: null,

        driverEXP: '',
        driverTOL: '',
        attendantEXP: '',
        attendantTOL: '',
        attendantEXP2: '',
        attendantTOL2: '',
      },
      //#endregion

      //#region Cargo info data and details
      cargoInfoData: {
        cargoProductList: [],
        cargoPackageList: [],
        cargoTruckTypeList: [],
        cargoBillTypeList: [],
        cargoBillCodeList: [],
        cargoContNoList: [],
        cargoContSizeList: [],
        cargoContTypeList: [],
        cargoVolumeDivisorList: [],
        // truckQtyList: [],
        // commodityList: [],
        // pMaterialList: [],
        // pkgTypeList: []
      },
      cargoInfoList: [],
      selectedCargoInfoData: {
        product: null,
        package: null,
        truckType: null,
        billType: null,
        driverEXP: '0',
        driverTOL: '0',

        attendantEXP: '0',
        attendantTOL: '0',
        attendantEXP2: '0',
        attendantTOL2: '0',

        cargoToll: '0',
        cargoTransport: '0',
        cargoTotal: '0',

        deletedCargoInfo: [],
        selectedCargoInfo: [],

        cndeleteremark: '',

        dim: null,

        // gWeight: null,
        // volume: null,
        // chargeable: null,

        // selectedCommodity: null,
        // selectedPMaterial: null,
        // packageCount: null,
        // selectedPackageType: null,

        subPkgList: [],
        selectedSubPkgs: null,
        contDetailsList: [],
        selectedContDetails: null,
        deletedContDetails: null,
      },
      cargoPopupDetails: {
        cargoAddType: '',
        isEdit: false,
        editIndex: -1,
        editDetails: null,
      },
      truckVolumeDivisor: '5000', //same as JobPageCargoInfoPopup.vue
      //#endregion

      //#region Document info data and details
      documentInfoData: {
        updatePopup: false,
        statusList: [],
        docTypeList: [],
        docStatusList: [],
      },
      selectedDocumentInfoData: {
        docData: [],
        selectedDoc: [],
      },
      viewDocumentButtons: [
        { text: 'View', index: 0, width: 60 },
        //{ text: '<img src="https://i.ibb.co/0QC3YFs/icons8-attach-50.png" height="20" width="20">', index: 1 },
      ],
      attachmentList: [],
      openPopup: false,
      attToView: [],
      noAtt: false,
      totalAtt: '',
      selectedViewDocumentList: '',
      //#endregion

      //#region Delete Popup
      showDeletePopup: false,
      showConsignmentDeletePopup: false,
      deletePopupButtons: [
        { text: 'Delete', color: 'red', textColor: 'white' },
        { text: 'Cancel', color: 'lightgray', textColor: 'black' },
      ],
      deleteRemarks: '',
      deleteRemarksError: '',
      //#endregion

      consignmentToDelete: [],
      tableEditButton: [{ text: 'Edit', index: 0, width: 60 }],

      reasonOpen: false,

      //#region EDN Section
      showEDN: false,
      filterOpen: false,
      eDNLastRefreshed: '',

      ednListing: [],
      selectedeDN: [],

      allDNChecked: true,
      rangeDNChceked: false,
      dnRangeFrom: null,
      dnRangeTo: null,
      dnRangeFromError: '',
      dnRangeToError: '',

      ednJsonDetails: null,

      showDNReject: false,
      eDNRejectButtons: [
        { text: 'Reject', color: 'red', textColor: 'white' },
        { text: 'Cancel', color: 'lightgray', textColor: 'black' },
      ],

      showCC: false,
      showBCC: false,
      rejectToEmails: [],
      rejectCCEmails: [],
      rejectBCCEmails: [],
      eDNRejectMessage: '',
      eDNRejectReason: '',

      showVerification: false,
      eDNSections: [],
      eDNAttachments: [],
      eDNRequestNo: '',
      //#endregion
      SASControlList: [],
      smallDateTimeMinValue: new Date(2000, 0, 1),
      smallDateTimeMaxDate: new Date(2079, 0, 1),

      // For vessel schedule sync
      hub: null,
      shipCallNoLoading: false,
    };
  },
  props: {},
  computed: {
    linkDisabledClass: function () {
      if (this.reportIsCancelled) {
        return 'report-disabled-cancelled';
      } else if (this.reportNoContainer) {
        return 'report-disabled-container';
      } else if (this.reportCargoTypeF) {
        return 'report-disabled-cargo-type';
      }
      return '';
    },
    linkDisabled: function () {
      if (this.reportIsCancelled || this.reportNoContainer || this.reportCargoTypeF) {
        return true;
      }
      return false;
    },
    saveAsConfirm: function () {
      return this.selectedSaveBy?.keyValue === '1';
    },
    shipCallNoSearch: {
      get() {
        return this.selectedShippingInfoData['shipCallNoValue'] ?? '';
      },
      set(val) {
        this.selectedShippingInfoData['shipCallNoValue'] = val;
      },
    },
  },
  methods: {
    menuClicked: async function (item) {
      this.viewMode = false;
      this.jobCompletedDisabled = false;
      this.driverInfoDisabled = true;
      this.driverCodeRequired = false;
      if (!item.enabled) {
        this.$showAlert('error', 'Access Denied', `You have no permission for '${item['title']}'`);
        return;
      }

      switch (item['title']) {
        case 'New Trucking Job': {
          this.popupTitle = item['title'];
          this.selectedHeaderInfoData['jobNo'] = '**********';
          this.selectedHeaderInfoData['jobStatus'] = 'New';
          this.selectedHeaderInfoData['jobTime'] = new Date();
          this.selectedHeaderInfoData['reqJobTime'] = new Date(
            new Date().getFullYear(),
            new Date().getMonth(),
            new Date().getDate(),
            0,
            0,
          );
          const userBranch = this.loginDetails['Branch']['BrName'];
          this.selectedGeneralInfoData['selectedBranch'] = this.generalInfoData['branchList'].filter((b) => {
            return b['Branch Name'].trim() == userBranch.trim();
          })[0];
          this.popupTitle = `New Job`;

          this.milestoneHeight = 0;
          this.showPopup = true;
          break;
        }
        case 'Edit Trucking Job':
          if (this.selectedJob.length == 0) {
            this.$showAlert('error', 'No Job Selected', `Please select a record to edit`);
          } else {
            if (this.selectedJob[0]['Job State'] == 'Cancelled') {
              this.$showAlert('error', 'Cancelled Job', `Cancelled Job cannot be editted`);
              return;
            }
            this.showPopup = true;
            this.popupTitle = `Edit Job`;

            this.viewEditClicked('e');
          }
          break;
        case 'Delete Trucking Job':
          if (this.selectedJob.length == 0) {
            await this.$showAlert('error', 'No Job Selected', `Please select a job to delete`);
          } else if (this.selectedJob[0]['Job State'] == 'Cancelled') {
            this.$showAlert('error', 'Job is Cancelled', `You cannot delete a job that is already cancelled.`);
          } else {
            this.showDeletePopup = true;
          }
          break;
        case 'View Trucking Job':
          if (this.selectedJob.length == 0) {
            this.$showAlert('error', 'No Job Selected', `Please select a record to view`);
          } else {
            this.viewMode = true;
            this.jobCompletedDisabled = true;
            this.driverInfoDisabled = true;
            this.driverCodeRequired = false;
            this.showPopup = true;
            this.popupTitle = `View Job`;

            this.viewEditClicked('v');
          }
          break;
        case 'Print Report ...':
          this.showReportPopup = false;
          if (this.selectedJob.length == 0) {
            this.$showAlert('error', 'No Job Selected', `Please select a record to print`);
          } else {
            this.$fullscreenLoading('Getting Job Details');
            const getJobDetails = this.$jsonNullToStringV2(
              this.$deepTrimJsonV3(
                await this.$apiCallV2(`/TruckingJob/JobDetailsTMS?jobno=${this.selectedJob[0]['Job No'].trim()}`),
              ),
            );
            this.reportData = getJobDetails['JobDetails']['Job'];
            this.$fullscreenLoading();

            this.printDOChecked = true;
            this.printGatePassChecked = false;
            this.printPopup = true;
          }
          break;
        case 'Scan Document':
          this.showDocUpload = true;
          this.popupTitle = 'Document AI';
          break;
        case 'Duplicate Job':
          if (this.selectedJob.length == 0) {
            this.$showAlert('error', 'No Job Selected', `Please select a record to duplicate`);
          } else {
            if (this.selectedJob[0]['Job State'] == 'Cancelled') {
              this.$showAlert('error', 'Cancelled Job', `Cancelled Job cannot be duplicated`);
              return;
            }

            // this.showPopupFrom = true;
            await this.viewEditClicked('c');

            this.showPopup = true;
            this.selectedHeaderInfoData['jobNo'] = '**********';
            this.selectedHeaderInfoData['jobStatus'] = 'New';
            this.selectedHeaderInfoData['jobTime'] = new Date(
              new Date().getFullYear(),
              new Date().getMonth(),
              new Date().getDate(),
              new Date().getHours(),
              new Date().getMinutes(),
              0,
              0,
              0,
            );
            this.cargoInfoList = [];
            const userBranch = this.loginDetails['Branch']['BrName'];
            this.selectedGeneralInfoData['selectedBranch'] = this.generalInfoData['branchList'].filter((b) => {
              return b['Branch Name'].trim() == userBranch.trim();
            })[0];
            this.popupTitle = 'Duplicate Job ';
          }
          break;
        case 'eDN':
          this.showEDN = true;
          await this.refreshDNClicked();
          break;
        case 'Job Charges':
          if (this.selectedJob.length == 0) {
            this.$showAlert('error', 'No Job Selected', 'Please select a record to view Job Charges');
          } else {
            const route = this.$router.resolve(
              `/Home/${this.$route.params['user']}/FINAR/Job%20Charges?jobNo=${this.selectedJob[0]['Job No'].trim()}`,
            );
            window.open(route.href, '_blank');
          }
          break;
      }
    },
    refreshClicked: async function () {
      this.jobsList = [];
      this.searchByError = '';
      this.searchByValueError = '';
      this.tableBoxColumnOrderViews = [];
      this.tableBoxColumnOrderID = null;

      if (this.searchFilterChecked && (!this.selectedSearchBy || !this.searchByValue)) {
        this.searchByError = !this.selectedSearchBy ? 'Please select your search criteria' : '';
        this.searchByValueError = !this.searchByValue ? 'Please enter your search value' : '';

        return;
      }

      const dataToPost = {
        stjobdate: this.dateFilterChecked ? this.refreshStart.formatString('yyyy-MM-dd') : '',
        edjobdate: this.dateFilterChecked ? this.refreshEnd.formatString('yyyy-MM-dd') : '',
        searchfield: this.searchFilterChecked ? this.selectedSearchBy['Search Criteria'] : '',
        searchcriteria: this.searchFilterChecked ? this.selectedCriteria['name'] : '',
        searchvalue: this.searchFilterChecked ? this.searchByValue : '',
      };

      if (this.dateFilterChecked && this.searchFilterChecked) {
        this.filterText = `Filter by ${this.selectedSearchBy['Search Criteria']} ${this.selectedCriteria['name']} ${this.searchByValue}, from ${this.refreshStart.formatString('yyyy-MM-dd')} to ${this.refreshEnd.formatString('yyyy-MM-dd')}`;
      } else if (this.dateFilterChecked && !this.searchFilterChecked) {
        this.filterText = `Filter by date from ${this.refreshStart.formatString('yyyy-MM-dd')} to ${this.refreshEnd.formatString('yyyy-MM-dd')}`;
      } else if (!this.dateFilterChecked && this.searchFilterChecked) {
        this.filterText = `Filter by ${this.selectedSearchBy['Search Criteria']} ${this.selectedCriteria['name']} ${this.searchByValue}`;
      }
      this.showFilterBox = false;

      const columnOrderID = {
        formname: 'Trucking_TruckingJob',
        formctrlname: 'TruckingJob',
      };

      this.$fullscreenLoading('Getting Job Data');
      this.tableLoading = true;

      const jobData = await this.$apiCallV2(`/TruckingJob/GetListJobTrackingTMS`, dataToPost);
      const columnOrder = await this.$apiCallV2('/TableBox/GetColumnOrder', columnOrderID);

      this.jobsList = jobData;
      this.tableBoxColumnOrderViews = columnOrder;
      this.tableBoxColumnOrderID = columnOrderID;

      if (this.prePopulate) {
        const preJobData = this.jobsList.filter((j) => j['Job No'].trim() == this.preJob.trim());
        this.selectedJob = preJobData;
      }
      this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm');

      this.tableLoading = false;
      this.$fullscreenLoading();
    },
    checkRequiredFields: function () {
      this.rMessages = [];
      let error = false;
      //#region for Header info checking
      if (!this.selectedHeaderInfoData['selectedPickUp']) {
        this.rMessages.push({ Key: 'pickup', Value: 'Pick Up is Required!' });
        this.errorMessages['pickup'] = 'Pick Up is Required!';
        error = true;
      }
      if (!this.selectedHeaderInfoData['selectedDropOff']) {
        this.rMessages.push({ Key: 'dropoff', Value: 'Drop Off is Required!' });
        this.errorMessages['dropoff'] = 'Drop Off is Required!';
        error = true;
      }
      if (!this.selectedHeaderInfoData['selectedBillTo']) {
        this.rMessages.push({ Key: 'billcu_no', Value: 'Bill To is Required!' });
        this.errorMessages['billcu_no'] = 'Bill To is Required!';
        error = true;
      }
      if (!this.selectedJobType) {
        this.rMessages.push({ Key: 'job_type', Value: 'Job Type is Required!' });
        this.errorMessages['job_type'] = 'Job Type is Required!';
        error = true;
      }
      if (!this.selectedShipperConsignee) {
        this.rMessages.push({ Key: 'shipperConsignee', Value: 'Shipper/Consignee is Required!' });
        this.errorMessages['shipperConsignee'] = 'Shipper/Consignee is Required!';
        error = true;
      }
      if (!this.selectedHeaderInfoData['DOno']) {
        this.rMessages.push({ Key: 'delivery_order', Value: 'D/O # is Required!' });
        this.errorMessages['delivery_order'] = 'D/O # is Required!';
        error = true;
      }
      if (!this.selectedHeaderInfoData['reqJobTime']) {
        this.rMessages.push({ Key: 'reqdatefrom', Value: 'Date/Time is Required!' });
        this.reqdatefrom = 'Date/Time is Required!';
        error = true;
      }
      if (!this.selectedHeaderInfoData['jobTime']) {
        this.rMessages.push({ Key: 'job_time', Value: 'Job Time is Required!' });
        this.jobTimeErr = 'Job Time is Required!';
        error = true;
      }
      if (!this.selectedHeaderInfoData['selectedBillingFmt'] && !this.bilfmtIsDisabled) {
        this.rMessages.push({ Key: 'billfmt', Value: 'Billing Fmt is Required!' });
        this.errorMessages['billfmt'] = 'Billing Fmt is Required!';
        error = true;
      }
      if (this.cargoInfoList.length == 0) {
        this.rMessages.push({ Key: 'consignments', Value: 'Cargo Info is Required!' });
        this.errorMessages['consignments'] = 'Cargo Info is Required!';
        error = true;
      }
      if (!this.tabsItems[4]['disabled']) {
        const jobType = this.selectedJobType ? this.selectedJobType['Job Type'] : null;
        if ((jobType == 'I' || jobType == 'E') && !this.shipCallNoSearch) {
          this.rMessages.push({ Key: 'shipcallno', Value: 'Ship Call # is Required!' });
          this.errorMessages['shipcallno'] = 'Ship Call # is Required!';
          error = true;
        }
        if ((jobType == 'I' || jobType == 'E') && !this.selectedShippingInfoData['selectedVesselCode']) {
          this.rMessages.push({ Key: 'vesselcode', Value: 'Vessel Code is Required!' });
          this.errorMessages['vesselcode'] = 'Vessel Code is Required!';
          error = true;
        }
        if (jobType == 'E' && !this.selectedShippingInfoData['selectedBookingRefNo']) {
          this.rMessages.push({ Key: 'bkgrefno', Value: 'Booking Ref # is Required!' });
          this.errorMessages['bkgrefno'] = 'Booking Ref # is Required!';
          error = true;
        }
      }
      if (this.selectedLorryInfoData['selectedLorryNoList'] && !this.selectedLorryInfoData['selectedDriverCodeList']) {
        this.rMessages.push({ Key: 'drivercode', Value: 'Driver Code is Required!' });
        this.driverCodeErr = 'Driver Code is Required!';
        error = true;
      }
      //#endregion
      return error;
    },
    searchBySelected: function (e) {
      if (e) {
        if (e['Search Criteria'] == 'Required Date') {
          this.selectedCriteria = this.searchCriteriaItems[0];
          this.criteriaDisabled = true;
        } else {
          this.criteriaDisabled = false;
        }
      }
    },
    viewEditClicked: async function (type, jobno) {
      this.$fullscreenLoading('Getting Job Details');

      this.jobTimeErr = '';
      this.driverCodeErr = '';
      this.reqdatefrom = '';

      this.popupTitle += jobno ? ` ${jobno}` : ` ${this.selectedJob[0]['Job No'].trim()}`;
      const getJobDetails = this.$jsonNullToStringV2(
        this.$deepTrimJsonV3(
          await this.$apiCallV2(
            `/TruckingJob/JobDetailsTMS?jobno=${jobno ? jobno : this.selectedJob[0]['Job No'].trim()}`,
          ),
        ),
      );

      const details = getJobDetails['JobDetails']['Job'];

      this.editDetails = details;
      this.createdBy = `${this.editDetails['Created By'] ? this.editDetails['Created By'].trim() : ''} ${this.editDetails['Created Date'] ? new Date(this.editDetails['Created Date']).formatString('dd/MM/yyyy HH:mm') : ''}`;
      this.updatedBy = `${this.editDetails['Updated By'] ? this.editDetails['Updated By'].trim() : ''} ${this.editDetails['Last Update'] ? new Date(this.editDetails['Last Update']).formatString('dd/MM/yyyy HH:mm') : ''}`;

      if (type != 'c') {
        this.selectedHeaderInfoData['jobNo'] = '';
        this.selectedHeaderInfoData['jobStatus'] = 'New';
        this.selectedHeaderInfoData['jobTime'] = new Date(
          new Date().getFullYear(),
          new Date().getMonth(),
          new Date().getDate(),
          new Date().getHours(),
          new Date().getMinutes(),
          0,
          0,
          0,
        );
      }

      //#region Header Info
      // this.headerInfoData['services'] = this.header
      this.selectedHeaderInfoData['jobNo'] = details['Job No'] ? details['Job No'].trim() : '';
      this.selectedHeaderInfoData['job_date'] = details['Job Time'] ? new Date(details['Job Time']) : null;
      this.selectedHeaderInfoData['jobTime'] = details['Job Time'] ? new Date(details['Job Time']) : null;
      this.selectedShipperConsignee =
        details['Customer'] ?
          this.headerInfoData['shipperConsignee'].filter((t) => {
            return t['Customer Code'].trim() == details['Customer'].trim();
          })[0]
        : null;
      this.selectedHeaderInfoData['selectedShipper'] =
        details['Customer'] ?
          this.headerInfoData['shipper'].filter((t) => {
            return t['Customer Code'].trim() == details['Customer'].trim();
          })[0]
        : null;
      // this.selectedHeaderInfoData['selectedVesselCode'] =
      //   details['Vessel Code'] ?
      //     this.headerInfoData['vesselIDList'].filter((t) => {
      //       return t['Vessel ID'].trim() == details['Vessel Code'];
      //     })[0]
      //   : null;
      this.selectedHeaderInfoData['selectedBillingFmt'] =
        details['BillingFmt'] ?
          this.headerInfoData['billingfmt'].filter((t) => {
            return t['Billing Fmt'].trim() == details['BillingFmt'].trim();
          })[0]
        : null;
      this.selectedHeaderInfoData['reqJobTime'] =
        details['Required Date From'] ? new Date(details['Required Date From']) : null;
      this.selectedHeaderInfoData['reqJobTimeTo'] =
        details['Required Date To'] ? new Date(details['Required Date To']) : null;
      this.selectedHeaderInfoData['selectedRequestBy'] = details['Requested By'] ? details['Requested By'].trim() : '';
      this.selectedHeaderInfoData['DOno'] = details['D / O No'] ? details['D / O No'].trim() : '';
      this.selectedHeaderInfoData['selectedBillTo'] =
        details['Bill To'] ?
          this.headerInfoData['billTo'].filter((t) => {
            return t['Customer Code'].trim() == details['Bill To'].trim();
          })[0]
        : null;
      this.selectedJobType =
        details['Job Type'] ?
          this.headerInfoData['jobType'].filter((t) => {
            return t['Job Type'].trim() == details['Job Type'].trim();
          })[0]
        : null;
      this.selectedHeaderInfoData['ReqTimeDesc'] = details['Req Time Desc'] ? details['Req Time Desc'].trim() : '';
      this.selectedGeneralInfoData['remarks'] = details['Remarks'] ? details['Remarks'].trim() : '';
      if (this.selectedHeaderInfoData['selectedBillTo']) {
        this.billToSelected(this.selectedHeaderInfoData['selectedBillTo']);
      }

      this.selectedHeaderInfoData['selectedPickUp'] =
        details['Pick Up'] ?
          this.headerInfoData['pickup'].filter((t) => {
            return t['Destination Code'].trim() == details['Pick Up'].trim();
          })[0]
        : null;
      if (this.selectedHeaderInfoData['selectedPickUp']) {
        this.$nextTick(() => this.pickUpSelected(this.selectedHeaderInfoData['selectedPickUp']));
      }
      this.selectedHeaderInfoData['selectedDropOff'] =
        details['Drop Off'] ?
          this.headerInfoData['dropoff'].filter((t) => {
            return t['Destination Code'].trim() == details['Drop Off'].trim();
          })[0]
        : null;
      if (this.selectedHeaderInfoData['selectedDropOff']) {
        this.$nextTick(() => this.dropOffSelected(this.selectedHeaderInfoData['selectedDropOff']));
      }
      this.selectedHeaderInfoData['selectedTransit1'] =
        details['Transit 1'] ?
          this.headerInfoData['transit1'].filter((t) => {
            return t['Destination Code'].trim() == details['Transit 1'].trim();
          })[0]
        : null;
      if (this.selectedHeaderInfoData['selectedTransit1']) {
        this.$nextTick(() => this.transit1Selected(this.selectedHeaderInfoData['selectedTransit1']));
      }
      this.selectedHeaderInfoData['selectedTransit2'] =
        details['Transit 2'] ?
          this.headerInfoData['transit2'].filter((t) => {
            return t['Destination Code'].trim() == details['Transit 2'].trim();
          })[0]
        : null;
      if (this.selectedHeaderInfoData['selectedTransit2']) {
        this.$nextTick(() => this.transit2Selected(this.selectedHeaderInfoData['selectedTransit2']));
      }
      this.selectedHeaderInfoData['selectedCargo'] =
        details['Cargo Type'] ?
          this.headerInfoData['cargo'].filter((t) => {
            return t['Cargo Type'].trim() == details['Cargo Type'].trim();
          })[0]
        : null;

      this.selectedHeaderInfoData['selectedBillBy'] =
        details['Bill By'] ?
          this.headerInfoData['billBy'].filter((t) => {
            return t['BillingBy'].trim() == details['Bill By'].trim();
          })[0]
        : null;
      this.selectedHeaderInfoData['selectedOperationTeam'] =
        details['Operator Team'] ?
          this.headerInfoData['operationTeam'].filter((t) => {
            return t['Team'].trim() == details['Operator Team'].trim();
          })[0]
        : null;

      this.selectedHeaderInfoData['reqJobTime'] =
        details['Required Date From'] ? new Date(details['Required Date From']) : null;
      this.selectedHeaderInfoData['selectedConsignee'] =
        details['Consignee'] ?
          this.headerInfoData['consignee'].filter((t) => {
            return t['Bill To Code'].trim() == details['Consignee'].trim();
          })[0]
        : null;
      this.selectedHeaderInfoData['Projno'] = details['Project No'] ? details['Project No'].trim() : '';
      this.selectedHeaderInfoData['CustRefno'] = details['Cust Ref No'] ? details['Cust Ref No'].trim() : '';

      this.selectedHeaderInfoData['selectedCargoPallet'] =
        details['Total Packages'] ?
          details['Total Packages'].trim() === '0.00' ?
            ''
          : parseInt(details['Total Packages'].trim()).toString()
        : '';
      this.selectedHeaderInfoData['selectedCargoEstTrucks'] =
        details['Est Vehicle Needed'] ? details['Est Vehicle Needed'].trim() : '';

      if (details['Services']) {
        // API returns an object if one service is selected.
        // API returns an array of objects if more than one services is selected
        const servicesArray = Array.isArray(details['Services']) ? details['Services'] : [details['Services']];

        servicesArray.forEach((incomingService) => {
          const extractedService = {
            selected: true,
            item: incomingService['Description'],
            remarks: incomingService['Remarks'],
            data: { Status: incomingService['Status'] },
          };
          this.headerInfoData['services'].forEach((service, index) => {
            if (incomingService.Description === service.item) {
              this.headerInfoData['services'][index] = extractedService;
            }
          });
        });
      }

      const allServices = this.headerInfoData['services'];
      allServices.forEach((s) => {
        s['originalSelected'] = s['selected']; // Set 'originalSelected' to the initial 'selected' state
      });

      // #endregion

      // #region for shipping info tab
      this.selectedShippingInfoData['selectedShipCall'] =
        details['Ship Call No'] ?
          this.shippingInfoData['shipCallNoList'].getSelected(details['Ship Call No'], 'Ship Call No')
        : null;
      if (details['Ship Call No'] && !this.selectedShippingInfoData['selectedShipCall']) {
        this.selectedShippingInfoData['shipCallNoValue'] = details['Ship Call No'];
      }
      this.selectedShippingInfoData['selectedVesselCode'] =
        details['Vessel Code'] ?
          this.shippingInfoData['vesselCodeList'].filter((t) => {
            return t['Vessel ID'].trim() == details['Vessel Code'].trim();
          })[0]
        : null;

      // this.selectedShippingInfoData['shippingMarks'] = details[''] ? details[''].trim() : '';
      // console.log(this.selectedShippingInfoData['shippingMarks'])

      this.selectedShippingInfoData['voyNo'] = details['Voy No'] ? details['Voy No'].trim() : '';
      this.selectedShippingInfoData['selectedFinalDestination'] =
        details['Final Dest'] ? details['Final Dest'].trim() : '';
      this.selectedShippingInfoData['selectedBookingRefNo'] =
        details['Booking Ref No'] ? details['Booking Ref No'].trim() : '';
      //this.selectedShippingInfoData['goodsDescription'] = details['goodsdescrpt'] ? details['goodsdescrpt'].trim() : '';
      this.selectedShippingInfoData['goodsDescription'] =
        details['GoodsDesc'] ? details['GoodsDesc'].map((g) => g['GoodsDescription']).join('\n') : '';
      this.selectedShippingInfoData['shippingMarks'] =
        details['ShipMarks'] ? details['ShipMarks'].map((g) => g['ShipMark']).join('\n') : '';

      this.selectedShippingInfoData['selectedAgentList'] =
        details['Shipping Agt'] ?
          this.shippingInfoData['shippingAgentList'].filter((t) => {
            return t['Agent Code'].trim() == details['Shipping Agt'].trim();
          })[0]
        : null;
      this.selectedShippingInfoData['selectedShippingLine'] = details['Ship Line'] ? details['Ship Line'].trim() : '';
      this.selectedShippingInfoData['selectedAgent'] =
        details['Agent'] ?
          this.shippingInfoData['agentList'].filter((t) => {
            return t['Agent Code'].trim() == details['Agent'].trim();
          })[0]
        : null;
      this.selectedShippingInfoData['selectedForwardingAgent'] =
        details['Fwdg Agent'] ?
          this.shippingInfoData['forwardingAgent'].filter((t) => {
            return t['Agent Code'].trim() == details['Fwdg Agent'].trim();
          })[0]
        : null;
      this.selectedShippingInfoData['selectedTranshipmentPort'] =
        details['Transhipment Port'] ?
          this.shippingInfoData['transhipmentPort'].filter((t) => {
            return t['Port Code'].trim() == details['Transhipment Port'].trim();
          })[0]
        : null;
      this.selectedShippingInfoData['selectedDischargeLoadPort'] =
        details['Load / Discharge Port'] ?
          this.shippingInfoData['dischargeLoadPort'].filter((t) => {
            return t['Port Code'].trim() == details['Load / Discharge Port'].trim();
          })[0]
        : null;

      this.selectedShippingInfoData['selectedLocalCarrier'] =
        details['Local Carrier'] ?
          this.shippingInfoData['localCarrier'].filter((t) => {
            return t['Agent Code'].trim() == details['Local Carrier'].trim();
          })[0]
        : null;
      this.selectedShippingInfoData['selectedForeignForwardingAgent'] =
        details['Foreign Fwdg'] ?
          this.shippingInfoData['foreignForwardingAgent'].filter((t) => {
            return t['Agent Code'].trim() == details['Foreign Fwdg'].trim();
          })[0]
        : null;
      this.selectedShippingInfoData['selectedForeignCarrier'] =
        details['Foreign Carrier'] ?
          this.shippingInfoData['foreignCarrier'].filter((t) => {
            return t['Agent Code'].trim() == details['Foreign Carrier'].trim();
          })[0]
        : null;

      this.selectedShippingInfoData['ETADateTime'] = details['Date ETA'] ? new Date(details['Date ETA']) : null;
      this.selectedShippingInfoData['ETDDateTime'] = details['Date ETD'] ? new Date(details['Date ETD']) : null;
      this.selectedShippingInfoData['closeDateTime'] =
        details['Closing Time'] ? new Date(details['Closing Time']) : null;
      this.selectedShippingInfoData['freeStorageUntil'] =
        details['Unstuffing Date'] ? new Date(details['Unstuffing Date']) : null;
      this.selectedShippingInfoData['unstuffingDateTime'] =
        details['Free Storage'] ? new Date(details['Free Storage']) : null;

      // #endregion

      // #region lorry/driver info
      this.selectedLorryInfoData['selectedLorryNoList'] =
        details['Lorry No'] ?
          this.LorryInfoTabData['lorryNoList'].filter((t) => {
            return t['Lorry No'].trim() == details['Lorry No'].trim();
          })[0]
        : null;
      this.selectedLorryInfoData['selectedDriverCodeList'] =
        details['Driver Code'] ?
          this.LorryInfoTabData['driverCodeList'].filter((t) => {
            return t['Driver Code'].trim() == details['Driver Code'].trim();
          })[0]
        : null;
      this.selectedLorryInfoData['isJobCompleted'] = details['isCompleted'] == '1';

      // #region Send To/ Cargo Info - Job Total
      this.selectedHeaderInfoData['selectedCargoPackage'] =
        details['Package Code'] ?
          this.cargoInfoData['cargoPackageList'].find(
            (t) => t['Package Code'].trim() === details['Package Code'].trim(),
          )
        : null;
      this.selectedHeaderInfoData['selectedCargoTruckType'] =
        details['Vehicle Category'] ?
          this.cargoInfoData['cargoTruckTypeList'].find(
            (t) => t['Truck Type'].trim() === details['Vehicle Category'].trim(),
          )
        : null;

      //get job documents from Another API instead of the JobDetails API
      this.showDocumentsLoader = true;
      await this.$apiCallV2(
        `/TruckingJob/GetJobDocuments?jobno=${jobno ? jobno : this.selectedJob[0]['Job No'].trim()}`,
      ).then((results) => {
        const jobDocs = this.$jsonNullToStringV2(this.$deepTrimJsonV3(results));
        //Documents tab
        jobDocs.forEach((d, index) => {
          const due = d['Due DateTime'] ? new Date(d['Due DateTime']).formatString('yyyy-MM-dd HH:mm') : '';
          const received =
            d['Received DateTime'] ? new Date(d['Received DateTime']).formatString('yyyy-MM-dd HH:mm') : '';
          const start = d['Start DateTime'] ? new Date(d['Start DateTime']).formatString('yyyy-MM-dd HH:mm') : '';

          this.selectedDocumentInfoData['docData'].push({
            'Doc Code': d['Doc Code'],
            'Doc Ref #': d['Reference No'],
            Attachment: d['AttImageName'],
            Remarks: d['Remarks'],
            Status: d['Status Count'] > 0 ? d['Status Code'] : 'N/A',
            'Start Date': d['Status Count'] > 0 ? start : 'N/A',
            'Received Date': d['Status Count'] > 0 ? received : 'N/A',
            'Due Date': d['Status Count'] > 0 ? due : 'N/A',
            'Assign To': d['Status Count'] > 0 ? d['Assign To'] : 'N/A',
            Sets: d['Status Count'] > 0 ? d['Sets'] : 'N/A',
            Page: d['Status Count'] > 0 ? d['Pages'] : 'N/A',

            documentguid: d['DocumentGUID'],
            statusseqno: '',
            ID: `${d['Doc Code']}-${d['Reference No']}`,
            fileAtt: d['AttImage'],
            filetype: d['AttImageType'],
            Action: '',
            'Row #': index, //id for image viewing
          });
        });
        this.showDocumentsLoader = false;
      });

      // this.selectedDocumentInfoData['docData'] = details['Documents'] ?? [];
      // this.tableDataType = {
      //   'Doc Rec Date': 'date',
      //   'Doc Date': 'date'
      // };
      if (details['Trip']) {
        const tripdetails = details['Trip'];
        this.selectedLorryInfoData['selectedLorryNoList'] =
          tripdetails['VehicleCode'] ?
            this.LorryInfoTabData['lorryNoList'].filter((t) => {
              return t['Vehicle Code'].trim() == tripdetails['VehicleCode'].trim();
            })[0]
          : null;
        this.selectedLorryInfoData['selectedDriverCodeList'] =
          tripdetails['Driver'] ?
            this.LorryInfoTabData['driverCodeList'].filter((t) => {
              return t['Driver Code'].trim() == tripdetails['Driver'].trim();
            })[0]
          : null;
        this.selectedLorryInfoData['driverEXP'] =
          tripdetails['Driver Exp 1'] ? this.$currencyFormat(tripdetails['Driver Exp 1'].trim()) : '';
        this.selectedLorryInfoData['driverTOL'] =
          tripdetails['Driver Exp 2'] ? this.$currencyFormat(tripdetails['Driver Exp 2'].trim()) : '';
        this.selectedLorryInfoData['selectedAttendantList'] =
          tripdetails['Attendant'] ?
            this.LorryInfoTabData['attendantList'].filter((t) => {
              return t['Attendant Code'].trim() == tripdetails['Attendant'].trim();
            })[0]
          : null;
        this.selectedLorryInfoData['attendantEXP'] =
          tripdetails['Attendant Exp 1'] ? this.$currencyFormat(tripdetails['Attendant Exp 1'].trim()) : '';
        this.selectedLorryInfoData['attendantTOL'] =
          tripdetails['Attendant Exp 2'] ? this.$currencyFormat(tripdetails['Attendant Exp 2'].trim()) : '';
        this.selectedLorryInfoData['selectedAttendantList2'] =
          tripdetails['Attendant2'] ?
            this.LorryInfoTabData['attendantList'].filter((t) => {
              return t['Attendant Code'].trim() == tripdetails['Attendant2'].trim();
            })[0]
          : null;
        this.selectedLorryInfoData['attendantEXP2'] =
          tripdetails['Attendant2 Exp 1'] ? this.$currencyFormat(tripdetails['Attendant2 Exp 1'].trim()) : '';
        this.selectedLorryInfoData['attendantTOL2'] =
          tripdetails['Attendant2 Exp 2'] ? this.$currencyFormat(tripdetails['Attendant2 Exp 2'].trim()) : '';

        if (type == 'e') {
          this.driverInfoDisabled = !this.selectedLorryInfoData['selectedLorryNoList'];
        }
        this.selectedLorry = this.selectedLorryInfoData['selectedLorryNoList'];
        this.driverCodeRequired = Boolean(this.selectedLorryInfoData['selectedLorryNoList']);
        this.selectedLorryInfoData['isJobCompleted'] =
          tripdetails['Status'] == '5011' || tripdetails['Status'] == '0810'; // ready for billing code
        if (type == 'v') {
          this.jobCompletedDisabled = true;
        } else {
          this.jobCompletedDisabled = this.selectedLorryInfoData['isJobCompleted']; // Disable checkbox if job is completed
        }
      }
      // #endregion

      if (details['JobConsignment']) {
        this.cargoInfoList = [];
        this.selectedCargoInfoData['selectedCargoInfoData'] = [];

        const consignmentDetails = details['JobConsignment'].map((c) => {
          return {
            '': this.getConsignmentIcons(c['CN Status'], c['CN Billing Status']),
            'Consignment #': c['Consignment No'],

            Product: `${c['Product'] ? c['Product'].split('(')[0].trim() : ''}`,
            //'Qty': `${c['Qty'] ? c['Qty'] : ''}`,
            Qty: `${c['Qty'] ? Math.round(c['Qty']) : ''}`, // remove decimals
            'Total KG': `${c['Total KG'] ? this.convertNumber(c['Total KG']) : ''}`,
            'Total M3': `${c['Total M3'] ? this.convertNumber(c['Total M3']) : ''}`,
            'Volume Weight': `${this.convertNumber((c['M3/Pkg'] * 1000000) / this.truckVolumeDivisor)}`,
            'Truck Type': `${c['Truck Type'] ? c['Truck Type'] : ''}`,
            'Bill Rate Type': `${c['Bill Rate Type'] ? c['Bill Rate Type'] : ''}`,
            'Bill Code': `${c['Bill Code'] ? c['Bill Code'] : ''}`,

            'Bill Rate': c['Bill Rate'] ? this.$currencyFormat(c['Bill Rate']) : this.$currencyFormat('0'),
            'Total Chrg': `${c['Total Chg'] ? this.$currencyFormat(c['Total Chg']) : ''}`,
            'Toll Chrg': `${c['Total Toll'] ? this.$currencyFormat(c['Total Toll']) : ''}`,

            'Item Type': `${c['Item Tyce'] ? c['Item Type'] : ''}`,
            Package: `${c['Package'] ? c['Package'] : ''}`,
            'KG/Pkg': `${c['KG/Pkg'] ? this.convertNumber(c['KG/Pkg']) : ''}`,
            'M3/Pkg': `${c['M3/Pkg'] ? this.convertNumber(c['M3/Pkg']) : ''}`,
            'CN Status': `${c['CN Status'] ? c['CN Status'] : ''}`,
            'CN Billing Status': `${c['CN Billing Status'] ? c['CN Billing Status'] : ''}`,

            'Length(M)': this.convertNumber(c['Length(M)']) || '',
            'Width(M)': this.convertNumber(c['Width(M)']) || '',
            'Height(M)': this.convertNumber(c['Height(M)']) || '',

            Temp: c['Temp.'],
            'Est Trucks': c['Est Truck'],
            'Cont. #': c['Cont #'],
            'Seal #': c['Seal #'],
            Opr: c['Opr'],
            'Cont. Size': c['Size'],
            'Cont. Type': c['Type'],
            Remarks: c['Remarks'],
            'Delete Remarks': c['Delete Remarks'],

            Seqno: c['Seqno'] || '',
            action:
              c['CN Status'] ?
                c['CN Status'] == 'Cancelled' ?
                  'D'
                : 'E'
              : 'E',
          };
        });
        this.$nextTick(() => (this.cargoInfoList = consignmentDetails));
      }

      this.$fullscreenLoading();
      this.milestoneHeight = 70;
    },
    getConsignmentIcons: function (status, billStatus) {
      if (status == 'Cancelled') {
        return 'https://dsmlib.logistics.systems/Icons/Cross.png';
      } else if (status == 'Assigned' && billStatus == 'Billed') {
        return 'https://dsmlib.logistics.systems/Icons/AssignAndBill.png';
      } else if (status == 'Assigned') {
        return 'https://dsmlib.logistics.systems/Icons/Approve.png';
      }
      return '';
    },
    clearPopupData: function () {
      this.viewMode = false;
      this.jobCompletedDisabled = false;
      this.driverInfoDisabled = true;
      this.driverCodeRequired = false;

      //Own variable instead of under object
      this.selectedShipperConsignee = null;

      Object.keys(this.selectedHeaderInfoData).forEach((k) => {
        this.selectedHeaderInfoData[k] = null;
      });
      Object.keys(this.selectedGeneralInfoData).forEach((k) => {
        this.selectedGeneralInfoData[k] = null;
      });
      Object.keys(this.selectedShippingInfoData).forEach((k) => {
        this.selectedShippingInfoData[k] = null;
      });

      //#region Cargo info
      this.selectedCargoInfoData['driverEXP'] = '0';
      this.selectedCargoInfoData['driverTOL'] = '0';
      this.selectedCargoInfoData['attendantEXP'] = '0';
      this.selectedCargoInfoData['attendantTOL'] = '0';
      this.selectedCargoInfoData['attendantEXP2'] = '0';
      this.selectedCargoInfoData['attendantTOL2'] = '0';
      this.selectedCargoInfoData['cargoToll'] = '0';
      this.selectedCargoInfoData['cargoTransport'] = '0';
      this.selectedCargoInfoData['cargoTotal'] = '0';
      this.cargoInfoList = [];
      this.selectedCargoInfoData['deletedCargoInfo'] = [];
      this.selectedCargoInfoData['selectedCargoInfo'] = [];
      this.selectedCargoInfoData['dim'] = '';
      //#endregion

      Object.keys(this.selectedLorryInfoData).forEach((k) => {
        this.selectedLorryInfoData[k] = null;
      });

      this.selectedDocumentInfoData['docData'] = [];
      this.selectedDocumentInfoData['selectedDoc'] = [];
      // if (this.headerInfoData['services']) {
      //   this.headerInfoData['services'].forEach((s) => {
      //     s['selected'] = false;
      //     s['remarks'] = '';
      //   });
      // } // moved to another funtion because causing services list to empty when Save clicked

      Object.keys(this.errorMessages).forEach((k) => {
        this.errorMessages[k] = '';
      });

      this.sMessages = [];
      this.eMessages = [];
      this.rMessages = [];

      this.documentsToDelete = [];
      this.editDeleteDoc = [];
      this.editAddDoc = [];

      this.milestonesItem = null;
      this.showMilestones = true;

      // this.tabsItems[6]['disabled'] = true;
      // this.tabsItems[7]['disabled'] = true;
      // this.tabsItems[8]['disabled'] = true;
      // this.tabsItems[9]['disabled'] = true;

      // this.dnListing = [];
      // this.rotListing = [];
      // this.statusTaskListing = [];
      // this.smkListing = [];

      this.editDetails = null;

      this.currentTabIndex = 1;
      this.documentInfoData['updatePopup'] = false;
      this.ednJsonDetails = null;
    },
    clearServicesData: function () {
      if (this.headerInfoData['services']) {
        this.headerInfoData['services'].forEach((s) => {
          s['selected'] = false;
          s['remarks'] = '';
        });
      }
    },
    billToSelected: async function (e) {
      if (!e) {
        this.selectedHeaderInfoData['billToDetails'] = null;
        return;
      }
      this.$fullscreenLoading('Loading');
      const getOutstanding = await this.$apiCallV2(
        `/FreightForwarding/GetAROutstanding?cust=${encodeURIComponent(e['Customer Code'].trim())}&jobdate=${this.selectedHeaderInfoData['jobTime']?.formatString('yyyy-MM-dd') ?? ''}`,
      );
      const details = getOutstanding[0];
      this.$fullscreenLoading();
      if (details['errMsg']) {
        await this.$showAlert(
          'error',
          'Credit Outstanding',
          `<div style="text-align: left">${details['errMsg']}</div>`,
        );

        this.$nextTick(() => {
          this.selectedHeaderInfoData = {
            ...this.selectedHeaderInfoData,
            selectedBillTo: null,
          };
          this.$refs['billcu_no'].setFocus();
        });
      }
    },
    shipperConsigneeSelected: function (e) {
      if (e) {
        this.selectedHeaderInfoData['selectedBillTo'] = this.headerInfoData['billTo'].getSelected(
          e['Bill To Code'],
          'Customer Code',
        );
        this.billToSelected(this.selectedHeaderInfoData['selectedBillTo']);
      } else {
        this.selectedHeaderInfoData['selectedBillTo'] = null;
      }
    },

    //#region for consignment
    showHideDetailsPopup: function (type, edit) {
      if (edit) {
        if (this.selectedCargoInfoData['selectedCargoInfo'].length == 0) {
          this.$showAlert(
            'error',
            'No Details Selected',
            `No ${type == 'Send To' ? 'Cargo Info' : 'Send To / Cargo Info'} details selected to edit`,
          );
          return;
        } else if (this.selectedCargoInfoData['selectedCargoInfo'].length > 1) {
          this.$showAlert(
            'error',
            'Multiple Details Selected',
            `Please select only 1 ${type == 'Send To' ? 'Cargo Info' : 'Send To / Cargo Info'} details to edit`,
          );
          return;
        }
      }

      // this.cargoPopupDetails['cargoAddType'] = type;
      // this.cargoPopupDetails['isEdit'] = edit ? true : false;

      // let toCompare = JSON.parse(JSON.stringify(this.selectedCargoInfoData['selectedCargoInfo'][0]));
      // this.cargoPopupDetails['editIndex'] = edit ? this.cargoInfoList.findIndex(c => {
      //   let cargoItem = JSON.parse(JSON.stringify(c));
      //   if (!cargoItem['_rowid']) {
      //     delete toCompare['_rowid'];
      //   }
      //   return JSON.stringify(cargoItem) == JSON.stringify(toCompare);
      // }) : -1;
      // this.cargoPopupDetails['editDetails'] = edit ? this.selectedCargoInfoData['selectedCargoInfo'][0] : null;
      this.cargoPopupDetails['cargoAddType'] = type;
      this.cargoPopupDetails['isEdit'] = Boolean(edit);
      this.cargoPopupDetails['editIndex'] = -1;
      this.cargoPopupDetails['editDetails'] = edit ? this.selectedCargoInfoData['selectedCargoInfo'][0] : null;
      if (edit) {
        const selectedCargoInfo = this.selectedCargoInfoData['selectedCargoInfo'][0];
        const compareColumns = ['Product', 'Qty', 'Package', 'Truck Type', 'Bill Type'];

        this.cargoPopupDetails['editIndex'] = this.cargoInfoList.findIndex((s) =>
          compareColumns.every((column) => s[column] === selectedCargoInfo[column]),
        );
      }
    },
    convertNumber: function (value) {
      if (value) {
        if (typeof value === 'number') {
          value = value.toString();
        }
        value = value.replaceAll(',', '');
        const converted = Number(value);
        const formatted = value ? this.$weightFormat(converted) : this.$weightFormat('0');
        return formatted;
      }
      return '';
    },
    cargoInfoPopupAddDetails: function (_sub, details) {
      this.cargoPopupDetails['editDetails'] = [];
      const tempCargo = this.cargoInfoList;
      this.cargoInfoList = [];
      const addEditDetails = {
        '': '',
        'Consignment #': '', //consignment #

        Product: details['truckSelectedProduct'] ? details['truckSelectedProduct']['Product Code'] : '',
        //'Qty': parseFloat(details['truckSelectedQty']).toFixed(4),
        Qty: Math.round(details['truckSelectedQty']), // remove decimals
        'Total KG': this.convertNumber(details['truckTotalKG']), //'150',
        'Total M3': this.convertNumber(details['truckTotalM3']), //'35',
        'Volume Weight': this.convertNumber(details['truckVolumeWeight']),
        'Truck Type': details['truckSelectedType'] ? details['truckSelectedType']['Truck Type'] : '', //'10T',
        'Bill Rate Type': details['truckSelectedBillType'] ? details['truckSelectedBillType']['BillType'] : '', //'1',
        'Bill Code': details['truckSelectedBillCode'] ? details['truckSelectedBillCode']['BillRateCode'] : '', //'TYREB',

        'Bill Rate': details['truckBillRate'],
        'Total Chrg':
          details['truckTotalCharge'] ? this.$currencyFormat(details['truckTotalCharge']) : this.$currencyFormat('0'),
        'Toll Chrg':
          details['truckTollCharge'] ? this.$currencyFormat(details['truckTollCharge']) : this.$currencyFormat('0'),

        'Item Type': '',
        Package: details['truckSelectedPackage'] ? details['truckSelectedPackage']['Package Code'] : '', //'PE',
        'KG/Pkg': this.convertNumber(details['truckPerKG']),
        'M3/Pkg': this.convertNumber(details['truckPerM3']),
        'CN Status': '',
        'CN Billing Status': '',

        'Length(M)': this.convertNumber(details['dimLength']),
        'Width(M)': this.convertNumber(details['dimWidth']),
        'Height(M)': this.convertNumber(details['dimHeight']),

        Temp: details['truckTemp'],
        'Est Trucks': details['truckEstTrucks'],
        //if truckSelectedContNo null, take from truckInputedContNo. (User allowed to select from dropdown or save input data.)
        'Cont. #':
          details['truckSelectedContNo'] ?
            details['truckSelectedContNo']['Container Code']
          : details['truckInputedContNo'],
        'Seal #': details['truckSealNo'],
        Opr: details['truckOpr'],
        'Cont. Size': details['truckSelectedContSize'] ? details['truckSelectedContSize']['Size'] : '',
        'Cont. Type': details['truckSelectedContType'] ? details['truckSelectedContType']['Cont Type'] : '',
        Remarks: details['truckSelectedRemarks'],
        'Delete Remarks': '',

        // 'Seqno': '',
        // 'action': '',
      };

      if (this.cargoPopupDetails['editIndex'] > -1) {
        // is edit
        const tempAction = tempCargo[this.cargoPopupDetails['editIndex']]['action'];
        if (!tempAction) {
          addEditDetails['action'] = 'E';
        } else if (tempAction == 'N') {
          addEditDetails['action'] = 'N';
        }

        addEditDetails['Seqno'] = tempCargo[this.cargoPopupDetails['editIndex']]['Seqno'];
        addEditDetails['Consignment #'] = tempCargo[this.cargoPopupDetails['editIndex']]['Consignment #'];

        tempCargo.splice(this.cargoPopupDetails['editIndex'], 1, addEditDetails);
      } else {
        addEditDetails['action'] = 'N';
        tempCargo.push(addEditDetails);
      }
      this.$nextTick(() => (this.cargoInfoList = tempCargo));

      this.cargoPopupDetails['cargoAddType'] = '';
      this.selectedCargoInfoData['selectedCargoInfo'] = [];

      // this.cargoPopupDetails['cargoAddType'] = '';

      // if (sub == 'Cargo' || sub == 'Send To') {
      //   let tempCargo = this.cargoInfoList;
      //   this.cargoInfoList = [];
      //   let addEditDetails = {
      //     'cnaction': 'N',
      //     'cnseqno': '',  //#
      //     'cnno': '',  //consignment #
      //     'prodcode': details.truckSelectedProduct ? details.truckSelectedProduct['Product Code'] : '',
      //     'cnqty': details.truckSelectedQty,
      //     'cnpkgcode': details.truckSelectedPackage ? details.truckSelectedPackage['Package Code'] : '', //'PE',
      //     'cnttlkg': details.truckTotalKG, //'150',
      //     'cnttlm3': details.truckTotalM3, //'35',
      //     'cntrucktype': details.truckSelectedType ? details.truckSelectedType['Truck Type'] : '', //'10T',
      //     'billtype': details.truckSelectedBillType ? details.truckSelectedBillType['BillType']: '' , //'1',
      //     'billcode': details.truckSelectedBillCode ? details.truckSelectedBillCode['BillRateCode']: '',  //'TYREB',
      //     'billrate': (details.truckSelectedBillType['BillType'] == '' ? 0 : 0) || (details.truckSelectedBillType['BillType'] == 3 ? details.truckBillRate/details.truckTotalM3 : details.truckBillRate) || (details.truckSelectedBillType['BillType'] == 4 ? details.truckBillRate/details.truckTotalKG : details.truckBillRate), //'0',
      //     'cnremarks': details.truckSelectedRemarks, //'CN 01'

      //     'height': details.dimHeight,
      //     'width': details.dimWidth,
      //     'length': details.dimLength,
      //     'temp': details.truckTemp,
      //     'cnesttruck': details.truckEstTrucks,
      //     'contno': details.truckSelectedContNo ? details.truckSelectedContNo['Container Code'] : '',
      //     'sealno': details.truckSealNo,
      //     'contsize': details.truckSelectedContSize ? details.truckSelectedContSize['Size'] : '',
      //     'conttype': details.truckSelectedContType ? details.truckSelectedContType['Cont Type'] : '',
      //     'operatorcode': details.truckOpr,

      //               // 'Dimensions': `${details.dimLength ? details.dimLength : '0'}m X ${details.dimWidth ? details.dimWidth : '0'}m X ${details.dimHeight ? details.dimHeight : '0'}m`,

      //     // 'Temp': details.truckTemp,
      //     // 'Est Trucks': details.truckEstTrucks,
      //     // 'Cont. #': details.truckSelectedContNo ? details.truckSelectedContNo['Container Code'] : '',
      //     // 'Seal #': details.truckSealNo,
      //     // 'Opr': details.truckOpr,
      //     // 'Cont. Size': details.truckSelectedContSize ? details.truckSelectedContSize['Size'] : '',
      //     // 'Cont. Type': details.truckSelectedContType ? details.truckSelectedContType['Cont Type'] : '',
      //     // 'Remarks': details.truckSelectedRemarks,

      //     // //action
      //     // '#': '',
      //     // 'Consignment #': '',

      //     // 'Product': details.truckSelectedProduct ? details.truckSelectedProduct['Product Code'] : '',
      //     // 'Qty': details.truckSelectedQty,
      //     // 'Total KG': details.truckTotalKG,
      //     // 'Total M3': details.truckTotalM3,
      //     // 'Truck Type': details.truckSelectedType ? details.truckSelectedType['Truck Type'] : '',
      //     // 'Bill Rate Type': details.truckSelectedBillType ? details.truckSelectedBillType['BillType']: '' ,
      //     // 'Bill Code': details.truckSelectedBillCode ? details.truckSelectedBillCode['BillRateCode']: '',
      //     // 'Bill Rate': (details.truckSelectedBillType['BillType'] == '' ? 0 : 0) || (details.truckSelectedBillType['BillType'] == 3 ? details.truckBillRate/details.truckTotalM3 : details.truckBillRate) || (details.truckSelectedBillType['BillType'] == 4 ? details.truckBillRate/details.truckTotalKG : details.truckBillRate),

      //     // 'Total Chrg': details.truckSelectedBillType ? (details.truckSelectedBillType['BillType'] == 0 ? details.truckBillRate*details.truckSelectedQty : details.truckBillRate) : '',
      //     // 'Item Type': details.truckSelectedProduct ? details.truckSelectedProduct['ItemType'] : '',
      //     // 'Package': details.truckSelectedPackage ? details.truckSelectedPackage['Package Code'] : '',
      //     // 'KG/Pkg' : details.truckTotalKG ? ( isNaN(details.truckTotalKG/details.truckSelectedQty) ? '0.0000' : details.truckTotalKG/details.truckSelectedQty ) : '',
      //     // 'M3/Pkg' : details.truckTotalM3 ? ( isNaN(details.truckTotalM3/details.truckSelectedQty) ? '0.0000' : details.truckTotalM3/details.truckSelectedQty  ) : '',
      //     // 'Toll Chrg': '',
      //     // 'CN Status': '',
      //     // 'CN Billing Status': '',

      //     // 'Dimensions': `${details.dimLength ? details.dimLength : '0'}m X ${details.dimWidth ? details.dimWidth : '0'}m X ${details.dimHeight ? details.dimHeight : '0'}m`,

      //     // 'Temp': details.truckTemp,
      //     // 'Est Trucks': details.truckEstTrucks,
      //     // 'Cont. #': details.truckSelectedContNo ? details.truckSelectedContNo['Container Code'] : '',
      //     // 'Seal #': details.truckSealNo,
      //     // 'Opr': details.truckOpr,
      //     // 'Cont. Size': details.truckSelectedContSize ? details.truckSelectedContSize['Size'] : '',
      //     // 'Cont. Type': details.truckSelectedContType ? details.truckSelectedContType['Cont Type'] : '',
      //     // 'Remarks': details.truckSelectedRemarks,
      //   };

      //   if (this.cargoPopupDetails['editIndex'] >= 0) {
      //     tempCargo.splice(this.cargoPopupDetails['editIndex'], 1, addEditDetails);
      //     this.selectedCargoInfoData['selectedCargoInfo'] = [];
      //   } else {
      //     tempCargo.push(addEditDetails);
      //   }

      //   this.$nextTick(() => this.cargoInfoList = tempCargo);
      // }
    },
    cargoPopupCancel: function () {
      this.cargoPopupDetails['cargoAddType'] = '';
      this.cargoPopupDetails['isEdit'] = false;
      this.cargoPopupDetails['editDetails'] = [];
    },
    cargoInfoPopupDeleteDetails: function () {
      if (this.selectedCargoInfoData['selectedCargoInfo'].length == 0) {
        this.$showAlert('error', 'No Consignments', 'Please select at least one consignment to delete.');
      } else {
        const hasDeleteOrAssign = this.selectedCargoInfoData['selectedCargoInfo'].map((c) => c['']).filter((c) => c);
        if (hasDeleteOrAssign.length > 0) {
          this.$showAlert(
            'error',
            'Invalid Consignment',
            'One or more consignment(s) selected to delete has been Cancelled or Assigned.\n\nThese consignment(s) cannnot be deleted.',
          );
        } else {
          const toDeleteHasEAction = this.selectedCargoInfoData['selectedCargoInfo'].filter((c) => c['action'] != 'N');
          if (toDeleteHasEAction.length > 0) {
            this.selectedCargoInfoData['cndeleteremark'] = '';
            this.showConsignmentDeletePopup = true;
          } else {
            this.selectedCargoInfoData['cndeleteremark'] = 'offline delete';
            this.consignmentPopupDeleteDetails('Delete');
          }
        }
      }
    },

    consignmentPopupDeleteDetails: async function (e) {
      switch (e) {
        case 'Delete':
          if (!this.selectedCargoInfoData['cndeleteremark']) {
            this.deleteRemarksError = 'Please enter your remarks to delete';
            this.entryRequired.push({ key: 'deleteRemarks', value: 'Please enter delete remarks!' });
          } else {
            this.showDeleteConsignmentPopup = false;

            const temp = this.cargoInfoList;
            this.cargoInfoList = [];

            this.selectedCargoInfoData['selectedCargoInfo'].forEach((sc) => {
              const index = temp.findIndex((c) => JSON.stringify(c) == JSON.stringify(sc));

              if (sc['action'] != 'N') {
                sc[''] = 'https://dsmlib.logistics.systems/Icons/GrayCross.png';
                sc['action'] = 'D';
                sc['Delete Remarks'] = this.selectedCargoInfoData['cndeleteremark'];
                temp.splice(index, 1, sc);
              } else {
                temp.splice(index, 1);
              }
            });

            await this.$nextTick();
            this.cargoInfoList = temp;
            this.selectedCargoInfoData['selectedCargoInfo'] = [];

            this.showConsignmentDeletePopup = false;
          }
          break;
        case 'Cancel':
          this.selectedCargoInfoData['cndeleteremark'] = '';
          this.showConsignmentDeletePopup = false;
          break;
      }
    },

    getCargoInfoBillCode: async function () {
      if (!(this.truckTypeSend && this.billTypeSend)) return;
      this.temp = this.selectedHeaderInfoData['jobTime'].formatString('yyyy-MM-dd');
      this.accountcode =
        !this.selectedHeaderInfoData['selectedBillTo'] ?
          ''
        : this.selectedHeaderInfoData['selectedBillTo']['Customer Code'];
      this.departurecode =
        !this.selectedHeaderInfoData['selectedPickUp'] ?
          ''
        : this.selectedHeaderInfoData['selectedPickUp']['Destination Code'];
      this.destinationcode =
        !this.selectedHeaderInfoData['selectedDropOff'] ?
          ''
        : this.selectedHeaderInfoData['selectedDropOff']['Destination Code'];

      const cargoInfoBillCode = await this.$apiCallV2(
        `/TruckingJob/GetTMSBillCodeList?billratetype=${this.billTypeSend}&accountcode=${this.accountcode}&jobdate=${this.temp}&departurecode=${this.departurecode}&destinationcode=${this.destinationcode}&vehiclecategory=${this.truckTypeSend}`,
      ); //newH

      //this.$refs['BillCodeRef'].truckBillCodeList = []; //removed due to "Avoid mutating a prop directly"
      this.cargoInfoData['cargoBillCodeList'] = [];

      this.$nextTick(() => {
        //this.$refs['BillCodeRef'].truckBillCodeList = cargoInfoBillCode; //removed due to "Avoid mutating a prop directly"
        this.cargoInfoData['cargoBillCodeList'] = cargoInfoBillCode;
      });
    },
    billTypeSelected: function (e) {
      this.billTypeSend = e;
      this.getCargoInfoBillCode();
    },
    truckTypeSelected: function (e) {
      this.truckTypeSend = e;
      this.getCargoInfoBillCode();
    },
    // #endregion

    //#region get data from API
    getHeaderInfoTabDetails: function () {
      const headerInfoTabDetailsPromises = [];

      headerInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'cargotype', module: 'TMS' }).then((result) => {
          this.headerInfoData['cargo'] = this.$deepTrimJsonV3(result);
        }),
      );

      headerInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'billingby' }).then((result) => {
          this.headerInfoData['billBy'] = this.$deepTrimJsonV3(result);
        }),
      );

      headerInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'team', module: 'TMS' }).then((result) => {
          this.headerInfoData['operationTeam'] = this.$deepTrimJsonV3(result);
        }),
      );

      headerInfoTabDetailsPromises.push(
        this.getShipperConsigneeListing()
      );

      headerInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'vesselid' }).then((result) => {
          const vesselID = this.$deepTrimJsonV3(result);
          const vesselIDForVerification = vesselID.map((item) => ({
            ...item,
            Description: ` ${item['Vessel ID']} - ${item['Vessel Name']}`,
          }));
          this.headerInfoData['vesselIDList'] = vesselID;
          this.shippingInfoData['vesselCodeList'] = vesselIDForVerification;
        }),
      );

      headerInfoTabDetailsPromises.push(
        this.getBillToListing()
      );

      headerInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'jobtype', module: 'TMS' }).then((result) => {
          const jobTypeList = this.$deepTrimJsonV3(result);
          this.headerInfoData['jobType'] = jobTypeList;
        }),
      );

      headerInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'billingformat', module: 'TMS' }).then((result) => {
          const billingFmtList = this.$deepTrimJsonV3(result);
          this.headerInfoData['billingfmt'] = billingFmtList;
        }),
      );

      headerInfoTabDetailsPromises.push(
        this.getDestinationListing()
      );

      headerInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'job_servicelist', module: 'FMS' }).then((result) => {
          result.forEach((s) => {
            this.headerInfoData['services'].push({ selected: false, item: s['Description'], remarks: '', data: s });
          });
        }),
      );

      headerInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'job_unconfirm_reason', module: 'TMS' }).then(
          (result) => {
            const unconfirmReasons = this.$deepTrimJsonV3(result);
            this.reasonsList = unconfirmReasons;
          },
        ),
      );

      // var tariffZonesPickup = this.$deepTrimJsonV3(await this.$apiCallV2('/TruckingJob/GetDetailsMaster?masterfile=destination&module=MASTER&recordid=01HARD')); //newH
      // this.headerInfoData['zones'] = tariffZonesPickup;
      // this.headerInfoData['tariffArea'] = tariffZonesPickup;
      // this.headerInfoData['dropOffAddress'] = tariffZonesPickup;
      // this.headerInfoData['pickUpAddress']= tariffZonesPickup;
      // this.loadingCount++;

      return headerInfoTabDetailsPromises;
    },

    getLorryInfoTabDetails: function () {
      const lorryInfoTabDetailsPromises = [];
      lorryInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'vehicle_tms' }).then((result) => {
          const lorryNoList = this.$deepTrimJsonV3(result);
          const lorryNoListForVerification = lorryNoList.map((item) => ({
            ...item,
            Description: `${item['Vehicle Code']} - ${item['Vehicle Number']}`,
          }));
          this.LorryInfoTabData['lorryNoList'] = lorryNoListForVerification;
        }),
      );

      lorryInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'driverno' }).then((result) => {
          const driverCodeList = this.$deepTrimJsonV3(result);
          const driverCodeListForVerification = driverCodeList.map((item) => ({
            ...item,
            Description: `${item['Driver Code']} - ${item['Driver Name']}`,
          }));
          this.LorryInfoTabData['driverCodeList'] = driverCodeListForVerification;
        }),
      );

      lorryInfoTabDetailsPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'attendant' }).then((result) => {
          const attendantList = this.$deepTrimJsonV3(result);
          this.LorryInfoTabData['attendantList'] = attendantList;
          this.LorryInfoTabData['attendantList'] = attendantList;
        }),
      );
      return lorryInfoTabDetailsPromises;
    },

    getShippingInfoTabDetails: function () {
      const shippingInfoTabPromises = [];

      shippingInfoTabPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'agent' }).then((result) => {
          this.shippingInfoData['agentList'] = result.map((item) => {
            return {
              ...item,
              Description: `${item['Agent Code']} - ${item['Agent Name']}`,
            };
          });
          this.shippingInfoData['localCarrier'] = result.map((item) => {
            return {
              ...item,
              Description: `${item['Agent Code']} - ${item['Agent Name']}`,
            };
          });
          this.shippingInfoData['foreignCarrier'] = result.map((item) => {
            return {
              ...item,
              Description: `${item['Agent Code']} - ${item['Agent Name']}`,
            };
          });
        }),
      );

      shippingInfoTabPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'frtforwarder' }).then((result) => {
          this.shippingInfoData['forwardingAgent'] = result.map((item) => {
            return {
              ...item,
              Description: `${item['Agent Code']} - ${item['Agent Name']}`,
            };
          });
        }),
      );

      shippingInfoTabPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'foreignforwarder' }).then((result) => {
          this.shippingInfoData['foreignForwardingAgent'] = result.map((item) => {
            return {
              ...item,
              Description: `${item['Agent Code']} - ${item['Agent Name']}`,
            };
          });
        }),
      );

      shippingInfoTabPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'shipcallno' }).then((result) => {
          this.shippingInfoData['shipCallNoList'] = result;
        }),
      );

      shippingInfoTabPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'shippingagent' }).then((result) => {
          this.shippingInfoData['shippingAgentList'] = result.map((item) => {
            return {
              ...item,
              Description: `${item['Agent Code']} - ${item['Agent Name']}`,
            };
          });
        }),
      );

      shippingInfoTabPromises.push(
        this.getPortListing()
      );
      return shippingInfoTabPromises;
    },

    getCargoInfoTabDetails: function () {
      const cargoInfoTabPromises = [];

      cargoInfoTabPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'container' }).then((result) => {
          this.cargoInfoData['cargoContNoList'] = this.$deepTrimJsonV3(result);
        }),
      );

      cargoInfoTabPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'contsize' }).then((result) => {
          this.cargoInfoData['cargoContSizeList'] = this.$deepTrimJsonV3(result);
        }),
      );

      cargoInfoTabPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'conttype', module: 'TMS' }).then((result) => {
          this.cargoInfoData['cargoContTypeList'] = this.$deepTrimJsonV3(result);
        }),
      );

      cargoInfoTabPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'whproduct', module: 'TMS' }).then((result) => {
          this.cargoInfoData['cargoProductList'] = this.$deepTrimJsonV3(result);
        }),
      );

      cargoInfoTabPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'whpackage' }).then((result) => {
          this.cargoInfoData['cargoPackageList'] = this.$deepTrimJsonV3(result);
        }),
      );

      cargoInfoTabPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'trucktype' }).then((result) => {
          this.cargoInfoData['cargoTruckTypeList'] = this.$deepTrimJsonV3(result);
        }),
      );

      cargoInfoTabPromises.push(
        this.$apiCallV2('/Master/GetListMaster', { masterfile: 'billingtype', module: 'TMS' }).then((result) => {
          this.cargoInfoData['cargoBillTypeList'] = this.$deepTrimJsonV3(result);
        }),
      );

      cargoInfoTabPromises.push(
        this.$apiCallV2('/TruckingJob/GetSettingsSystem').then((result) => {
          const trimmedResult = this.$deepTrimJsonV3(result);
          this.cargoInfoData['cargoVolumeDivisorList'] = trimmedResult;
          this.defaultTabIndex = trimmedResult[0]['TMS_OrderUpdateForm'] == 'FRMQUICKORDER' ? 0 : 1;
          this.defaultVolDivisor = trimmedResult[0]['TMS_VolDivisor'];
          this.defaultJobType = trimmedResult[0]['TMS_DefaultJobType'];
          this.defaultBillBy = trimmedResult[0]['TMS_DefaultBillBy'];
        }),
      );
      return cargoInfoTabPromises;
    },
    //#endregion

    //#region InputQuery Refresh Clicked Handlers
    shipperConsigneeInputQueryRefreshClicked: async function (placeholder) {
      this.headerInfoData['shipperConsignee'] = [];
      this.$fullscreenLoading(`Getting Latest ${placeholder} List`);
      await this.getShipperConsigneeListing();
      this.$fullscreenLoading();
    },
    billToInputQueryRefreshClicked: async function (placeholder) {
      this.headerInfoData['billTo'] = [];
      this.$fullscreenLoading(`Getting Latest ${placeholder} List`);
      await this.getBillToListing();
      this.$fullscreenLoading();
    },
    destinationInputQueryRefreshClicked: async function (placeholder) {
      this.headerInfoData['pickup'] = [];
      this.headerInfoData['transit1'] = [];
      this.headerInfoData['transit2'] = [];
      this.headerInfoData['dropoff'] = [];
      this.$fullscreenLoading(`Getting Latest ${placeholder} List`);
      await this.getDestinationListing();
      this.$fullscreenLoading();
    },
    portInputQueryRefreshClicked: async function (placeholder) {
      this.shippingInfoData['transhipmentPort'] = [];
      this.shippingInfoData['dischargeLoadPort'] = [];
      this.$fullscreenLoading(`Getting Latest ${placeholder} List`);
      await this.getPortListing();
      this.$fullscreenLoading();
    },
    //#endregion

    //#region API call add description
    getShipperConsigneeListing: async function () {
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'shipperconsignee' }).then((result) => {
        const shipperList = this.$deepTrimJsonV3(result);
        const shipperListForVerification = shipperList.map((item) => ({
          ...item,
          Description: `${item['Customer Code']} - ${item['Customer Name']}`,
        }));
        this.headerInfoData['shipperConsignee'] = shipperListForVerification;
        this.headerInfoData['shipper'] = shipperList;
        this.headerInfoData['consignee'] = shipperList;
      });
    },
    getBillToListing: async function () {
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'billcustwrate' }).then((result) => {
        const billToList = this.$deepTrimJsonV3(result);
        const billToListForVerification = billToList.map((item) => ({
          ...item,
          Description: ` ${item['Customer Code']} - ${item['Customer Name']}`,
        }));
        this.headerInfoData['billTo'] = billToListForVerification;
      });
    },
    getDestinationListing: async function () {
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'destination', module: 'TMS' }).then((result) => {
        const pickupTransitDropoffQuery = this.$deepTrimJsonV3(result);
        const pickupTransitDropoffQueryForVerification = pickupTransitDropoffQuery.map((item) => ({
          ...item,
          Description: ` ${item['Destination Code']} - ${item['Company']}`,
        }));
        this.headerInfoData['pickup'] = pickupTransitDropoffQueryForVerification;
        this.headerInfoData['transit1'] = pickupTransitDropoffQueryForVerification;
        this.headerInfoData['transit2'] = pickupTransitDropoffQueryForVerification;
        this.headerInfoData['dropoff'] = pickupTransitDropoffQueryForVerification;
      });
    },
    getPortListing: async function () {
      await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'portcode' }).then((result) => {
        this.shippingInfoData['transhipmentPort'] = result.map((item) => {
          return {
            ...item,
            Description: `${item['Port Code']} - ${item['Port Location']}`,
          };
        });
        this.shippingInfoData['dischargeLoadPort'] = result.map((item) => {
          return {
            ...item,
            Description: `${item['Port Code']} - ${item['Port Location']}`,
          };
        });
      });
    },
    //#endregion

    //#region format cargo
    formatCargoPkgList: function () {
      const formatted = [];
      this.cargoInfoList.forEach((data, index) => {
        const dim = data['Dimensions'].split('X');
        const conno = data['Consignment #'].split('-');

        formatted.push({
          cnaction: this.popupTitle[0],
          cnseqno: index,
          cnno: conno[0].replace(index, '').trim(),
          prodcode: data['Product'],
          cnqty: Number(data['Qty']).toFixed(),
          cnpkgcode: data['Package'],
          cnttlkg: data['Total KG'],
          cnttlm3: data['Total M3'],
          cntrucktype: data['Truck Type'],
          billtype: data['Bill Rate Type'],
          billcode: data['Bill Code'],
          billrate: data['Total Chrg'],

          cnremarks: data['Remarks'],
          offhrs: '',
          height: dim[2].replace('m', '').trim(),
          width: dim[1].replace('m', '').trim(),
          length: dim[0].replace('m', '').trim(),
          temp: data['Temp.'],
          cnesttruck: data['Est Trucks'],
          contno: data['Cont. #'],
          sealno: data['Seal #'],
          contsize: data['Cont. Size'],
          conttype: data['Cont. Type'],
          operatorcode: data['Opr'],
        });
      });
      return formatted;
    },

    formatCargoContList: function () {
      return this.cargoInfoList.map((c) => {
        return {
          cnaction: c['action'] || 'E',
          cnseqno: c['Seqno'], //#
          cnno: c['Consignment #'], //consignment #
          prodcode: c['Product'] || '',
          cnqty: c['Qty'],
          cnpkgcode: c['Package'], //'PE',
          cnttlkg: c['Total KG'], //'150',
          cnttlm3: c['Total M3'], //'35',
          cntrucktype: c['Truck Type'], //'10T',
          billtype: c['Bill Rate Type'], //'1',
          billcode: c['Bill Code'], //'TYREB',
          billrate: c['Bill Rate'], //'0',
          cnremarks: c['Remarks'], //'CN 01'

          height: c['Height(M)'],
          width: c['Width(M)'],
          length: c['Length(M)'],
          temp: c['Temp'],
          cnesttruck: c['Est Trucks'],
          contno: c['Cont. #'],
          sealno: c['Seal #'],
          contsize: c['Cont. Size'],
          conttype: c['Cont. Type'],
          operatorcode: c['Opr'],

          'KG/Pkg': `${c['KG/Pkg'] ? c['KG/Pkg'] : ''}`,
          'M3/Pkg': `${c['M3/Pkg'] ? c['M3/Pkg'] : ''}`,
          'Toll Chrg': `${c['Total Toll'] ? c['Total Toll'] : ''}`,
          'CN Status': `${c['CN Status'] ? c['CN Status'] : ''}`,
          'CN Billing Status': `${c['CN Billing Status'] ? c['CN Billing Status'] : ''}`,

          cndeleteremarks: c['Delete Remarks'],
        };
      });
    },

    formatServicesList: function () {
      const formatted = [];
      if (this.popupTitle[0] == 'E') {
        const services = this.headerInfoData['services'];
        services.forEach((s) => {
          if (s['selected'] !== s['originalSelected']) {
            // get services that changed
            formatted.push({
              serviceaction: s['selected'] ? 'N' : 'D', // 'D' if unselected
              status: s['data']['Status'],
              seqno: '',
              remarks: s['remarks'],
              timeupdated: new Date().formatString('yyyy-MM-dd'),
            });
          } else if (s['selected']) {
            formatted.push({
              serviceaction: 'E', // remaining set as 'E'
              status: s['data']['Status'],
              seqno: '',
              remarks: s['remarks'],
              timeupdated: new Date().formatString('yyyy-MM-dd'),
            });
          }
        });
      } else if (this.popupTitle[0] == 'N') {
        const selected = this.headerInfoData['services'].filter((s) => {
          return s['selected'];
        }); // get selected services only
        selected.forEach((s) => {
          formatted.push({
            serviceaction: this.popupTitle[0],
            status: s['data']['Status'],
            seqno: '',
            remarks: s['remarks'],
            timeupdated: new Date().formatString('yyyy-MM-dd'),
          });
        });
      }
      return formatted;
    },

    formatDocuments: function () {
      const adding = this.selectedDocumentInfoData['docData'].filter((d) => d['Action']);
      const deleting = this.documentsToDelete;

      let addingStr = JSON.stringify(adding.concat(deleting));
      addingStr = addingStr.replace(/"N\/A"/g, '""'); //replace all key values that is "N/A" to empty string instead of below code which replace any N/A
      // addingStr = addingStr.split('N/A').join('');

      const formatted = JSON.parse(addingStr);
      formatted.forEach((f) => {
        f['Start Date'] = f['Start Date'].split(':').join('');
        f['Received Date'] = f['Received Date'].split(':').join('');
        f['Due Date'] = f['Due Date'].split(':').join('');
      });
      return formatted;
    },

    formatDeleteDocuments: function () {
      // this.deleteItems
    },
    //#endregion

    saveClicked: async function (noload, bypassCompulsory, save) {
      this.sMessages = [];
      this.eMessages = [];
      this.rMessages = [];

      this.selectedUnconfirmReason = null;
      this.unconfirmRemarks = '';

      this.popupError = false;
      this.popupErrorText = '';
      if (!noload) {
        // Only show the loading if noload is null or undefined
        this.$fullscreenLoading('Check Compulsory Fields');
      }

      const compulsoryError = this.checkRequiredFields();
      this.$fullscreenLoading();
      //console.log(this.selectedHeaderInfoData['selectedCargoPackage'])
      const postData = {
        action: this.popupTitle.includes('Duplicate') ? 'N' : this.popupTitle[0],
        job_no: this.selectedHeaderInfoData['jobNo'],
        cu_number: !this.selectedShipperConsignee ? '' : this.selectedShipperConsignee['Customer Code'], //'ADIDA',
        billcu_no:
          !this.selectedHeaderInfoData['selectedBillTo'] ?
            ''
          : this.selectedHeaderInfoData['selectedBillTo']['Customer Code'], //'ADIDA',
        lastupdate: '',
        job_type: !this.selectedJobType ? '' : this.selectedJobType['Job Type'], //'I',
        job_date:
          this.selectedHeaderInfoData['jobTime'] ?
            this.selectedHeaderInfoData['jobTime'].formatString('yyyy-MM-dd')
          : '', //'2022-08-19',
        jobtime:
          this.selectedHeaderInfoData['jobTime'] ? this.selectedHeaderInfoData['jobTime'].formatString('HHmm') : '', //'1500',
        reqdatefrom:
          this.selectedHeaderInfoData['reqJobTime'] ?
            this.selectedHeaderInfoData['reqJobTime'].formatString('yyyy-MM-dd')
          : '', //'2022-08-19',
        reqtimefrom:
          this.selectedHeaderInfoData['reqJobTime'] ?
            this.selectedHeaderInfoData['reqJobTime'].formatString('HHmm')
          : '', //'1500',
        reqdateto:
          this.selectedHeaderInfoData['reqJobTimeTo'] ?
            this.selectedHeaderInfoData['reqJobTimeTo'].formatString('yyyy-MM-dd')
          : this.selectedHeaderInfoData['reqJobTime'].formatString('yyyy-MM-dd'),
        reqtimeto:
          this.selectedHeaderInfoData['reqJobTimeTo'] ?
            this.selectedHeaderInfoData['reqJobTimeTo'].formatString('HHmm')
          : this.selectedHeaderInfoData['reqJobTime'].formatString('HHmm'),
        reqtimedesc: !this.selectedHeaderInfoData['ReqTimeDesc'] ? '' : this.selectedHeaderInfoData['ReqTimeDesc'], //'ASAP',
        offhrs: !this.selectedLorryInfoData['isOfficeHours'] ? '0' : '1', //'1',
        cargotype_code:
          !this.selectedHeaderInfoData['selectedCargo'] ?
            ''
          : this.selectedHeaderInfoData['selectedCargo']['Cargo Type'], //'BULK',
        delivery_order: !this.selectedHeaderInfoData['DOno'] ? '' : this.selectedHeaderInfoData['DOno'],
        requestby:
          !this.selectedHeaderInfoData['selectedRequestBy'] ? '' : this.selectedHeaderInfoData['selectedRequestBy'], //'AADO1587',
        remarks: !this.selectedGeneralInfoData['remarks'] ? '' : this.selectedGeneralInfoData['remarks'], //'TBA',
        pickup:
          !this.selectedHeaderInfoData['selectedPickUp'] ?
            ''
          : this.selectedHeaderInfoData['selectedPickUp']['Destination Code'], //'1016701',
        transit1:
          !this.selectedHeaderInfoData['selectedTransit1'] ?
            ''
          : this.selectedHeaderInfoData['selectedTransit1']['Destination Code'], //'04-G',
        transit2:
          !this.selectedHeaderInfoData['selectedTransit2'] ?
            ''
          : this.selectedHeaderInfoData['selectedTransit2']['Destination Code'], //'0G',
        dropoff:
          !this.selectedHeaderInfoData['selectedDropOff'] ?
            ''
          : this.selectedHeaderInfoData['selectedDropOff']['Destination Code'], //'0F',
        urgent: '', //'Y',
        projectno: !this.selectedHeaderInfoData['Projno'] ? '' : this.selectedHeaderInfoData['Projno'], //'-',
        custref: !this.selectedHeaderInfoData['CustRefno'] ? '' : this.selectedHeaderInfoData['CustRefno'], //'CUSF111',
        base: this.loginDetails['BaseName'], //'PKG',
        bookingrefno:
          !this.selectedShippingInfoData['selectedBookingRefNo'] ?
            ''
          : this.selectedShippingInfoData['selectedBookingRefNo'], //'BF77933',
        shipper:
          !this.selectedHeaderInfoData['selectedShipper'] ? '' : this.selectedHeaderInfoData['selectedShipper']['#'], //'001-CM',
        consignee:
          !this.selectedHeaderInfoData['selectedConsignee'] ?
            ''
          : this.selectedHeaderInfoData['selectedConsignee']['Bill To Code'], //'ADIDA',
        billby:
          !this.selectedHeaderInfoData['selectedBillBy'] ?
            ''
          : this.selectedHeaderInfoData['selectedBillBy']['BillingBy'], //'1',
        pkgcode:
          !this.selectedHeaderInfoData['selectedCargoPackage'] ?
            ''
          : this.selectedHeaderInfoData['selectedCargoPackage']['Package Code'], //'PK',
        estqty: '', //'1',
        ttlpallet:
          !this.selectedHeaderInfoData['selectedCargoPallet'] ? '' : this.selectedHeaderInfoData['selectedCargoPallet'], //'0',
        ttlkg:
          !this.selectedHeaderInfoData['selectedCargoGrossWeight'] ?
            ''
          : this.selectedHeaderInfoData['selectedCargoGrossWeight'], //'40',
        ttlm3:
          !this.selectedHeaderInfoData['selectedCargoGrossVolume'] ?
            ''
          : this.selectedHeaderInfoData['selectedCargoGrossVolume'], //'48',
        esttruck:
          !this.selectedHeaderInfoData['selectedCargoEstTrucks'] ?
            ''
          : this.selectedHeaderInfoData['selectedCargoEstTrucks'], //'1',
        trucktype:
          !this.selectedHeaderInfoData['selectedCargoTruckType'] ?
            ''
          : this.selectedHeaderInfoData['selectedCargoTruckType']['Truck Type'], //'3T',
        billfmt:
          !this.selectedHeaderInfoData['selectedBillingFmt'] ?
            ''
          : this.selectedHeaderInfoData['selectedBillingFmt']['Billing Fmt'], //'5',
        opteam:
          !this.selectedHeaderInfoData['selectedOperationTeam'] ?
            ''
          : this.selectedHeaderInfoData['selectedOperationTeam']['Team'], //'18T20F',
        agent:
          !this.selectedShippingInfoData['selectedAgent'] ?
            ''
          : this.selectedShippingInfoData['selectedAgent']['Agent Code'], //'ABX---',
        fwdgagent:
          !this.selectedShippingInfoData['selectedForwardingAgent'] ?
            ''
          : this.selectedShippingInfoData['selectedForwardingAgent']['Agent Code'], //'FSF',
        localcarrier:
          !this.selectedShippingInfoData['selectedLocalCarrier'] ?
            ''
          : this.selectedShippingInfoData['selectedLocalCarrier']['Agent Code'], //'1234',
        foreignfwdg:
          !this.selectedShippingInfoData['selectedForeignForwardingAgent'] ?
            ''
          : this.selectedShippingInfoData['selectedForeignForwardingAgent']['Agent Code'], //'ITM',
        foreigncarrier:
          !this.selectedShippingInfoData['selectedForeignCarrier'] ?
            ''
          : this.selectedShippingInfoData['selectedForeignCarrier']['Agent Code'], //'456',
        shipcallid:
          this.selectedShippingInfoData['selectedShipCall'] ?
            this.selectedShippingInfoData['selectedShipCall']['Ship Call No']
          : this.selectedShippingInfoData['shipCallNoValue'],//'001-CM',
        voyageno: !this.selectedShippingInfoData['voyNo'] ? '' : this.selectedShippingInfoData['voyNo'], //'TBA',
        vesselcode:
          !this.selectedShippingInfoData['selectedVesselCode'] ?
            ''
          : this.selectedShippingInfoData['selectedVesselCode']['Vessel ID'],
        shippingline:
          !this.selectedShippingInfoData['selectedShippingLine'] ?
            ''
          : this.selectedShippingInfoData['selectedShippingLine'], //'SPLINE',
        shippingagent:
          !this.selectedShippingInfoData['selectedAgentList'] ?
            ''
          : this.selectedShippingInfoData['selectedAgentList']['Agent Code'], //'ATAMA-',
        finaldest:
          !this.selectedShippingInfoData['selectedFinalDestination'] ?
            ''
          : this.selectedShippingInfoData['selectedFinalDestination'], //'MYPKG',
        transhipport:
          !this.selectedShippingInfoData['selectedTranshipmentPort'] ?
            ''
          : this.selectedShippingInfoData['selectedTranshipmentPort']['Port Code'], //'MYSRN',
        dischargeport:
          !this.selectedShippingInfoData['selectedDischargeLoadPort'] ?
            ''
          : this.selectedShippingInfoData['selectedDischargeLoadPort']['Port Code'],
        loadport:
          !this.selectedShippingInfoData['selectedDischargeLoadPort'] ?
            ''
          : this.selectedShippingInfoData['selectedDischargeLoadPort']['Port Code'], //'MYPKG',
        etadate:
          !this.selectedShippingInfoData['ETADateTime'] ?
            ''
          : this.selectedShippingInfoData['ETADateTime'].formatString('yyyy-MM-dd'), //'2022-08-20',
        etatime:
          !this.selectedShippingInfoData['ETADateTime'] ?
            ''
          : this.selectedShippingInfoData['ETADateTime'].formatString('HHmm'), //'1000',
        etddate:
          !this.selectedShippingInfoData['ETDDateTime'] ?
            ''
          : this.selectedShippingInfoData['ETDDateTime'].formatString('yyyy-MM-dd'), //'2022-08-20',
        etdtime:
          !this.selectedShippingInfoData['ETDDateTime'] ?
            ''
          : this.selectedShippingInfoData['ETDDateTime'].formatString('HHmm'), //'1400',
        closingdate:
          !this.selectedShippingInfoData['closeDateTime'] ?
            ''
          : this.selectedShippingInfoData['closeDateTime'].formatString('yyyy-MM-dd'),
        closingtime:
          !this.selectedShippingInfoData['closeDateTime'] ?
            ''
          : this.selectedShippingInfoData['closeDateTime'].formatString('HHmm'),
        freestoragedate:
          !this.selectedShippingInfoData['freeStorageUntil'] ?
            ''
          : this.selectedShippingInfoData['freeStorageUntil'].formatString('yyyy-MM-dd'),
        unstuffingdate:
          !this.selectedShippingInfoData['unstuffingDateTime'] ?
            ''
          : this.selectedShippingInfoData['unstuffingDateTime'].formatString('yyyy-MM-dd'), //'2022-08-19',
        unstuffingtime:
          !this.selectedShippingInfoData['unstuffingDateTime'] ?
            ''
          : this.selectedShippingInfoData['unstuffingDateTime'].formatString('HHmm'), //'1507',
        demurragedate: '',
        prev_refjobno: this.editDetails?.['Prev Job No'] ?? '',
        system: 'TMS', //'TMS',
        dateupdate: '',
        currentstatus: '',
        jobconfirm: '', //'TRUE',
        jobnoconfirmremarks: '',
        actfinishdate: '',
        actfinishtime: '',
        indicator: '', // 'FRMMAIN',
        copyfr: '',
        salesteam_code: '',
        salesperson: '',
        serviceteam_code: '',
        serviceperson: '',
        goodsdescription:
          !this.selectedShippingInfoData['goodsDescription'] ? '' : this.selectedShippingInfoData['goodsDescription'], //'LARGE AND BIG',
        shipmark: !this.selectedShippingInfoData['shippingMarks'] ? '' : this.selectedShippingInfoData['shippingMarks'], // 'N/A',
        lorry:
          !this.selectedLorryInfoData['selectedLorryNoList'] ?
            ''
          : this.selectedLorryInfoData['selectedLorryNoList']['Vehicle Code'],
        drivercode:
          !this.selectedLorryInfoData['selectedDriverCodeList'] ?
            ''
          : this.selectedLorryInfoData['selectedDriverCodeList']['Driver Code'],
        attendantcode:
          !this.selectedLorryInfoData['selectedAttendantList'] ?
            ''
          : this.selectedLorryInfoData['selectedAttendantList']['Attendant Code'],
        attendant2code:
          !this.selectedLorryInfoData['selectedAttendantList2'] ?
            ''
          : this.selectedLorryInfoData['selectedAttendantList2']['Attendant Code'],
        jobcomplete: this.selectedLorryInfoData['isJobCompleted'] ? '1' : '0',
        drvexp1: !this.selectedLorryInfoData['driverEXP'] ? '' : this.selectedLorryInfoData['driverEXP'],
        drvexp2: !this.selectedLorryInfoData['driverTOL'] ? '' : this.selectedLorryInfoData['driverTOL'],
        attnexp1: !this.selectedLorryInfoData['attendantEXP'] ? '' : this.selectedLorryInfoData['attendantEXP'],
        attnexp2: !this.selectedLorryInfoData['attendantTOL'] ? '' : this.selectedLorryInfoData['attendantTOL'],
        attn2exp1: !this.selectedLorryInfoData['attendantEXP2'] ? '' : this.selectedLorryInfoData['attendantEXP2'],
        attn2exp2: !this.selectedLorryInfoData['attendantTOL2'] ? '' : this.selectedLorryInfoData['attendantTOL2'],
        compname: '',
        services: [],
        documents: [],
      };

      postData['consignments'] = this.formatCargoContList();
      postData['documents'] = this.formatDocuments();
      postData['services'] = this.formatServicesList();
      //#region Checking Compulsory fields
      // var compulsoryError = await this.$apiCallV2('/TruckingJob/CheckSaveJSON', postData);

      // check min and max date
      if (this.selectedHeaderInfoData['jobTime'] != null && this.selectedHeaderInfoData['jobTime'] != '') {
        if (this.checkDateTime('Job Time', this.selectedHeaderInfoData['jobTime'])) {
          // Job Time
          this.currentTabIndex = 0;
          this.rMessages.push({ Key: 'job_time', Value: this.jobTimeErr });
          return;
        }
      }
      if (this.selectedHeaderInfoData['reqJobTime'] != null && this.selectedHeaderInfoData['reqJobTime'] != '') {
        if (this.checkDateTime('Required Date/Time', this.selectedHeaderInfoData['reqJobTime'])) {
          // Job Time
          this.currentTabIndex = 0;
          this.rMessages.push({ Key: 'reqJobTime', Value: this.reqdatefrom });
          return;
        }
      }

      //#endregion
      if (compulsoryError && !bypassCompulsory) {
        const temp = this.bilfmtIsDisabled == true ? 'billfmt' : '';
        // this.rMessages = rMessages['error'].filter((e) => { return e['Key'] != 'jobtime' && e['Key'] != 'reqdatefrom' && e['Key'] != temp}).concat(this.rMessages);

        this.rMessages.forEach((r) => (this.errorMessages[r['Key']] = r['Value']));

        const mustFillFields = this.rMessages.filter(
          (r) => r['Key'] != 'shipcallno' && r['Key'] != 'vesselcode' && r['Key'] != 'bkgrefno' && r['Key'] != temp,
        );
        this.saveAlertType = mustFillFields.length > 0 ? 'requiredCantSave' : 'required';
        this.savePopupType = mustFillFields.length > 0 ? 'error' : 'warning';
        this.savePopupTitle =
          mustFillFields.length > 0 ? 'Error Saving Job' : 'The below fields are required but is empty or invalid';
        this.savePopupButtons =
          mustFillFields.length > 0 ? [] : [{ text: 'Save' }, { text: 'Cancel', color: 'lightgray' }];

        if (mustFillFields.length > 0) {
          // Needed fields to be fileed in
          this.saveErrorList = mustFillFields.map((r) => {
            return `${r['Value'].replace(' is Required!', '').trim()}`;
          });
        } else {
          this.saveErrorList = this.rMessages.map((r) => {
            return `${r['Value'].replace(' is Required!', '').trim()}`;
          });
        }

        this.showSavePopup = true;
      } else if (!bypassCompulsory) {
        this.popupError = false;
        this.saveAlertType = 'choiceMode';
        this.savePopupType = 'warning';
        this.savePopupTitle = 'Saving Job';
        this.savePopupButtons = [{ text: 'Save' }, { text: 'Cancel', color: 'lightgray' }];
        this.showSavePopup = true;
      }

      if (save) {
        // If user choose to save the job
        if (this.saveAlertType == 'requiredCantSave' || this.saveAlertType == 'required') {
          postData['jobconfirm'] = false;
        } else {
          postData['jobconfirm'] = this.saveAsConfirm;
        }

        postData['indicator'] = this.selectedUnconfirmReason ? this.selectedUnconfirmReason['Reason'] : '';
        postData['jobnoconfirmremarks'] = this.unconfirmRemarks;

        if (this.ednJsonDetails) {
          postData['eDN'] = '1';
          postData['RequestNo'] = this.ednJsonDetails['orderno'];
          postData['RequestNoTag'] = this.ednJsonDetails['autorequestno'];
          postData['prev_refjobno'] = this.ednJsonDetails['job_no'];
        }

        //console.log(postData)
        this.$fullscreenLoading('Saving Job');
        const saveResult = this.$deepTrimJsonV3(await this.$apiCallV2('/TruckingJob/SaveTMSJob', postData));

        this.$fullscreenLoading();

        if (saveResult[0]['Results'] == 0) {
          const jobNo = JSON.parse(JSON.stringify(this.selectedHeaderInfoData))['jobNo'];
          this.clearPopupData();
          await this.$nextTick();
          if (postData['action'] == 'N') {
            this.sMessages.push('Successfully saved job!');
            this.sMessages.push(`Your job number is ${saveResult[0]['Job No']}`);
            // this.sMessages.push(`<a href="${window.location.origin}/Reports/Job%20Sheet/${saveResult[0]['Job No']}" target="_blank">View</a>`);
            this.sMessages.push(
              `<a href="${window.location.origin}/${this.userParam}/ReportsV2/Job%20Sheet?jobNo=${saveResult[0]['Job No']}" target="_blank">View Job Sheet</a>`,
            );
          } else {
            this.sMessages.push(`Successfully edited job for Job No ${jobNo}!`);
          }

          this.popupTitle = `View Job`;
          this.viewMode = true;
          this.jobCompletedDisabled = true;
          this.driverInfoDisabled = true;
          this.driverCodeRequired = false;
          // this.bilfmtIsDisabled = true;

          this.documentsToDelete = [];
          this.refreshClicked();

          this.viewEditClicked('v', saveResult[0]['Job No'] || jobNo);

          if (this.showEDN) {
            this.selectedeDN = [];
            this.showEDN = false;
          }
        } else {
          this.eMessages.push(saveResult[0]['ErrMsg']);
        }
      }
    },
    checkDateTime: function (field, date) {
      //console.log(date)
      //console.log(field)
      if (date != null && date != '') {
        if (date > this.smallDateTimeMaxDate) {
          const errMsg = `Please select date earlier than ${this.smallDateTimeMaxDate.formatString('yyyy-MM-dd')}`;
          if (field == 'Job Time') {
            this.jobTimeErr = errMsg;
          }
          if (field == 'Required Date/Time') {
            this.reqdatefrom = errMsg;
          }
          return true;
        } else if (date < this.smallDateTimeMinValue) {
          const errMsg = `Please select date later than ${this.smallDateTimeMinValue.formatString('yyyy-MM-dd')}`;
          if (field == 'Job Time') {
            this.jobTimeErr = errMsg;
          }
          if (field == 'Required Date/Time') {
            this.reqdatefrom = errMsg;
          }
          return true;
        }
      }
    },
    savePopupButtonClicked: function (e) {
      switch (e) {
        case 'Yes':
          if (!this.selectedUnconfirmReason) {
            this.popupErrorText = 'Please select your reason';
          } else if (this.selectedUnconfirmReason['Reason'].includes('OTHERS') && !this.unconfirmRemarks) {
            this.popupErrorText = 'Please state your remarks';
          } else {
            this.saveClicked(true, true, true);
            this.showSavePopup = false;
          }
          break;
        case 'Save':
          if (!this.saveAsConfirm) {
            if (!this.selectedUnconfirmReason) {
              this.popupErrorText = 'Please select your reason';
            } else if (this.selectedUnconfirmReason['Reason'].includes('OTHERS') && !this.unconfirmRemarks) {
              this.popupErrorText = 'Please state your remarks';
            } else {
              this.saveClicked(true, true, true);
              this.showSavePopup = false;
            }
          } else {
            this.saveClicked(true, true, true);
            this.showSavePopup = false;
          }
          break;
        default:
          this.showSavePopup = false;
          this.selectedSaveBy = { keyValue: '1', label: 'Confirm Job' };
          break;
      }
    },
    requiredClicked: function (e) {
      if (['shipcallno', 'bkgrefno'].includes(e) && this.currentTabIndex != 4) {
        this.currentTabIndex = 4;
      } else if (this.currentTabIndex != 0) {
        this.currentTabIndex = 0;
      }
      if (e == 'consignments') {
        this.showHideDetailsPopup('Cargo');
      } else {
        this.$nextTick(() => this.$refs[e].setFocus());
      }
    },
    closeClicked: function () {
      this.showDocUpload = false;

      Object.keys(this.selectedHeaderInfoData).forEach((k) => {
        this.selectedHeaderInfoData[k] = null;
      });
      Object.keys(this.selectedGeneralInfoData).forEach((k) => {
        this.selectedGeneralInfoData[k] = null;
      });
      Object.keys(this.selectedShippingInfoData).forEach((k) => {
        this.selectedShippingInfoData[k] = null;
      });
      Object.keys(this.selectedCargoInfoData).forEach((k) => {
        if (k == 'subPkgList' || k == 'contDetailsList') {
          this.selectedCargoInfoData[k] = [];
        } else {
          this.selectedCargoInfoData[k] = null;
        }
      });

      this.selectedDocumentInfoData['docData'] = [];
      this.selectedDocumentInfoData['selectedDoc'] = null;

      this.editDeleteDoc = [];
      this.editAddDoc = [];

      // this.dnListing = [];
      // this.rotListing = [];
      // this.statusTaskListing = [];
      // this.smkListing = [];

      this.currentTabIndex = 1;
      this.$refs.jobformcomponent.resetFile();
    },
    submitVerifiedInfo: function ({
      selectedHeaderInfoData,
      selectedGeneralInfoData,
      selectedShippingInfoData,
      selectedCargoInfoData,
      _subPackageList,
      containerList,
    }) {
      this.showDocUpload = false;
      this.selectedHeaderInfoData = selectedHeaderInfoData;
      this.selectedGeneralInfoData = selectedGeneralInfoData;
      this.selectedShippingInfoData = selectedShippingInfoData;
      this.selectedCargoInfoData = selectedCargoInfoData;

      this.selectedHeaderInfoData['jobNo'] = '**********';
      this.selectedHeaderInfoData['jobStatus'] = 'New';
      this.selectedHeaderInfoData['jobTime'] = new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate(),
        new Date().getHours(),
        new Date().getMinutes(),
        0,
        0,
        0,
      );

      this.$nextTick(() => {
        containerList.forEach((element) => {
          this.selectedCargoInfoData['contDetailsList'].push({
            'Cont #': element.contNo,
            'Seal No': element.sealNo,
            'Cont Size': element.contSize ? element.contSize : '',
            'Cont Type': element.contType ? element.contType.trim() : '',
            Status: element.status ? element.status.trim() : '',
            'Handling Type': element.handlingType ? element.handlingType.trim() : '',
            'Delivery Type': element.deliveryType ? element.deliveryType.trim() : '',
            Remarks: element.remarks,
            'From API': element.fromAPI,
          });
        });
      });
      this.$refs.jobformcomponent.resetFile();
      this.showPopup = true;
    },

    // #region Delete Popup
    deletePopupButtonClicked: async function (e) {
      switch (e) {
        case 'Delete':
          if (!this.deleteRemarks) {
            this.deleteRemarksError = 'Please enter your remarks to delete';
          } else {
            this.showDeletePopup = false;

            this.$fullscreenLoading('Deleting Job');
            const deleteResult = await this.$apiCallV2('/TruckingJob/SaveTMSJob', {
              job_no: this.selectedJob[0]['Job No'],
              job_type: 'D',
              system: 'TMS',
              action: 'D',
              deleteremarks: this.deleteRemarks,
              ErrMsg: '',
              Results: 0,
            });
            this.$fullscreenLoading();

            this.deleteRemarksError = '';
            this.deleteRemarks = '';

            if (deleteResult[0]['Results'] == 0) {
              this.refreshClicked();

              await this.$showAlert(
                'success',
                'Job Deleted',
                `Job # ${this.selectedJob[0]['Job No']} has been successfully deleted.`,
              );

              this.selectedJob = [];
            } else {
              await this.$showAlert(
                'error',
                'Job Not Deleted',
                `Job # ${this.selectedJob[0]['Job No']} is not deleted.<br>Error: ${deleteResult[0]['ErrMsg']}`,
              );
            }
          }
          break;
        case 'Cancel':
          this.deleteRemarks = '';
          this.deleteRemarksError = '';

          this.showDeletePopup = false;
          break;
      }
    },
    //#endregion

    // #region for document
    addDoc: function () {
      this.documentInfoData['updatePopup'] = true;
    },

    documentUpdate: async function (e) {
      const editDetails = this.editDocumentsList;
      const details = e['details'];
      const hasStatus = details['statuses'];

      const allData = [];

      if (hasStatus == 'N/A') {
        // Doc without status
        const att = Array.from(details['attachment']);

        if (editDetails.length > 0) {
          // For edit
          if (details['docRef'] != editDetails[0]['Doc Ref #']) {
            // Changes in docref#
            editDetails.forEach((ed) => {
              // let index = this.selectedDocumentInfoData['docData'].indexOf(ed);
              const index = this.selectedDocumentInfoData['docData'].findIndex(
                (d) => JSON.stringify(d) == JSON.stringify(ed),
              );
              ed['Action'] = 'E';
              ed['Doc Ref #'] = details['docRef'];
              ed['ID'] = `${details['docCode']}-${details['docRef']}`;

              this.selectedDocumentInfoData['docData'].splice(index, 1, ed);
            });
          }
        }

        const fixedData = {
          'Doc Code': details['docCode'],
          'Doc Ref #': details['docRef'],
          Attachment: '',
          Remarks: '',
          Status: 'N/A',
          'Start Date': 'N/A',
          'Received Date': 'N/A',
          'Due Date': 'N/A',
          'Assign To': 'N/A',
          Sets: 'N/A',
          Page: 'N/A',

          documentguid: '',
          statusseqno: '',
          ID: `${details['docCode']}-${details['docRef']}`,
          fileAtt: '',
          filetype: '',
          Action: 'N',
        };

        if (att.length > 0) {
          // if has attachment
          for (const a of att) {
            const attBase64 = await a.toBase64(true);
            allData.push({
              'Doc Code': details['docCode'],
              'Doc Ref #': details['docRef'],
              Attachment: a.name,
              Remarks: '',
              Status: 'N/A',
              'Start Date': 'N/A',
              'Received Date': 'N/A',
              'Due Date': 'N/A',
              'Assign To': 'N/A',
              Sets: 'N/A',
              Page: 'N/A',

              documentguid: '',
              statusseqno: '',
              ID: `${details['docCode']}-${details['docRef']}`,
              fileAtt: attBase64,
              filetype: a.type,
              Action: 'N',
            });
          }
        } else if (editDetails.length == 0 && details) {
          // without attachment and not edit ?? !editDetails
          allData.push(fixedData);
        }

        const temp = this.selectedDocumentInfoData['docData'];
        this.selectedDocumentInfoData['docData'] = [];
        this.$nextTick(() => {
          this.selectedDocumentInfoData['docData'] = temp.concat(allData);
        });
      } else {
        // Doc with status
        const statusChecked = hasStatus.filter((s) => s['checked']);
        for (const s of statusChecked) {
          allData.push({
            'Doc Code': details['docCode'],
            'Doc Ref #': details['docRef'],
            Attachment: s['attachment'] ? s['attachment'][0].name : '',
            Remarks: s['remarks'],
            Status: s['docStatus'],
            'Start Date': s['start'] ? s['start'].formatString('yyyy-MM-dd HH:mm') : '',
            'Received Date': s['received'] ? s['received'].formatString('yyyy-MM-dd HH:mm') : '',
            'Due Date': s['due'] ? s['due'].formatString('yyyy-MM-dd HH:mm') : '',
            'Assign To': s['assignTo'],
            Sets: s['sets'],
            Page: s['page'],

            documentguid: '',
            statusseqno: '',
            ID: `${details['docCode']}-${details['docRef']}`,
            fileAtt: s['attachment'] ? await s['attachment'][0].toBase64(true) : '',
            filetype: s['attachment'] ? s['attachment'][0].type : '',
            Action: 'N',
          });
        }

        if (editDetails.length == 0) {
          // For new
          const temp = this.selectedDocumentInfoData['docData'];
          this.selectedDocumentInfoData['docData'] = [];
          //if no status check then add a blank record to allData.
          if (allData.length == 0) {
            const fixedData = {
              'Doc Code': details['docCode'],
              'Doc Ref #': details['docRef'],
              Attachment: '',
              Remarks: '',
              Status: '',
              'Start Date': '',
              'Received Date': '',
              'Due Date': '',
              'Assign To': '',
              Sets: '',
              Page: '0',

              documentguid: '',
              statusseqno: '',
              ID: `${details['docCode']}-${details['docRef']}`,
              fileAtt: '',
              filetype: '',
              Action: 'N',
            };
            allData.push(fixedData);
          }

          this.$nextTick(() => {
            this.selectedDocumentInfoData['docData'] = temp.concat(allData);
          });
        } else {
          // For edit
          //find if there is any records that have empty status. If yes then add that record into allData
          const emptyStatus = editDetails.find((f) => f['Status'] === '');
          if (emptyStatus) {
            const fixedData = {
              'Doc Code': emptyStatus['docCode'],
              'Doc Ref #': emptyStatus['docRef'],
              Attachment: '',
              Remarks: '',
              Status: '',
              'Start Date': '',
              'Received Date': '',
              'Due Date': '',
              'Assign To': '',
              Sets: '',
              Page: '0',

              documentguid: '',
              statusseqno: '',
              ID: `${emptyStatus['docCode']}-${emptyStatus['docRef']}`,
              fileAtt: '',
              filetype: '',
              Action: 'N',
            };
            allData.push(fixedData);
          }

          const oldStatus = editDetails.map((ed) => ed['Status']);
          const newStatus = allData.map((ad) => ad['Status']);

          const toDelete = oldStatus.filter((o) => !newStatus.includes(o));
          const toAdd = newStatus.filter((n) => !oldStatus.includes(n));
          const toCheck = newStatus.filter((n) => oldStatus.includes(n));

          toDelete.forEach((d) => {
            const deleting = editDetails.find((ed) => ed['Status'] == d);
            // let index = this.selectedDocumentInfoData['docData'].indexOf(deleting);
            const index = this.selectedDocumentInfoData['docData'].findIndex(
              (d) => JSON.stringify(d) == JSON.stringify(deleting),
            );

            this.selectedDocumentInfoData['docData'].splice(index, 1);
            deleting['Action'] = 'D';
            this.documentsToDelete.push(deleting);
          });
          toAdd.forEach((a) => {
            const adding = allData.find((ad) => ad['Status'] == a);
            this.selectedDocumentInfoData['docData'].push(adding);
          });
          toCheck.forEach((c) => {
            const oldData = editDetails.find((ed) => ed['Status'] == c);
            const newData = allData.find((ed) => ed['Status'] == c);
            // let index = this.selectedDocumentInfoData['docData'].indexOf(oldData);
            const index = this.selectedDocumentInfoData['docData'].findIndex(
              (d) => JSON.stringify(d) == JSON.stringify(oldData),
            );

            Object.keys(oldData).forEach((k) => {
              if (oldData[k] == null) {
                oldData[k] = '';
              }
            });

            if (oldData['Sets'] == 0) {
              oldData['Sets'] = '';
            }
            if (oldData['Page'] == 0) {
              oldData['Page'] = '';
            }

            let hasChanges = false;

            if (newData['Attachment']) {
              hasChanges = true;
            }

            if (
              newData['Start Date'] != oldData['Start Date'] ||
              newData['Received Date'] != oldData['Received Date'] ||
              newData['Due Date'] != oldData['Due Date'] ||
              newData['Remarks'] != oldData['Remarks'] ||
              newData['Assign To'] != oldData['Assign To'] ||
              newData['Sets'] != oldData['Sets'] ||
              newData['Page'] != oldData['Page']
            ) {
              oldData['Start Date'] = newData['Start Date'];
              oldData['Received Date'] = newData['Received Date'];
              oldData['Due Date'] = newData['Due Date'];
              oldData['Remarks'] = newData['Remarks'];
              oldData['Assign To'] = newData['Assign To'];
              oldData['Sets'] = newData['Sets'];
              oldData['Page'] = newData['Page'];

              hasChanges = true;
            }

            if (hasChanges) {
              oldData['Action'] = oldData['Action'] == 'N' ? oldData['Action'] : 'E';
              this.selectedDocumentInfoData['docData'].splice(index, 1, oldData);
            }

            if (details['docRef'] != editDetails[0]['Doc Ref #']) {
              // Changes in docref#
              editDetails.forEach((ed) => {
                const index = this.selectedDocumentInfoData['docData'].findIndex(
                  (d) => JSON.stringify(d) == JSON.stringify(ed),
                );
                ed['Action'] = ed['Action'] == 'N' ? ed['Action'] : 'E';
                ed['Doc Ref #'] = details['docRef'];
                ed['ID'] = `${details['docCode']}-${details['docRef']}`;

                this.selectedDocumentInfoData['docData'].splice(index, 1, ed);
              });
            }

            oldData['Attachment'] = newData['Attachment'];
            oldData['fileAtt'] = newData['fileAtt'];
            oldData['filetype'] = newData['filetype'];
          });

          const temp = this.selectedDocumentInfoData['docData'];
          this.selectedDocumentInfoData['docData'] = [];
          this.$nextTick(() => (this.selectedDocumentInfoData['docData'] = temp));
        }
      }

      this.selectedDocumentInfoData['selectedDoc'] = [];
    },

    deleteDoc: async function () {
      const deleteAll = [];
      const uniqueSelected = [
        ...new Set(
          this.selectedDocumentInfoData['selectedDoc'].map((s) => {
            return s['ID'];
          }),
        ),
      ];

      if (uniqueSelected.length == 0) {
        this.showDefaultPopup('error', 'No document selected', 'Please select at least one document to delete');
        return;
      }

      uniqueSelected.forEach((u) => {
        const selectedCount = this.selectedDocumentInfoData['selectedDoc'].filter((s) => {
          return s['ID'] == u;
        });
        const allCount = this.selectedDocumentInfoData['docData'].filter((s) => {
          return s['ID'] == u;
        });

        const deleteData = {
          docCode: selectedCount[0]['Doc Code'],
          refNo: selectedCount[0]['Doc Ref #'],
          allItems: allCount,
          selectedItems: selectedCount,
          allSelected: selectedCount.length == allCount.length,
        };
        deleteAll.push(deleteData);
      });

      if (deleteAll.length > 0) {
        for (const d of deleteAll) {
          this.deleteItems = d;

          const selectedNames = d['selectedItems']
            .map((s) => {
              return s['Attachment'];
            })
            .join(`<br/>`);
          const deleteResult = await this.$showAlert(
            'warning',
            'Deleting Doc Code',
            `You are about to delete ${d['selectedItems'].length} of ${d['allItems'].length} attachments for<br />Doc Code ${d['docCode']}<br />(Doc Ref #: ${d['refNo']})<br /><br />Selected Files:<br />${selectedNames}`,
            [
              {
                text: `Delete ${d['selectedItems'].length} of ${d['allItems'].length} Attachments for<br />Doc Code ${d['docCode']}<br />(Doc Ref #: ${d['refNo']})`,
                textColor: 'white',
                color: 'rgb(255, 167, 58)',
              },
              {
                text: `Delete All Attachments for<br />Doc Code ${d['docCode']}<br />(Doc Ref #: ${d['refNo']})`,
                textColor: 'white',
                color: 'red',
              },
              { text: 'Cancel', textColor: 'white', color: '#aaa' },
            ],
          );

          if (deleteResult == 'Cancel') {
            return;
          } else if (deleteResult.includes('Delete All')) {
            this.deleteItems['allItems'].forEach((d) => {
              // var index = this.selectedDocumentInfoData['docData'].indexOf(d);
              const index = this.selectedDocumentInfoData['docData'].findIndex(
                (dd) => JSON.stringify(dd) == JSON.stringify(d),
              );

              if (d['documentguid']) {
                // To send to API for delete
                d['Action'] = 'D';
                this.documentsToDelete.push(d);
              }

              this.selectedDocumentInfoData['docData'].splice(index, 1);
            });
          } else {
            this.deleteItems['selectedItems'].forEach((d) => {
              // var index = this.selectedDocumentInfoData['docData'].indexOf(d);
              const index = this.selectedDocumentInfoData['docData'].findIndex(
                (dd) => JSON.stringify(dd) == JSON.stringify(d),
              );

              if (d['documentguid']) {
                // To send to API for delete
                d['Action'] = 'D';
                this.documentsToDelete.push(d);
              }

              this.selectedDocumentInfoData['docData'].splice(index, 1);
            });
          }
          const temp = this.selectedDocumentInfoData['docData'];
          this.selectedDocumentInfoData['docData'] = [];
          this.$nextTick(() => {
            this.selectedDocumentInfoData['docData'] = temp;
          });

          this.selectedDocumentInfoData['selectedDoc'] = [];
        }
      }
    },

    documentsDoubleClicked: function (e) {
      window.open(e['oriObj']['AttImage'], '_blank');
      // if (e['Attachment']) {
      //   var base64 = e['oriObj']['AttImage'];
      //   var byteChar = atob(base64);
      //   var byteArray = new Array(byteChar.length);
      //   for (var i = 0; i < byteChar.length; i++) {
      //     byteArray[i] = byteChar.charCodeAt(i);
      //   }
      //   var u8Array = new Uint8Array(byteArray);

      //   var blob = new Blob([ u8Array ], { type: e['oriObj']['AttImageType'].includes('/') ? e['oriObj']['AttImageType'] : '' });
      //   console.log(blob);

      //   var url = window.URL.createObjectURL(blob, { oneTimeOnly: false });
      //   console.log(url);
      //   var link = document.createElement('a');
      //   link.target = '_blank';
      //   if (e['oriObj']['AttImageType'].includes('/')) {
      //     link.href = url;
      //   } else {
      //     link.download = `myfile${e['oriObj']['AttImageType'].includes('.') ? '' : '.'}${e['oriObj']['AttImageType']}`;
      //   }
      //   // console.log(link);
      //   link.click();
      // } else {
      //   this.$alert.showAlert('No attachment', 'No attachment for the selected document', 'error');
      // }
    },

    editDoc: function () {
      if (this.selectedDocumentInfoData['selectedDoc'].length == 0) {
        this.showDefaultPopup('error', 'No document selected', 'Please select at least one document to edit');
      } else {
        const uniqueID = [...new Set(this.selectedDocumentInfoData['selectedDoc'].map((s) => s['ID']))];
        if (uniqueID.length > 1) {
          this.showDefaultPopup(
            'error',
            'Multiple Document Selected',
            'Please select at most one unique document to edit',
          );
        } else {
          this.editDocumentsList = this.selectedDocumentInfoData['docData'].filter((d) => d['ID'] == uniqueID[0]);
          this.documentInfoData['updatePopup'] = true;
        }
      }
    },
    //#endregion

    tooltip: function (show, text) {
      this.showTooltip = show;
      this.tooltipText = text;
    },
    // validation: function(btn) {
    //   if (btn == 'No' || btn == 'Cancel') {
    //     popup.hide();
    //   } else if (btn == 'Yes' || btn == 'Save') {
    //     if (!this.selectedUnconfirmReason) {
    //       this.popupErrorText = 'Please select your reason';
    //     } else if (this.selectedUnconfirmReason['Reason'].split('-')[0].trim() == 'OTHERS' && !this.unconfirmRemarks) {
    //       this.popupErrorText = 'Please enter your remarks';
    //     } else {
    //       this.popupErrorText = '';
    //     }
    //   }
    // },
    popupValidation: function () {
      if (!this.selectedUnconfirmReason) {
        this.popupError = true;
      } else if (this.selectedUnconfirmReason['Reason'].split('-')[0].trim() == 'OTHERS' && !this.unconfirmRemarks) {
        this.popupError = true;
      } else {
        this.popupError = false;
      }
    },
    lorryNoChanged: async function (e, old) {
      if (e) {
        this.driverInfoDisabled = false;
        this.driverCodeRequired = true;
        this.selectedLorryInfoData['driverEXP'] = this.$currencyFormat('0');
        this.selectedLorryInfoData['driverTOL'] = this.$currencyFormat('0');
        this.selectedLorryInfoData['attendantEXP'] = this.$currencyFormat('0');
        this.selectedLorryInfoData['attendantTOL'] = this.$currencyFormat('0');
        this.selectedLorryInfoData['attendantEXP2'] = this.$currencyFormat('0');
        this.selectedLorryInfoData['attendantTOL2'] = this.$currencyFormat('0');
      } else {
        if (this.jobCompletedDisabled) {
          const confirmEdit = await this.$showAlert(
            'warning',
            'Completed Job',
            `Job has been marked as completed. Are you sure you want to edit Lorry No?`,
            [{ text: 'Yes' }, { text: 'No' }],
          );
          if (confirmEdit === 'No') {
            this.selectedLorryInfoData['selectedLorryNoList'] = old;
            return;
          }
        }
        this.driverInfoDisabled = true;
        this.driverCodeRequired = false;
        this.selectedLorryInfoData['selectedDriverCodeList'] = null;
        this.selectedLorryInfoData['selectedAttendantList'] = null;
        this.selectedLorryInfoData['selectedAttendantList2'] = null;
        this.selectedLorryInfoData['driverEXP'] = '';
        this.selectedLorryInfoData['driverTOL'] = '';
        this.selectedLorryInfoData['attendantEXP'] = '';
        this.selectedLorryInfoData['attendantTOL'] = '';
        this.selectedLorryInfoData['attendantEXP2'] = '';
        this.selectedLorryInfoData['attendantTOL2'] = '';

        // this.selectedLorryInfoData['isJobCompleted'] = false;
        // this.jobCompletedDisabled = false;
      }
    },
    lorryNoFocusChanged: function (e) {
      if (e == false) {
        if (
          this.selectedLorryInfoData['selectedLorryNoList'] == '' ||
          !this.selectedLorryInfoData['selectedLorryNoList']
        ) {
          this.lorryNoChanged(this.selectedLorryInfoData['selectedLorryNoList'], this.selectedLorry);
        } else {
          this.selectedLorry = this.selectedLorryInfoData['selectedLorryNoList'];
        }
      }
    },
    // driverCodeChanged: function(e) {
    // 	if (!e) {
    //     this.selectedLorryInfoData['driverEXP'] = '0';
    //     this.selectedLorryInfoData['driverTOL'] = '0';
    //     return;
    //   }
    // 	// this.selectedLorryInfoData['selectedDriverNameList'] = e['Driver Name'] ? e['Driver Name'].trim() : '';
    // 	// this.selectedLorryInfoData['selectedDriverICList'] = e['Driver IC'] ? e['Driver IC'].trim() : '';
    // },
    // attendantChanged: function(e) {
    //   if (!e) {
    //     this.selectedLorryInfoData['attendantEXP'] = '0';
    //     this.selectedLorryInfoData['attendantTOL'] = '0';
    //   }
    // },
    // attendant2Changed: function(e) {
    //   if (!e) {
    //     this.selectedLorryInfoData['attendantEXP2'] = '0';
    //     this.selectedLorryInfoData['attendantTOL2'] = '0';
    //   }
    // },
    pickUpSelected: function (e) {
      this.selectedHeaderInfoData['tariffAreaPickup'] = e['Tariff Area'];
      this.selectedHeaderInfoData['zonesPickup'] = e['Zones'];
      this.selectedHeaderInfoData['pickUpAddress'] =
        `${e['Company']}\n${e['Add1'] || ''}\n${e['Add2'] || ''}\n${e['Add3'] || ''}\n${e['Add4'] || ''}`;
    },
    transit1Selected: function (e) {
      this.selectedHeaderInfoData['tariffAreaTransit1'] = e['Tariff Area'];
      this.selectedHeaderInfoData['zonesTransit1'] = e['Zones'];
      this.selectedHeaderInfoData['transit1Address'] =
        `${e['Company']}\n${e['Add1'] || ''}\n${e['Add2'] || ''}\n${e['Add3'] || ''}\n${e['Add4'] || ''}`;
    },
    transit2Selected: function (e) {
      this.selectedHeaderInfoData['tariffAreaTransit2'] = e['Tariff Area'];
      this.selectedHeaderInfoData['zonesTransit2'] = e['Zones'];
      this.selectedHeaderInfoData['transit2Address'] =
        `${e['Company']}\n${e['Add1'] || ''}\n${e['Add2'] || ''}\n${e['Add3'] || ''}\n${e['Add4'] || ''}`;
    },
    dropOffSelected: function (e) {
      this.selectedHeaderInfoData['tariffAreaDropoff'] = e['Tariff Area'];
      this.selectedHeaderInfoData['zonesDropoff'] = e['Zones'];
      this.selectedHeaderInfoData['dropOffAddress'] =
        `${e['Company']}\n${e['Add1'] || ''}\n${e['Add2'] || ''}\n${e['Add3'] || ''}\n${e['Add4'] || ''}`;
    },
    jobTypeSelected: function (e) {
      this.temp = e['Job Type'];

      if (this.temp == 'I') {
        this.tabsItems[4]['disabled'] = false;
        this.dischargePortMode = true;
        this.unstuffviewmode = false;
        this.loadPortMandatory = true;
        this.dischargePortMandatory = false;
        this.changeFieldCaption = false;
      } else if (this.temp == 'E') {
        this.tabsItems[4]['disabled'] = false;
        this.dischargePortMode = false;
        this.unstuffviewmode = true;
        this.loadPortMandatory = true;
        this.dischargePortMandatory = true;
        this.changeFieldCaption = false;
      } else if (
        this.temp == 'R' ||
        this.temp == 'E-CB HDG' ||
        this.temp == 'L-BKH' ||
        this.temp == 'L-SA' ||
        this.temp == 'I-CB' ||
        this.temp == 'B'
      ) {
        this.tabsItems[4]['disabled'] = false;
        this.changeFieldCaption = true;

        if (!(this.temp == 'I-CB')) {
          this.dischargePortMode = true;
          this.unstuffviewmode = false;
          this.loadPortMandatory = false;
          this.dischargePortMandatory = false;
        } else {
          this.dischargePortMode = false;
          this.unstuffviewmode = false;
          this.loadPortMandatory = false;
          this.dischargePortMandatory = false;
        }
      } else {
        this.tabsItems[4]['disabled'] = true;
        this.dischargePortMode = false;
        this.unstuffviewmode = false;
        this.loadPortMandatory = false;
        this.dischargePortMandatory = false;
        this.changeFieldCaption = false;
      }
    },
    shipCallNoSelected: function (_e) {
      //   this.selectedShippingInfoData['voyNo'] = e['Voy No'];
      //   var vessel = this.shippingInfoData['vesselIDList'].filter((v) => { return v['Vessel ID'].trim() == e['Vessel ID'].trim() });
      //   this.selectedShippingInfoData['selectedVesselID'] = vessel[0];
      //   if (this.pageType == 'Import') {
      //     this.selectedShippingInfoData['polETA'] = this.$dateTime.getDate(e['ETA'], 'yyyy-MM-dd HHmm');
      //   } else if (e['ETD']) {
      //     this.selectedShippingInfoData['polETD'] = this.$dateTime.getDate(e['ETD'], 'yyyy-MM-dd HHmm');
      //   } else {
      //     this.selectedShippingInfoData['polETD'] = this.$dateTime.getDate(e['ETA'], 'yyyy-MM-dd HHmm');
      //   }
    },
    appendStatusIcon: function (list) {
      const allStatus = list.sort((a, b) => {
        return a['#'] < b['#'] ? 1 : -1;
      });
      const statusWithIcons = allStatus.map((s) => {
        switch (s['Progress']) {
          case 'Completed':
            return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/StatusComplete.png' }, s);
          case 'Due':
            return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/StatusDue.png' }, s);
          case 'Due In 2 days':
            return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/StatusDueIn2Days.png' }, s);
          case 'Pending':
            return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/StatusPending.png' }, s);
          case 'Cancelled':
            return Object.assign({ '': 'https://dsmlib.logistics.systems/Icons/StatusCancelled.png' }, s);
          default:
            return Object.assign({ '': '' }, s);
        }
      });

      return statusWithIcons;
    },
    addDeliveryNote: function () {
      const route = this.$router.resolve(
        `/Home/${this.$route.params['user']}/FreightForwarding/Delivery%20Notes?jobNo=${this.selectedJob[0]['Job No'].trim()}&action=N`,
      );
      window.open(route.href, '_blank');
    },
    addROT: async function () {
      this.$fullscreenLoading('Verifying');
      const validate = await this.$apiCallV2(`/ROT/ValidateJobsForROT?jobno=${this.selectedJob[0]['Job No'].trim()}`);
      this.$fullscreenLoading();

      if (validate[0]['Results'] == 1) {
        this.rotVerifyPopup = true;
        this.rotVerifyError = validate[0]['ErrMsg'];
      } else {
        const route = this.$router.resolve(
          `/Home/${this.$route.params['user']}/FreightForwarding/ROT?jobNo=${this.selectedJob[0]['Job No'].trim()}`,
        );
        window.open(route.href, '_blank');
      }
    },
    showDefaultPopup: function (type, title, content) {
      this.defaultPopup['show'] = true;
      this.defaultPopup['type'] = type;
      this.defaultPopup['title'] = title;
      this.defaultPopup['content'] = content;
    },
    defaultPopupButtonClicked: function (e) {
      if (e == 'OK') {
        this.defaultPopup['show'] = false;
      }
    },

    //#region Print Popup
    printPopupButtonClicked: function (text) {
      switch (text) {
        case 'Print':
          if (!this.printDOChecked && !this.printGatePassChecked) {
            this.$showAlert('error', 'Report Type not Selected', 'Please select at least one report type to print');
          } else {
            this.reportData['printDateTime'] = new Date().formatString('dd/MM/yyyy HH:mm:ss');
            const selectedPickup = this.headerInfoData['pickup'].getSelected(
              this.reportData['Pick Up'],
              'Destination Code',
            );
            const selectedDropoff = this.headerInfoData['dropoff'].getSelected(
              this.reportData['Drop Off'],
              'Destination Code',
            );

            if (selectedPickup) {
              const details = [
                selectedPickup['Company'],
                selectedPickup['Add1'],
                selectedPickup['Add2'],
                selectedPickup['Add3'],
                selectedPickup['Add4'],
                `${selectedPickup['PostCode']} ${selectedPickup['City']}`,
                `${selectedPickup['State']} ${selectedPickup['Country']}`,
              ].filter((d) => d);
              this.reportData['Pick Up Details'] = details.join('\n');
            }
            if (selectedDropoff) {
              const details = [
                selectedDropoff['Company'],
                selectedDropoff['Add1'],
                selectedDropoff['Add2'],
                selectedDropoff['Add3'],
                selectedDropoff['Add4'],
                `${selectedDropoff['PostCode']} ${selectedDropoff['City']}`,
                `${selectedDropoff['State']} ${selectedDropoff['Country']}`,
              ].filter((d) => d);
              this.reportData['Drop Off Details'] = details.join('\n');
            }

            this.reportData['consignments'] = this.reportData['JobConsignment'].map((c, i) => {
              const prodCode =
                c['Product'] ?
                  c['Product'].split('(')[0] ?
                    c['Product'].split('(')[0].trim()
                  : ''
                : '';
              return {
                no: i + 1,
                prodCode: prodCode,
                prodDesc:
                  c['Product'] ?
                    this.cargoInfoData['cargoProductList'].getSelected(prodCode, 'Product Code') ?
                      this.cargoInfoData['cargoProductList'].getSelected(prodCode, 'Product Code')['Description']
                    : ''
                  : '',
                qty: `${c['Qty'] ? this.$currencyFormat(c['Qty']) : this.$currencyFormat(0)} ${c['Package']}`,
                grossWeight: `${c['Total KG'] ? this.$currencyFormat(c['Total KG']) : this.$currencyFormat(0)}`,
                measurements: `${c['Total M3'] ? this.$currencyFormat(c['Total M3']) : this.$currencyFormat(0)}`,

                //#region For Gate Pass
                cnno: c['Consignment No'] || '',
                customer: this.reportData['Customer'] || '',
                from: selectedPickup ? selectedPickup['Tariff Area'] : '',
                to: selectedDropoff ? selectedDropoff['Tariff Area'] : '',
                consignor: selectedPickup ? selectedPickup['Company'] : '',
                consignee: selectedDropoff ? selectedDropoff['Company'] : '',
                remarks: c['Remarks'] || '',
                qtygp: `${c['Qty'] ? Number(c['Qty']).toFixed(0) : '0'} ${c['Package']}`,
                //#endregion
              };
            });
            this.reportData['header'] = this.loginDetails['CoHeaderOpr'];
            this.reportData['vehicleCode'] =
              this.reportData['Trip'] ? this.reportData['Trip']['VehicleCode'] || '' : '';

            //#region For Gate Pass
            this.reportData['compName'] = this.loginDetails['CoName'];
            this.reportData['TMSJINO'] = this.reportData['Trip'] ? this.reportData['Trip']['TMSJINo'] || '' : '';
            this.reportData['printDate'] = new Date().formatString('dd/MM/yyyy');
            this.reportData['printTime'] = new Date().formatString('HH:mm:ss');
            this.reportData['preparedBy'] = this.reportData['Trip'] ? this.reportData['Trip']['UserID'] || '' : '';
            const vehicleDetails = this.LorryInfoTabData['lorryNoList'].getSelected(
              this.reportData['vehicleCode'],
              'Vehicle Code',
            );
            if (vehicleDetails) {
              this.reportData['vehicleNumber'] = vehicleDetails['Vehicle Number'];
            }
            const driverDetails = this.LorryInfoTabData['driverCodeList'].getSelected(
              this.reportData['Trip'] ? this.reportData['Trip']['Driver'] || '' : '',
              'Driver Code',
            );
            if (driverDetails) {
              this.reportData['driverCode'] = driverDetails['Driver Code'];
              this.reportData['driverName'] = driverDetails['Driver Name'];
            }
            //#endregion

            this.printPopup = false;
            this.showReportPopup = true;
          }
          break;
        case 'Cancel':
          this.printPopup = false;
          this.showReportPopup = false;
          break;
      }
    },
    //#endregion
    reasonFocusChanged: function (e) {
      this.reasonOpen = e;
    },
    geteRequestJson: async function () {
      this.$fullscreenLoading('Getting Details');
      const fullJSON = this.$deepTrimJsonV3(
        await this.$apiCallV2(`/TruckingJob/GeteJobRequestJSON?autorequestno=${this.selectedeDN[0]['AutoRequestNo']}`),
      );
      this.$fullscreenLoading();

      return fullJSON;
    },

    //#region eDN
    refreshDNClicked: async function () {
      if (this.rangeDNChceked && (!this.dnRangeFrom || !this.dnRangeTo)) {
        if (!this.dnRangeFrom) {
          this.dnRangeFromError = 'From date time is required!';
        }
        if (!this.dnRangeToError) {
          this.dnRangeToError = 'To date time is required!';
        }
        return;
      }

      this.filterOpen = false;
      this.ednListing = [];

      this.$fullscreenLoading('Getting DN Listing');
      if (this.allDNChecked) {
        this.ednListing = this.$deepTrimJsonV3(await this.$apiCallV2(`/TruckingJob/GetBookingRequest`));
      } else {
        this.ednListing = this.$deepTrimJsonV3(
          await this.$apiCallV2(
            `/TruckingJob/GetBookingRequest?from=${this.dnRangeFrom ? this.dnRangeFrom.formatString('yyyy-MM-dd') : ''}&to=${this.dnRangeTo ? this.dnRangeTo.formatString('yyyy-MM-dd') : ''}`,
          ),
        );
      }
      this.$fullscreenLoading();

      this.eDNLastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm');
    },
    dnCheckboxClicked: function (key) {
      this.allDNChecked = false;
      this.rangeDNChceked = false;

      this.$nextTick(() => (this[key] = true));
    },
    eDNAccept: async function () {
      this.ednJsonDetails = await this.geteRequestJson();
      this.ednJsonDetails['autorequestno'] = this.selectedeDN[0]['AutoRequestNo'];
      if (Array.isArray(this.ednJsonDetails)) {
        this.ednJsonDetails = null;
        this.$showAlert('error', 'No details', `${this.ednJsonDetails[0]['ErrMsg']}`);
      } else {
        this.eDNRequestNo = this.selectedeDN[0]['AutoRequestNo'];
        this.eDNSections = [
          {
            name: 'Quick Entry',
            verifyData: [
              {
                column: 'Lorry No',
                detected: `${this.ednJsonDetails['lorryno']}`,
                submittedList: this.LorryInfoTabData['lorryNoList'],
                submittedKey: 'Vehicle Code',
                submittedDesc: 'Vehicle Number',
                displayKey: 'Description',
                historyKey: 'lorryNo',
                isRequired: false,
                compareIndex: 0,
              },
              {
                column: 'Driver Name',
                detected: `${this.ednJsonDetails['drivername']}`,
                submittedList: this.LorryInfoTabData['driverCodeList'],
                submittedKey: 'Driver Code',
                submittedDesc: 'Driver Name',
                displayKey: 'Description',
                historyKey: 'driverName',
                isRequired: false,
                compareIndex: 0,
              },
            ],
          },
          {
            name: 'Header Info',
            verifyData: [
              {
                column: 'Customer',
                detected: `${this.ednJsonDetails['ShprCsgn']}
${this.ednJsonDetails['ShprCsgnName']}`,
                submittedList: this.headerInfoData['shipperConsignee'],
                submittedKey: 'Customer Code',
                submittedDesc: 'Customer Name',
                displayKey: 'Description',
                historyKey: 'shipperConsignee',
                isRequired: true,
                compareIndex: 1,
              },
              {
                column: 'Bill To',
                detected: `${this.ednJsonDetails['billto']}
${this.ednJsonDetails['BillToName']}`,
                submittedList: this.headerInfoData['billTo'],
                submittedKey: 'Customer Code',
                submittedDesc: 'Customer Name',
                displayKey: 'Description',
                historyKey: 'billTo',
                isRequired: true,
                compareIndex: 1,
              },
            ],
          },
          {
            name: 'Send To / Cargo Info',
            verifyData: [
              {
                column: 'Pick Up',
                detected: `${this.ednJsonDetails['pickup']}
${this.ednJsonDetails['pickup_company']}
${this.ednJsonDetails['pickup_addr1']}
${this.ednJsonDetails['pickup_addr2']}
${this.ednJsonDetails['pickup_addr3']}
${this.ednJsonDetails['pickup_postcode']} ${this.ednJsonDetails['pickup_city']}
${this.ednJsonDetails['pickup_state']} ${this.ednJsonDetails['pickup_country']}
Tel: ${this.ednJsonDetails['pickup_phone']}`,
                submittedList: this.headerInfoData['pickup'],
                submittedKey: 'Destination Code',
                submittedDesc: 'Company',
                displayKey: 'Description',
                historyKey: 'pickup',
                isRequired: true,
                compareIndex: 1,
              },
              {
                column: 'Drop Off',
                detected: `${this.ednJsonDetails['dropoff']}
${this.ednJsonDetails['dropoff_company']}
${this.ednJsonDetails['dropoff_addr1']}
${this.ednJsonDetails['dropoff_addr2']}
${this.ednJsonDetails['dropoff_addr3']}
${this.ednJsonDetails['dropoff_postcode']} ${this.ednJsonDetails['dropoff_city']}
${this.ednJsonDetails['dropoff_state']} ${this.ednJsonDetails['dropoff_country']}
Tel: ${this.ednJsonDetails['dropoff_phone']}`,
                submittedList: this.headerInfoData['dropoff'],
                submittedKey: 'Destination Code',
                submittedDesc: 'Company',
                displayKey: 'Description',
                historyKey: 'dropoff',
                isRequired: true,
                compareIndex: 1,
              },
            ],
          },
        ];

        await this.appendAttachmentToVerification(this.ednJsonDetails['attachments']);

        this.showVerification = true;
        // this.eROTthis.ednJsonDetails = this.ednJsonDetails;
      }
    },
    eDNReject: async function () {
      this.ednJsonDetails = await this.geteRequestJson();

      this.showCC = false;
      this.showBCC = false;

      this.rejectToEmails = [];
      this.rejectCCEmails = [];
      this.rejectBCCEmails = [];

      this.eDNRejectMessage = '';
      this.eDNRejectReason = '';

      this.showDNReject = true;
    },
    eDNRejectButtonsClicked: async function (text) {
      switch (text) {
        case 'Reject': {
          //#region Verification
          const errorMessage = [];
          if (this.rejectToEmails.length == 0) {
            errorMessage.push('Please enter at least one recipient.');
          }
          if (!this.eDNRejectReason) {
            errorMessage.push('Please enter your reason for rejection.');
          }

          const errorToEmails = this.rejectToEmails.filter((t) => !t['valid']);
          const errorCCEmails = this.rejectCCEmails.filter((t) => !t['valid']);
          const errorBCCEmails = this.rejectBCCEmails.filter((t) => !t['valid']);
          if (errorToEmails.length > 0 || errorCCEmails.length > 0 || errorBCCEmails.length > 0) {
            errorMessage.push('Please make sure that all the email addresses is valid');
          }
          //#endregion

          if (errorMessage.length > 0) {
            // Verification failed
            this.$showAlert('error', 'eDN Reject Error', errorMessage.join('\n'));
            return;
          }
          const edn = JSON.parse(JSON.stringify(this.selectedeDN[0]));
          const dataToPost = {
            emailto: this.rejectToEmails.map((r) => r['email']).join(';'),
            rejectreason: this.eDNRejectReason,
            rejectnotice: '',
            autorequestno: edn['AutoRequestNo'],
            emailmessage: this.eDNRejectMessage,
          };

          this.$fullscreenLoading('Reject e-Delivery Note');
          const ednReject = await this.$apiCallV2('/TruckingJob/RejecteJobTMS', dataToPost);
          this.$fullscreenLoading();

          if (ednReject[0]['Results'] == 0) {
            // Reject success
            //#region Send Reject Email
            this.$fullscreenLoading('Sending Rejection Email');

            let body = 'Your Delivery Note has been rejected.<br>';
            body += `Request #: ${edn['AutoRequestNo']}<br>`;
            body += `Date/Time Received: ${edn['Received Date']} ${edn['Received Time']}<br>`;
            body += `Date/Time Processed: ${new Date().formatString('dd/MM/yyyy HHmm')}<br>`;
            body += `Reason: ${this.eDNRejectReason}<br><br>`;

            body += `Goods Description: ${this.ednJsonDetails['goodsdescription']}<br>`;
            body += `Shipping Marks: ${this.ednJsonDetails['shippingmarks']}<br>`;
            body += `Total Weight: ${this.ednJsonDetails['consignments'][0]['packageweight']}<br>`;
            body += `Total M3: ${this.ednJsonDetails['consignments'][0]['packagem3']}<br><br>`;

            body += 'Consignments<br>';
            body += '---------------<br>';
            body += `No. of Packages: ${this.ednJsonDetails['consignments'][0]['packageqty']}<br>`;

            if (this.eDNRejectMessage) {
              body += `<br>${this.eDNRejectMessage}<br>`;
            }

            const disclaimer = await this.$apiCallV2(`/Disclaimer/GetSettingsSystem?settingsItem=Disclaimer`);
            body += `<br><i>${disclaimer[0]['SettingsValue']}</i><br><br>`;

            body += `Thank you.<br>`;
            body += `Regards,<br>`;
            body += `${this.loginDetails['LoginUserId']}<br>`;

            const dataForEmailing = {
              ToEmails: this.rejectToEmails.map((e) => e['email']),
              CCEmails: this.rejectCCEmails.map((e) => e['email']),
              BCCEmails: this.rejectBCCEmails.map((e) => e['email']),
              Subject: `Delivery Note Rejected - ${edn['Delivery Note #']}`,
              Body: body,
              Attachments: [],
            };

            await this.$apiCallV2('/EmailSender/SendEmail', dataForEmailing);
            this.$fullscreenLoading();
            //#endregion

            this.$showAlert(
              'success',
              'e-Delivery Note Rejected',
              `eDN with Request No ${edn['AutoRequestNo']} has been rejected.`,
            );

            this.selectedeDN = [];
            await this.refreshDNClicked();
          } else {
            // Reject failed
            this.$showAlert(
              'error',
              'e-Delivery Note Reject Error',
              `There was an error while rejecting eROT with Request No ${edn['AutoRequestNo']}. Please try again.`,
            );
            return;
          }
          break;
        }
        case 'Cancel':
          this.showDNReject = false;
          break;
      }

      this.showDNReject = false;
      this.showCC = false;
      this.showBCC = false;
      this.rejectToEmails = [];
      this.rejectCCEmails = [];
      this.rejectBCCEmails = [];
      this.eDNRejectMessage = '';
      this.eDNRejectReason = '';
    },
    verificationConfirm: async function (_detectedData, selectedData) {
      this.ednJsonDetails['ShprCsgn'] = selectedData['shipperConsignee'];
      this.ednJsonDetails['billTo'] = selectedData['billTo'];
      this.ednJsonDetails['pickup'] = selectedData['pickup'];
      this.ednJsonDetails['dropoff'] = selectedData['dropoff'];
      this.ednJsonDetails['lorryno'] = selectedData['lorryNo'];
      this.ednJsonDetails['drivername'] = selectedData['driverName'];

      await this.menuClicked({ title: 'New Trucking Job', enabled: true });

      this.showVerification = false;
      this.$nextTick(() => {
        //#region Header Info
        // this.selectedHeaderInfoData['jobNo'] = this.ednJsonDetails['Job No'] ? this.ednJsonDetails['Job No'].trim() : '';
        // this.selectedHeaderInfoData['job_date'] = this.ednJsonDetails['Job Time'] ? new Date(this.ednJsonDetails['Job Time']) : null;
        if (this.ednJsonDetails['daterequired'] && this.ednJsonDetails['timerequired']) {
          this.selectedHeaderInfoData['reqJobTime'] = new Date(
            `${this.ednJsonDetails['daterequired']} ${this.ednJsonDetails['timerequired'].substring(0, 2)}:${this.ednJsonDetails['timerequired'].substring(2)}`,
          );
        }
        this.selectedShipperConsignee =
          this.ednJsonDetails['ShprCsgn'] ?
            this.headerInfoData['shipperConsignee'].getSelected(this.ednJsonDetails['ShprCsgn'], 'Customer Code')
          : null;
        // this.selectedHeaderInfoData['selectedShipper'] = this.ednJsonDetails['Customer'] ? this.headerInfoData['shipper'].filter((t) => { return t['Customer Code'].trim() == this.ednJsonDetails['Customer'].trim() })[0] : null;
        // this.selectedHeaderInfoData['selectedVesselID'] = this.ednJsonDetails['Vessel Code'] ? this.headerInfoData['vesselIDList'].filter((t) => { return t['Vessel ID'].trim() == this.ednJsonDetails['Vessel Code'] })[0] : null;
        // this.selectedHeaderInfoData['selectedBillingFmt'] = this.ednJsonDetails['BillingFmt'] ? this.headerInfoData['billingfmt'].filter((t) => { return t['Billing Fmt'].trim() == this.ednJsonDetails['BillingFmt'].trim() })[0] : null;
        this.selectedHeaderInfoData['CustRefno'] = this.ednJsonDetails['orderno'];
        if (this.ednJsonDetails['daterequired'] && this.ednJsonDetails['timerequired']) {
          let time = this.ednJsonDetails['timerequired'].split('');
          time.splice(2, 0, ':');
          time = time.join('');
          this.selectedHeaderInfoData['reqJobTime'] = new Date(`${this.ednJsonDetails['daterequired']} ${time}`);
        } else {
          this.selectedHeaderInfoData['reqJobTime'] = null;
        }
        // this.selectedHeaderInfoData['selectedRequestBy'] = this.ednJsonDetails['Requested By'] ? this.ednJsonDetails['Requested By'].trim() : '';
        this.selectedHeaderInfoData['DOno'] = this.ednJsonDetails['autorequestno'];
        this.selectedHeaderInfoData['selectedBillTo'] =
          this.ednJsonDetails['billto'] ?
            this.headerInfoData['billTo'].getSelected(this.ednJsonDetails['billTo'], 'Customer Code')
          : null;
        this.selectedJobType =
          this.ednJsonDetails['job_type'] ?
            this.headerInfoData['jobType'].getSelected(this.ednJsonDetails['job_type'], 'Job Type')
          : null;
        if (this.selectedJobType) {
          this.jobTypeSelected(this.selectedJobType);
        }
        // this.selectedHeaderInfoData['ReqTimeDesc'] = this.ednJsonDetails['Req Time Desc'] ? this.ednJsonDetails['Req Time Desc'].trim() : '';
        // this.selectedGeneralInfoData['remarks'] = this.ednJsonDetails['Remarks'] ? this.ednJsonDetails['Remarks'].trim() : '';

        this.selectedHeaderInfoData['selectedPickUp'] =
          this.ednJsonDetails['pickup'] ?
            this.headerInfoData['pickup'].getSelected(this.ednJsonDetails['pickup'], 'Destination Code')
          : null;
        if (this.selectedHeaderInfoData['selectedPickUp']) {
          this.pickUpSelected(this.selectedHeaderInfoData['selectedPickUp']);
        }
        this.selectedHeaderInfoData['selectedDropOff'] =
          this.ednJsonDetails['dropoff'] ?
            this.headerInfoData['dropoff'].getSelected(this.ednJsonDetails['dropoff'], 'Destination Code')
          : null;
        if (this.selectedHeaderInfoData['selectedDropOff']) {
          this.dropOffSelected(this.selectedHeaderInfoData['selectedDropOff']);
        }
        // this.selectedHeaderInfoData['selectedTransit1'] = this.ednJsonDetails['Transit 1'] ? this.headerInfoData['transit1'].filter((t) => { return t['Destination Code'].trim() == this.ednJsonDetails['Transit 1'].trim() })[0] : null;
        // this.selectedHeaderInfoData['selectedTransit2'] = this.ednJsonDetails['Transit 2'] ? this.headerInfoData['transit2'].filter((t) => { return t['Destination Code'].trim() == this.ednJsonDetails['Transit 2'].trim() })[0] : null;

        // this.selectedHeaderInfoData['selectedCargo'] = this.ednJsonDetails[''] ?
        // #endregion

        //#region Shipping info
        this.selectedShippingInfoData['voyNo'] = this.ednJsonDetails['voyno'];
        // this.selectedShippingInfoData['selectedAgentList'] = this.ednJsonDetails['Shipping Agt'] ? this.shippingInfoData['shippingAgentList'].filter((t) => { return t['Agent Code'].trim() == this.ednJsonDetails['Shipping Agt'].trim() })[0] : null;
        // this.selectedShippingInfoData['selectedShippingLine'] = this.ednJsonDetails['Ship Line'] ? this.selectedShippingInfoData['selectedShippingLine'] : null;
        // this.selectedShippingInfoData['selectedAgent'] = this.ednJsonDetails['Agent'] ? this.shippingInfoData['agentList'].filter((t) => { return t['Agent Code'].trim() == this.ednJsonDetails['Agent'].trim() })[0] : null;
        // this.selectedShippingInfoData['selectedForwardingAgent'] = this.ednJsonDetails['Fwdg Agent'] ? this.shippingInfoData['forwardingAgent'].filter((t) => { return t['Agent Code'].trim() == this.ednJsonDetails['Fwdg Agent'].trim() })[0] : null;
        // this.selectedShippingInfoData['selectedTranshipmentPort'] = this.ednJsonDetails['Transhipment Port'] ? this.shippingInfoData['transhipmentPort'].filter((t) => { return t['Port Code'].trim() == this.ednJsonDetails['Transhipment Port'].trim() })[0] : null;
        // this.selectedShippingInfoData['selectedDischargeLoadPort'] = this.ednJsonDetails['Load / Discharge Port'] ? this.shippingInfoData['dischargeLoadPort'].filter((t) => { return t['Port Code'].trim() == this.ednJsonDetails['Load / Discharge Port'].trim() })[0] : null;

        // this.selectedShippingInfoData['selectedLocalCarrier'] = this.ednJsonDetails['Local Carrier'] ? this.shippingInfoData['localCarrier'].filter((t) => { return t['Agent Code'].trim() == this.ednJsonDetails['Local Carrier'].trim() })[0] : null;
        // this.selectedShippingInfoData['selectedForeignForwardingAgent'] = this.ednJsonDetails['Foreign Fwdg'] ? this.shippingInfoData['foreignForwardingAgent'].filter((t) => { return t['Agent Code'].trim() == this.ednJsonDetails['Foreign Fwdg'].trim() })[0] : null;
        // this.selectedShippingInfoData['selectedForeignCarrier'] = this.ednJsonDetails['Foreign Carrier'] ? this.shippingInfoData['foreignCarrier'].filter((t) => { return t['Agent Code'].trim() == this.ednJsonDetails['Foreign Carrier'].trim() })[0] : null;
        //#endregion

        //#region lorry/driver info
        this.selectedLorryInfoData['selectedLorryNoList'] =
          this.ednJsonDetails['lorryno'] ?
            this.LorryInfoTabData['lorryNoList'].getSelected(this.ednJsonDetails['lorryno'], 'Vehicle Code')
          : null;
        this.selectedLorryInfoData['selectedDriverCodeList'] =
          this.ednJsonDetails['drivername'] ?
            this.LorryInfoTabData['driverCodeList'].getSelected(this.ednJsonDetails['drivername'], 'Driver Code')
          : null;
        // this.selectedLorryInfoData['isJobCompleted'] = this.ednJsonDetails['isCompleted'] == '1';

        // if (this.ednJsonDetails['Trip']) {
        //   var tripthis.ednJsonDetails = this.ednJsonDetails['Trip'];
        //   this.selectedLorryInfoData['selectedLorryNoList'] = tripthis.ednJsonDetails['VehicleCode'] ? this.LorryInfoTabData['lorryNoList'].filter((t) => { return t['Vehicle Code'].trim() == tripthis.ednJsonDetails['VehicleCode'].trim() })[0] : null;
        //   this.selectedLorryInfoData['selectedDriverCodeList'] = tripthis.ednJsonDetails['Driver'] ? this.LorryInfoTabData['driverCodeList'].filter((t) => { return t['Driver Code'].trim() == tripthis.ednJsonDetails['Driver'].trim() })[0] : null;
        //   this.selectedLorryInfoData['driverEXP'] = tripthis.ednJsonDetails['Driver EXP 1'] ? tripthis.ednJsonDetails['Driver EXP 1'].trim() : '';
        //   this.selectedLorryInfoData['driverTOL'] = tripthis.ednJsonDetails['Driver EXP 2'] ? tripthis.ednJsonDetails['Driver EXP 2'].trim() : '';
        //   this.selectedLorryInfoData['selectedAttendantList'] = tripthis.ednJsonDetails['Attendant'] ? this.LorryInfoTabData['attendantList'].filter((t) => { return t['Attendant Code'].trim() == tripthis.ednJsonDetails['Attendant'].trim() })[0] : null;
        //   this.selectedLorryInfoData['attendantEXP'] = tripthis.ednJsonDetails['Attendant EXP 1'] ? tripthis.ednJsonDetails['Attendant EXP 1'] .trim() : '';
        //   this.selectedLorryInfoData['attendantTOL'] = tripthis.ednJsonDetails['Attendant EXP 2'] ? tripthis.ednJsonDetails['Attendant EXP 2'] .trim() : '';
        // }
        // #endregion

        if (this.ednJsonDetails['consignments']) {
          this.cargoInfoList = [];
          this.selectedCargoInfoData['selectedCargoInfoData'] = [];

          const consignmentDetails = this.ednJsonDetails['consignments'].map((c) => {
            return {
              '': '',
              'Consignment #': '',

              Product: '',
              Qty: c['packageqty'],
              'Total KG': c['packageweight'],
              'Total M3': c['packagem3'],
              'Truck Type': '',
              'Bill Rate Type': '',
              'Bill Code': '',
              'Bill Rate': '',

              'Total Chrg': '',
              'Item Type': '',
              Package: c['package_code'],
              'KG/Pkg': '',
              'M3/Pkg': '',
              'Toll Chrg': '',
              'CN Status': '',
              'CN Billing Status': '',

              Dimensions: '',

              Temp: '',
              'Est Trucks': '',
              'Cont. #': '',
              'Seal #': '',
              Opr: '',
              'Cont. Size': '',
              'Cont. Type': '',
              Remarks: c['remarks2'],
              action: 'N',
            };
          });
          this.$nextTick(() => (this.cargoInfoList = consignmentDetails));
        }

        // if (this.ednJsonDetails['Transit 1']) {
        //   this.selectedHeaderInfoData['tariffAreaTransit1'] = this.selectedHeaderInfoData['selectedTransit1']['Area Code'];
        //   this.selectedHeaderInfoData['zonesTransit1'] = this.selectedHeaderInfoData['selectedTransit1']['ZoneType'];
        //   this.selectedHeaderInfoData['transit1Address'] = this.selectedHeaderInfoData['selectedTransit1']['Address'];
        // }

        // if (this.ednJsonDetails['Transit 2']) {
        //   this.selectedHeaderInfoData['tariffAreaTransit2'] = this.selectedHeaderInfoData['selectedTransit2']['Area Code'];
        //   this.selectedHeaderInfoData['zonesTransit2'] = this.selectedHeaderInfoData['selectedTransit2']['ZoneType'];
        //   this.selectedHeaderInfoData['transit2Address'] = this.selectedHeaderInfoData['selectedTransit2']['Address'];
        // }
      });
    },
    appendAttachmentToVerification: async function (att) {
      if (att) {
        if (att.length > 0) {
          att = this.$deepTrimJsonV3(att);
          const form = new FormData();
          for (let x = 0; x < att.length; x++) {
            const bytesChar = atob(att[x]['doc_image']);
            const bytesArray = new Array(bytesChar.length);
            for (let i = 0; i < bytesChar.length; i++) {
              bytesArray[i] = bytesChar.charCodeAt(i);
            }
            const blob = new Blob([new Uint8Array(bytesArray)]);

            const fileNameSplit = att[x]['doc_name'].replaceAll(/[/\\?%*:|"<>]/g, '_').split('.');
            form.append('file', blob, `${fileNameSplit[0]}_${x + 1}.${fileNameSplit[1]}`);
          }
          form.append('folder', `eROT_${this.eDNRequestNo}`);

          const result = (await this.$apiCallV2('/Upload/UploadFile', form)) || {};
          if (Array.isArray(result['Files'])) {
            result['Files'].forEach((r, i) =>
              this.eDNAttachments.push({
                link: r,
                filename: att[i]['doc_name'],
                remarks: att[i]['doc_remark'],
              }),
            );
          }
        }
      }
    },
    //#endregion
    viewDocumentClicked: async function (_a, row) {
      this.$fullscreenLoading('Getting Attachments');
      this.attachmentList = [];
      this.attachmentList = await this.$apiCallV2(
        `/TruckingJob/GetJobDocuments?jobno=${this.selectedJob[0]['Job No']}`,
      );
      //console.log(row)
      this.selectedViewDocumentList = [];
      this.selectedViewDocumentList = row;
      //if no attachments, show "no attachments"
      if (this.attachmentList.length < 1 || !this.attachmentList) {
        this.noAtt = true;
      } else {
        this.noAtt = false;
        this.convertImage(this.attachmentList);
        this.attToView = this.attachmentList[row['Row #']];
        //console.log(this.attToView) // selected attachment to view
        this.totalAtt = this.attachmentList.length;

        for (let i = 0; i < this.attachmentList.length; i++) {
          this.attachmentList[i]['AttImageType'] = this.attachmentList[i]['AttImageType'].trim();

          for (const key in this.attachmentList[i]) {
            // if (this.attachmentList[i].hasOwnProperty(key)) {
            if (Object.prototype.hasOwnProperty.call(this.attachmentList[i], key)) {
              if (
                this.attachmentList[i][key] === '' ||
                this.attachmentList[i][key] == null ||
                !this.attachmentList[i][key]
              ) {
                this.attachmentList[i][key] = 'null';
              }
            }
          }
        }
      }
      this.$fullscreenLoading();
      if (this.attToView == undefined) {
        this.$showAlert('warning', 'Unsaved Document', 'Please save the job before viewing the document.');
        return;
      }
      this.openPopup = true;
      this.loading = false;
    },
    closePopup: function () {
      this.attachmentList = '';
      this.openPopup = false;
    },
    navAtt: function (clicked) {
      const getIndex = this.attToView;
      const inArray = this.attachmentList.indexOf(getIndex);

      // const curr = getIndex["#"] - 1;
      let next;
      const length = this.attachmentList.length;
      const end = length - 1;

      if (clicked == 'prev') {
        if (inArray == 0) {
          next = end;
        } else {
          next = inArray - 1;
        }
      } else if (inArray == end) {
        next = 0;
      } else {
        next = inArray + 1;
      }
      this.selectedViewDocumentList = this.selectedDocumentInfoData['docData'][next];
      this.attToView = this.attachmentList[next];
    },
    convertImage: function (data) {
      //loop to get and convert all attImg
      for (let j = 0; j < data.length; j++) {
        let type = '';
        if (data[j]['AttImageType'] == null) {
          data[j]['AttImageType'] = 'n/a';
        } else {
          type = data[j]['AttImageType'].trim();
        }

        //if empty
        if (data[j]['AttImage'] == null || data[j]['AttImage'].length == 0) {
          data[j]['AttImage'] = 'https://i.ibb.co/kyZfpjx/no-image.png';

          //if value = pdf file
        } else if (type === '.pdf' || type === 'application/pdf') {
          //qyvoon 20210520
          const b64Data = data[j]['AttImage'];
          const byteCharacters = atob(b64Data);
          const byteArrays = [];
          const sliceSize = 512;
          for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
              byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
          }
          const blob = new Blob(byteArrays, { type: 'application/pdf' });
          const fileurl = URL.createObjectURL(blob);
          data[j]['AttImage'] = fileurl;
        } else if (type === '.xlsx') {
          data[j]['AttImage'] =
            'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
            data[j]['AttImage'] +
            '==';
        } else if (type === '.ogg') {
          data[j]['AttImage'] = 'data:audio/ogg;base64,' + data[j]['AttImage'];
        } else if (type === '.pptx') {
          data[j]['AttImage'] =
            'data:application/vnd.openxmlformats-officedocument.presentationml.presentation;base64,' +
            data[j]['AttImage'].substring(0, data[j]['AttImage'].length - 4);
        } else if (type === '.docx') {
          data[j]['AttImage'] =
            'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,' +
            data[j]['AttImage'];
        } else if (type === '.msg') {
          data[j]['AttImage'] = 'data:application/vnd.msoutlook;base64,' + data[j]['AttImage'];
        } else if (
          type === '.jpeg' ||
          type === '.jpg' ||
          type === 'image/Jpeg' ||
          type === '.jpeg' ||
          type === 'image/jpeg' ||
          type === '.png' ||
          type === '.gif'
        ) {
          data[j]['AttImage'] = 'data:image/png;base64,' + data[j]['AttImage'];
        } else {
          data[j]['AttImage'] = 'data:application/octet-stream;base64,' + data[j]['AttImage'];
        }
      }
    },

    async openFromParams() {
      const params = new URLSearchParams(window.location.search);

      const newValue = params.get('New');
      if (newValue != null) this.menuClicked(this.menu.find((item) => item.title == 'New Trucking Job'));

      const View = params.get('View');

      const Edit = params.get('Edit');

      if (View || Edit) {
        this.searchFilterChecked = true;
        this.dateFilterChecked = false;
        this.searchByValue = View || Edit;
        await this.refreshClicked();
        const objectCleaned = Object.fromEntries(Object.entries(this.jobsList[0]).map(([k, v]) => [k, v ?? '']));

        this.selectedJob = [{ ...objectCleaned, _rowid: 'row0' }];
        if (View) this.menuClicked(this.menu.find((item) => item.title == 'View Trucking Job'));
        if (Edit) this.menuClicked(this.menu.find((item) => item.title == 'Edit Trucking Job'));
      }
    },
    //#region Query notifications
    vesselScheduleUpdated: async function (data) {
      if (data['configcode'].includes(this.loginDetails['ConfigCode']) && data['userid'] == this.loginDetails['LoginUserId']) {
        this.shipCallNoLoading = true;
        await this.refreshVesselSchedule();
      }
    },
    refreshVesselSchedule: async function () {
      this.shippingInfoData['shipCallNoList'] = await this.$apiCallV2('/Master/GetListMaster?save=true', { 
        masterfile: 'shipcallno' 
      });
      this.shipCallNoLoading = false;
    },
    //#endregion
  },
  async mounted() {
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Trucking Job') {
        this.SASControlList = x.data;
        x.data.forEach((k) => {
          // key in menu same as control value from api
          const selectedMenu = this.menu.find((m) => m['key2'] == k['Control']);
          if (selectedMenu) {
            selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] == 'True';
          }
          //visible true = !disabled
          if (k['Control'] == 'dtcboBillingFmt') {
            this.bilfmtIsDisabled = k['PropertiesValue'].toLowerCase() != 'true';
          }
          if (k['Control'] == 'mnuConfirmJob') {
            this.confirmJobView = k['PropertiesValue'].toLowerCase() != 'true';
          }
          if (k['Control'] == 'mnuPrintCN') {
            this.DNOrderView = k['PropertiesValue'].toLowerCase() != 'false';
          }
        });
      }
    });
    this.userParam = this.$route.params['user'];
    this.$fullscreenLoading('Loading');
    this.userParam = this.$route.params['user'];

    this.hub = await this.$signal.get('notifications');
    this.hub.on('VesselScheduleUpdate', this.vesselScheduleUpdated);

    this.loginDetails = await this.$apiCallV2('/Login/GetDetails');

    this.selectedCargoInfoData.driverEXP = this.$currencyFormat(parseInt(this.selectedCargoInfoData.driverEXP));
    this.selectedCargoInfoData.driverTOL = this.$currencyFormat(parseInt(this.selectedCargoInfoData.driverTOL));
    this.selectedCargoInfoData.attendantEXP = this.$currencyFormat(parseInt(this.selectedCargoInfoData.attendantEXP));
    this.selectedCargoInfoData.attendantTOL = this.$currencyFormat(parseInt(this.selectedCargoInfoData.attendantTOL));

    this.selectedCargoInfoData.cargoToll = this.$currencyFormat(parseInt(this.selectedCargoInfoData.cargoToll));
    this.selectedCargoInfoData.cargoTransport = this.$currencyFormat(
      parseInt(this.selectedCargoInfoData.cargoTransport),
    );
    this.selectedCargoInfoData.cargoTotal = this.$currencyFormat(parseInt(this.selectedCargoInfoData.cargoTotal));

    this.$fullscreenLoading();

    this.$fullscreenLoading('Getting Job Type');
    this.searchFilterItems = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'jobquery', module: 'TMS' });
    this.$fullscreenLoading();

    const query = this.$route.query;
    if (query['date'] && query['job']) {
      this.prePopulate = true;
      this.preJob = query['job'];
      this.preDate = query['date'];
    }

    if (this.prePopulate) {
      this.$fullscreenLoading('Please wait');
      this.refreshStart = new Date(this.preDate);
      this.refreshEnd = new Date(this.preDate);
      this.refreshClicked();
    } else {
      this.refreshStart = new Date();
      this.refreshEnd = new Date();
    }

    if (!this.selectedSearchBy) {
      this.selectedSearchBy = this.searchFilterItems[0];
    }

    if (!this.selectedCriteria) {
      this.selectedCriteria = this.searchCriteriaItems[2];
    }

    this.refreshClicked();
    let mountedPromises = [];
    let promisesFulfilled = 0;
    const getHeaderInfoTabDetailsPromises = this.getHeaderInfoTabDetails().map((promise) => {
      return promise.then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      });
    });
    mountedPromises = mountedPromises.concat(getHeaderInfoTabDetailsPromises);

    const getLorryInfoTabDetailsPromises = this.getLorryInfoTabDetails().map((promise) => {
      return promise.then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      });
    });
    mountedPromises = mountedPromises.concat(getLorryInfoTabDetailsPromises);

    const getShippingInfoTabDetailsPromises = this.getShippingInfoTabDetails().map((promise) => {
      return promise.then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      });
    });
    mountedPromises = mountedPromises.concat(getShippingInfoTabDetailsPromises);

    const getCargoInfoTabDetailsPromises = this.getCargoInfoTabDetails().map((promise) => {
      return promise.then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      });
    });
    mountedPromises = mountedPromises.concat(getCargoInfoTabDetailsPromises);

    // await this.getHeaderInfoTabDetails();
    // await this.getLorryInfoTabDetails();
    // await this.getShippingInfoTabDetails();
    // await this.getCargoInfoTabDetails();
    let sasControl = [];

    sasControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=TMS&page=Trucking Job`);
    await Promise.allSettled(mountedPromises).then((_promiseOutcomes) => {
      this.menu = this.menu.map((menu) => ({
        ...menu,
        enabled: true,
      }));

      sasControl.forEach((s) => {
        const menu = this.menu.find((m) => m['key'] == s['Control']);
        if (menu) {
          menu[s['Properties'].toLowerCase()] = s['PropertiesValue'] !== 'False';
          menu['message'] = 'Access Denied';
        }
      });

      this.openFromParams();
    });


    if (this.prePopulate) {
      this.$fullscreenLoading();
      this.menuClicked({ title: 'View Job', enabled: true });
    }
  },
  watch: {
    showPopup: function (val) {
      if (!val) {
        this.clearPopupData();
        this.clearServicesData();
      } else {
        if (this.defaultTabIndex >= 0) {
          this.currentTabIndex = this.defaultTabIndex;
        }

        if (this.defaultJobType) {
          this.selectedJobType = this.headerInfoData['jobType'].getSelected(this.defaultJobType, 'Job Type');
        }
        if (this.defaultBillBy) {
          this.selectedHeaderInfoData['selectedBillBy'] = this.headerInfoData['billBy'].getSelected(
            this.defaultBillBy,
            'BillingBy',
          );
        }
      }
    },

    billTypeSend() {
      if (this.billTypeSend && this.truckTypeSend) {
        this.getCargoInfoBillCode();
      }
    },
    truckTypeSend() {
      if (this.billTypeSend && this.truckTypeSend) {
        this.getCargoInfoBillCode();
      }
    },
    showPopupFrom: function (val) {
      if (!val) {
        this.clearPopupData();
        this.clearServicesData();
      }
    },
    selectedUnconfirmReason: function () {
      this.popupValidation();
    },
    selectedJobType: function (val) {
      if (val) {
        this.jobTypeSelected(val);
      }
    },
    unconfirmRemarks: function () {
      this.popupValidation();
    },
    showVerification: function (val) {
      if (!val) {
        this.$apiCallV2(`/Upload/DeleteFiles?folder=eROT_${this.eDNRequestNo}`);
        this.eDNAttachments = [];
      }
    },
    cargoInfoList: function (val) {
      if (val.length > 0) {
        let totalChrg = 0;
        let tollChrg = 0;
        let total = 0;

        totalChrg = val
          .map((data) => {
            return parseFloat(data['Total Chrg']) ?? 0;
          })
          .reduce((a, b) => {
            return a + b;
          });
        tollChrg = val
          .map((data) => {
            return parseFloat(data['Toll Chrg']) ?? 0;
          })
          .reduce((a, b) => {
            return a + b;
          });

        total = totalChrg + tollChrg;
        this.selectedCargoInfoData['cargoToll'] = totalChrg.toFixed(2).toString();
        this.selectedCargoInfoData['cargoTransport'] = tollChrg.toFixed(2).toString();
        this.selectedCargoInfoData['cargoTotal'] = total.toFixed(2).toString();

        //Job Total section
        this.selectedHeaderInfoData['selectedCargoContainerNo'] = val
          .filter((item) => !item['Delete Remarks'])
          .length.toString();
        this.selectedHeaderInfoData['selectedCargoQuantity'] = this.cargoInfoList
          .filter((item) => !item['Delete Remarks']) // Filter out items that have 'Delete Remarks'
          .reduce((total, item) => total + (parseFloat(item['Qty']) || 0), 0)
          .toString();
        this.selectedHeaderInfoData['selectedCargoGrossWeight'] = this.cargoInfoList
          .filter((item) => !item['Delete Remarks']) // Filter out items that have 'Delete Remarks'
          .reduce((total, item) => total + (parseFloat(item['Total KG'].replaceAll(',', '')) || 0), 0) // remove seperator before addition
          .toString();
        this.selectedHeaderInfoData['selectedCargoGrossWeight'] = this.$weightFormat(
          this.selectedHeaderInfoData['selectedCargoGrossWeight'],
        ); //format to weight format

        this.selectedHeaderInfoData['selectedCargoGrossVolume'] = this.cargoInfoList
          .filter((item) => !item['Delete Remarks'])
          .reduce((total, item) => total + (parseFloat(item['Total M3'].replaceAll(',', '')) || 0), 0)
          .toString();
        this.selectedHeaderInfoData['selectedCargoGrossVolume'] = this.$weightFormat(
          this.selectedHeaderInfoData['selectedCargoGrossVolume'],
        );

        this.selectedHeaderInfoData['selectedCargoTtlVolumetricWeight'] = this.cargoInfoList
          .filter((item) => !item['Delete Remarks'])
          .reduce((total, item) => total + (parseFloat(item['Volume Weight']?.replaceAll(',', '')) || 0), 0)
          .toString();
        this.selectedHeaderInfoData['selectedCargoTtlVolumetricWeight'] = this.$weightFormat(
          this.selectedHeaderInfoData['selectedCargoTtlVolumetricWeight'],
        );

        if (!this.selectedHeaderInfoData['selectedCargoEstTrucks']) {
          this.selectedHeaderInfoData['selectedCargoEstTrucks'] = this.cargoInfoList
            .filter((item) => !item['Delete Remarks'])
            .reduce((total, item) => total + (parseFloat(item['Est Trucks']) || 0), 0)
            .toString();
        }

        if (!this.selectedHeaderInfoData['selectedCargoPackage']) {
          const firstNonEmptyPackage = this.cargoInfoList
            .filter((item) => !item['Delete Remarks'])
            .find((item) => item['Package']);
          if (firstNonEmptyPackage) {
            this.selectedHeaderInfoData['selectedCargoPackage'] = this.cargoInfoData['cargoPackageList'].find(
              (t) => t['Package Code'].trim() === firstNonEmptyPackage['Package'].trim(),
            );
          }
        }
        if (!this.selectedHeaderInfoData['selectedCargoTruckType']) {
          const firstNonEmptyTruck = this.cargoInfoList
            .filter((item) => !item['Delete Remarks'])
            .find((item) => item['Truck Type']);
          if (firstNonEmptyTruck) {
            this.selectedHeaderInfoData['selectedCargoTruckType'] = this.cargoInfoData['cargoTruckTypeList'].find(
              (t) => t['Truck Type'].trim() === firstNonEmptyTruck['Truck Type'].trim(),
            );
          }
        }
      } else {
        this.selectedHeaderInfoData['selectedCargoContainerNo'] = '0';
        this.selectedHeaderInfoData['selectedCargoQuantity'] = '0';
        this.selectedHeaderInfoData['selectedCargoGrossWeight'] = this.$weightFormat('0');
        this.selectedHeaderInfoData['selectedCargoTtlVolumetricWeight'] = this.$weightFormat('0');
        //this.selectedHeaderInfoData['selectedCargoEstTrucks'] = '0';
      }
    },

    // currentTabIndex: function(val) {
    //   if (val == 0 || val == 2) { // Repopulating the tableboxes for cargo info tab
    //     this.selectedCargoInfoData['selectedCargoInfo'] = [];

    //     // this.selectedCargoInfoData['selectedSubPkgs'] = [];
    //     // this.selectedCargoInfoData['selectedContDetails'] = [];

    //     if (this.cargoInfoList.length > 0) {
    //       let temp = this.cargoInfoList;
    //       this.cargoInfoList = [];
    //       this.$nextTick(() => this.cargoInfoList = temp);
    //     }
    //   }
    // }
  },
  beforeDestroy() {
    this.hub.off('VesselScheduleUpdate');
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.trucking-job-main {
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  font-size: 12px;
  display: flex;

  > .left-menu {
    display: flex;
    justify-content: center;
  }

  > .right-tabulator {
    width: 100%;
    margin-left: 5px;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    > .filter-area {
      background-color: white;
      padding: 5px;
      border-radius: 5px;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      > * {
        //asterisk means universal selector
        margin-left: 5px;
      }

      > .filter-button {
        position: relative;

        > .filter-box {
          position: absolute;
          right: 0;
          top: 100%;
          border: 1px solid black;
          border-radius: 3px;
          padding: 5px;
          background-color: white;
          display: flex;
          flex-direction: column;
          z-index: 99;

          > .filter-item {
            border: 1px solid black;
            border-radius: 5px;
            padding: 5px;
            padding-top: 10px;
            margin: 5px;
            margin-top: 10px;
            position: relative;

            > .filter-check {
              position: absolute;
              top: -10px;
              background-color: white;
              padding: 0 10px;
              left: 10px;
            }

            > .dates {
              display: flex;
              > * {
                margin-right: 5px !important;
              }

              > :last-child {
                margin-right: 0 !important;
              }

              > .checkbox-main {
                white-space: nowrap;
              }
            }
          }

          > button {
            position: relative;
            right: 5px;
            align-self: flex-end;
          }
        }
      }
    }

    > .tabulator-area {
      background-color: white;
      margin-top: 5px;
      border-radius: 5px;
      height: 100%;
      padding: 5px;
    }
  }

  > .edn-popup {
    position: fixed;
    background-color: rgba(0, 0, 0, 0.5);
    top: 50px;
    left: 0;
    height: calc(100% - 50px);
    width: 100%;
    z-index: 199;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    > .edn-content {
      background-color: lightgray;
      padding: 5px;
      width: calc(100% - 90px);
      height: calc(100% - 20px);
      position: relative;
      display: flex;
      flex-direction: column;

      > .top-section,
      > .table-section,
      > .bottom-section {
        background-color: white;
        border-radius: 5px;
        padding: 5px;
      }

      > .top-section,
      > .bottom-section {
        display: flex;
        justify-content: flex-end;
      }

      > .top-section {
        align-items: center;

        > :first-child {
          margin-right: 5px;
        }

        > :last-child {
          margin-left: 5px;
        }
      }

      > .table-section {
        height: 100%;
        margin-top: 5px;
        overflow: hidden;

        > .verification-upload {
          display: flex;
          flex-direction: column;
          height: 100%;

          > object {
            background-color: lightgray;
            height: 100%;
          }
        }
      }

      > .bottom-section {
        margin-top: 5px;

        > button {
          margin-left: 5px;
          color: white;
        }

        > :nth-child(1) {
          background-color: green;
        }

        > :nth-child(2) {
          background-color: red;
        }

        > :nth-child(3),
        > .cancel-btn {
          background-color: gray;
        }
      }
    }
  }

  > .designer-v2-popup {
    background-color: rgba(150, 150, 150, 0.5);
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    z-index: 101;
    display: flex;
    padding: 0 35px;

    > .designer-container {
      width: 100%;
      padding: 10px;

      > .title {
        background-color: rgb(50, 54, 57);
        color: white;
        text-align: center;
        font-weight: bold;
        padding: 5px;
      }

      > :nth-child(2) {
        height: calc(100% - 28px);
      }
    }
  }
}

.delete-popup {
  text-align: left;
  padding: 20px;
}

.popup-milestones {
  //top of edit import job
  border: 1px solid rgba(0, 0, 0, 3);
  width: 100%;
  padding: 5px;
  margin-bottom: 8px;
  position: relative;
  font-size: 10px;

  > .milestone-row {
    display: flex;
    align-items: center;

    > * {
      margin-right: 5px;
    }

    > .milestone-block {
      position: relative;
      margin-bottom: 2px;

      > .milestone-item {
        border: 1px solid rgba(128, 128, 128, 0.5);
        max-width: 100px;
        min-width: 100px;
        -webkit-line-clamp: 2; //allows limiting of the contents of a block container to the specified number of lines.
        //only works in combi with display: -webkit-box and -webkit-box-orient: vertical
        display: -webkit-box;
        -webkit-box-orient: vertical;
        text-overflow: ellipsis; //... in the end
        overflow: hidden; //best to use this with -webkit-line-clamp
        cursor: default;
        padding: 3px;
      }

      > .milestone-tooltip {
        //the ! top right of milestones
        position: absolute;
        height: 100%;
        width: 100%;
        top: 0;
        left: 0;
      }
    }

    > .legend-items {
      display: flex;
      align-items: center;

      > .legend-box {
        height: 10px;
        width: 10px;
        border: 1px solid rgba(128, 128, 128, 0.5);
        margin-right: 3px;
      }
    }
  }

  > .collapse-milestone {
    position: absolute;
    bottom: 2px;
    right: 2px;
    transition: 0.3s;
    cursor: pointer;
  }
}

.popup-details {
  display: flex;
  height: 100%;

  > .summary {
    width: 115px;
    min-width: 115px;
    margin-right: 5px;
  }
}

.slot-body-block {
  //edit job, body
  display: flex;
  position: relative;

  > .slot-content-column {
    width: calc(100% / 3);
    margin-right: 5px;

    > .box-container {
      border: 1px solid rgba(0, 0, 0, 0.3);
      border-radius: 5px;
      padding: 10px 5px 5px 5px;
      margin-right: 5px;
      position: relative;
      margin-top: 10px;
    }

    > .address {
      height: 100%;
      border-radius: 3px;
      border: 1px solid rgba(128, 128, 128, 0.5);
      min-height: 100px;
      position: relative;
      display: flex;
      flex-direction: column;
      overflow: auto;
      margin-bottom: 5px;

      > .edit-area {
        position: absolute;
        top: 3px;
        right: 3px;
        display: flex;
        align-items: center;

        > button {
          padding: 1px 10px;
        }

        > img {
          //drop down menu beside button
          transition: 0.3s;
          cursor: pointer;
        }

        > .img-hide {
          transform: scaleY(1);
        }

        > .img-show {
          transform: scaleY(-1);
        }
      }

      > .address-block {
        overflow: auto;
        padding: 5px;
      }
    }

    > .address-collapse {
      max-height: 28px;
      min-height: 28px;
      justify-content: center;
      padding-left: 5px;
    }
  }

  > .column-flex {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  > .edit-address-popup {
    position: absolute;
    left: 0;
    top: 0;
    background-color: rgba(128, 128, 128, 0.5);
    height: 100%;
    width: 100%;
    z-index: 999;
    display: flex;
    align-items: center;
    justify-content: center;

    > .edit-content {
      width: 90%;
      max-width: 500px;
      background-color: white;
      padding: 10px;

      > .title {
        font-weight: bold;
        font-size: 13px;
        margin-bottom: 5px;
      }

      > .button-section {
        display: flex;
        justify-content: flex-end;

        > button {
          margin-left: 5px;
        }
      }
    }
  }
}

.shipping-textarea {
  //shipping info
  display: flex;
  height: calc(100% - 207px);
  min-height: 100px;

  > * {
    margin: 3px;
  }
}

.status-container {
  border: 1px solid rgb(200, 200, 200);
  padding: 3px;
  margin-right: 5px;
  margin-left: 3px;
}

.outstanding-prog {
  display: flex;
  border: 1px solid rgb(200, 200, 200);
  position: relative;
  width: calc(100% - 2px);
  margin-left: 3px;
  padding: 3px;

  > .progress-ind {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 50%;
    background-color: rgba(51, 231, 255, 0.5);
  }
}

.table-container {
  border: 1px solid rgba(0, 0, 0, 0.5);
  padding: 5px;
  margin-top: 3px;

  > .tabulator-title {
    display: flex;
    align-items: center;
    margin-bottom: 5px;

    > button {
      margin-left: 3px;
    }
  }
}

.tab-block {
  display: flex;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.5);
  margin-top: 10px;
  margin-bottom: 10px;
  position: relative;
  padding-top: 10px;
  padding-left: 5px;

  > .tab-title {
    width: fit-content;
    background-color: white;
    position: absolute;
    top: -10px;
    left: 10px;
    padding: 0 10px;
  }

  > .details-column {
    width: calc(100% / 3);
    margin-right: 5px;

    > .address-details {
      height: 75px;
      width: 100%;
      resize: none;

      &:disabled {
        background-color: rgba(200, 200, 200, 0.3);
        opacity: 0.8;
      }
    }
  }

  > .details-column2 {
    width: 100%;
    margin-right: 5px;
  }

  > .details-column4 {
    width: calc(100% / 4);
    margin-right: 5px;

    > .address-details {
      height: 75px;
      width: 100%;
      resize: none;

      &:disabled {
        background-color: rgba(200, 200, 200, 0.3);
        opacity: 0.8;
      }
    }
  }
}

.top-button-section {
  display: flex;
  width: 100%;
  background: white;

  > button {
    margin-right: 5px;
  }
}

.save-content {
  max-height: 150px;
  overflow: auto;
  box-shadow: inset 0 0 5px 0 gray;
  padding: 10px;
  text-align: left;
  margin: 5px 0;
}

.popup-error {
  color: red;
  text-align: center;
}

report-popup-selection {
  position: fixed;
  width: 100%;
  height: 100%;
  padding-top: 50px;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  z-index: 99999;
  transition: 0.3s;

  // > .report-popup-container {
  //   background-color: white;
  //   width: 100%;
  //   max-width: 300px;
  //   max-height: 180px;
  //   padding: 0 10px;
  //   overflow: auto;
  //   display: flex;
  //   justify-content: space-between;
  //   flex-wrap: wrap;

  //   > .report-link {
  //     color: black;
  //     border: 1px solid black;
  //     width: 100%;
  //     margin-top: 5px;
  //     padding: 5px;
  //     text-align: left;
  //     background-color: transparent;
  //     border-radius: 0;

  //     &:disabled {
  //       color: rgba(0,0,0,0.3);
  //       border: 1px solid rgba(0,0,0,0.3);
  //     }

  //     &:hover:enabled {
  //       background-color: rgba(0,0,0,0.2);
  //     }

  //     // &:hover:disabled::after {
  //     //   content: ' - No container available';
  //     //   color: red;
  //     // }
  //   }

  //   > .report-disabled-container:hover::after {
  //     content: ' - No container available';
  //     color: red;
  //   }

  //   > .report-disabled-cancelled:hover::after {
  //     content: ' - Job is cancelled';
  //     color: red;
  //   }

  //   > .report-disabled-cargo-type:hover::after {
  //     content: ' - Cargo Type invalid';
  //     color: red;
  //   }
  // }
  > .report-popup-container {
    background-color: white;
    width: 100%;
    max-width: 300px;
    max-height: 180px;
    padding: 0 10px;
    overflow: auto;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;

    > .report-link {
      color: black;
      border: 1px solid black;
      width: 100%;
      margin-top: 5px;
      padding: 5px;
      text-align: left;
      background-color: transparent;
      border-radius: 0;

      &:disabled {
        color: rgba(0, 0, 0, 0.3);
        border: 1px solid rgba(0, 0, 0, 0.3);
      }

      &:hover:enabled {
        background-color: rgba(0, 0, 0, 0.2);
      }

      // &:hover:disabled::after {
      //   content: ' - No container available';
      //   color: red;
      // }
    }

    > .report-disabled-container:hover::after {
      content: ' - No container available';
      color: red;
    }

    > .report-disabled-cancelled:hover::after {
      content: ' - Job is cancelled';
      color: red;
    }

    > .report-disabled-cargo-type:hover::after {
      content: ' - Cargo Type invalid';
      color: red;
    }
  }

  > .report-popup-title {
    width: 100%;
    max-width: 300px;
    background-color: white;
    text-align: center;
    font-weight: bold;
    padding: 5px;
  }

  > .report-popup-cancel {
    background-color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 300px;

    > button {
      margin: 5px 0;
    }
  }
}

.edn-reject-content {
  text-align: left;
  margin: 0 10px;

  > .collapsible-title-container {
    display: flex;
    align-items: center;
    cursor: pointer;

    > img {
      height: 20px;
      transition: 0.3s;
    }
  }

  > .message-reason-container {
    height: 100px;
  }
}

.documents {
  > .loader {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;

    > .loader-text {
      font-weight: bolder;
      font-size: 20px;
      color: white;
    }
  }
}

//attachment popup
.attchWin-main {
  width: calc(100%);
  height: calc(100% - 10px);
  padding: 5px;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  align-items: center;
  background-color: #00000048;
  z-index: 255;

  > .attchWin-close {
    height: 30px;
    width: 30px;
    font-weight: bold;
    border-radius: 50%;
    background-color: white;
    color: black;
    text-align: center;
    cursor: pointer;
    position: absolute;
    right: 0;
    top: 0;
    margin: 20px;
    font-size: 14px;
  }

  > .attchWin-display {
    width: 80%;
    margin: 0 auto;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    background-color: #eaeaea;
    border-radius: 3px;
    display: flex;
    height: 90vh;

    > .left {
      position: relative;
      float: left;
      width: 100%;
      height: 100%;
      border: 1px solid black;
      background: black;
      height: 100%;
      text-align: center;
      display: flex;

      > .leftButton {
        width: auto;
        height: 100%;
        float: left;
        align-items: center;
        left: 0;

        > .navButton-left {
          margin: 0 auot;
          position: relative;
          z-index: 300;
          font-size: 36px;
          color: white;
          background: none;
          top: 50%;

          &:hover {
            cursor: pointer;
            background: rgba(200, 200, 200, 0.8);
          }
        }
      }

      > .rightButton {
        width: auto;
        height: 100%;
        float: right;
        align-items: center;

        > .navButton-right {
          margin: 0;
          position: relative;
          top: 50%;
          float: right;
          /* -webkit-transform: translateY(-50%);
      transform: translateY(-50%); */
          z-index: 300;
          font-size: 36px;
          color: white;
          background: none;

          &:hover {
            cursor: pointer;
            background: rgba(200, 200, 200, 0.8);
          }
        }
      }

      > .attchWin-attachment {
        position: relative;
        //margin: 0 auto;
        width: 100%;
        height: 100%;
        text-align: center;
        -o-object-fit: contain;
        object-fit: contain;
        float: left;
        align-self: center;
        //top: 25%;

        img {
          width: 70%;
          object-fit: contain;
          margin: 0 auto;
          max-height: 90vh;
        }

        &:hover {
          cursor: pointer;
        }

        iframe {
          width: 100%;
          height: 100%;
          min-height: 600px;
        }
      }
    }

    > .right {
      //position: relative;
      float: left;
      width: 500px;
      height: 100%;
      min-width: 250px;
      border: 1px solid black;
      display: grid;

      > .attchWin-info {
        position: relative;
        margin: auto 5px 0;
        text-align: left;
        margin: 5px;
        height: fit-content;
        overflow-x: auto;

        #infoHeader {
          width: 100%;
          table-layout: fixed;
          font-size: 12px;
          border-collapse: collapse;
          background: #d4dfeb;
          margin: 5px 0px;
          border: 1px solid black;

          td {
            padding: 3px;
          }
          th {
            font-weight: bold;
            width: 92px;
          }
        }

        #tableContent {
          width: 100%;
          position: relative;
          table-layout: fixed;
          font-size: 12px;
          border-collapse: collapse;
          margin: 20px auto;
          background: #fff;
          align-self: center;
          text-align: left;

          tr {
            border: 1px solid black;
            padding: 3px;
          }
          td {
            padding: 3px;
            border: 1px solid black;
            overflow-x: auto;
          }
          th {
            font-weight: bold;
            background: #d4dfeb;
            border: 1px solid black;
            padding: 3px;
            width: 97px;
          }
        }
      }
      > .count {
        position: relative;
        width: auto;
        text-align: center;
        margin: auto 3px 0;
        height: fit-content;
        border: 1px solid black;
        background: #a1a9b2;
      }
    }
  }
}
</style>
